import { projectCalendarScope, readProjectCalendarScope, withProjectCalendarScope } from '../utils/projectCalendarRoute';
import { getHSEditorAddLabel } from '../platform/navigation/headerActions/hs';
import { useWorkspaceNavigation } from '../hooks/useWorkspaceNavigation';
import { useWorkspacePlugins } from '../hooks/useWorkspacePlugins';
import { GALLERY_SORTS, GALLERY_VIEWS, GALLERY_FILTERS } from '../features/takeoff/utils/galleryOrganisation';
import type { TakeoffHeaderState } from '../platform/navigation/headerActions/takeoff';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, startTransition } from 'react';
import { dispatchSignatureNativeAction, type SignatureHeaderState } from '../features/email/signatureNativeActions';
import { NotificationService } from '../services/NotificationService';
import { NativeBridge } from '../services/NativeBridge';
import { DataService } from '../services/DataService';
import { installNativeAddProjectData } from '../services/nativeAddProjectData';
import { MessagingService as MessagingSvc } from '../services/MessagingService';
import { resolveAvatarSourceUrl } from '../shared/ui/avatar';
import { buildNotificationTemplate } from '../notifications/templates';
import { initializeDataLifecycle } from '../utils/dataLifecycleScheduler';
import {
  buildWorkspacePathById,
  buildProjectPathById,
  buildTaskPathById,
  buildEmployerPathById,
} from '../utils/prettyRouteBuilders';
import {
  buildPrettyProjectDocumentPath,
  buildPrettyProjectTakeoffPath,
  buildPrettyProjectTabPath,
  buildPrettyProjectNotePath,
  parsePrettyProjectNotePath,
  buildPrettyProjectInterimPath,
  parsePrettyProjectInterimPath,
  buildPrettyProjectPricingPath,
  buildPrettyProjectEventsPath,
  buildPrettyProjectTeamMemberPath,
  parsePrettyWorkspacePath,
  parsePrettyWorkspaceEditPath,
  parsePrettyProjectDocumentPath,
  parsePrettyProjectTakeoffPath,
  parsePrettyProjectPricingPath,
  parsePrettyProjectEventsPath,
  parsePrettyProjectTeamMemberPath,
  parsePrettyProjectTabPath,
  parsePrettySharedPath,
  parsePrettyTaskPath,
  parsePrettyEmployerPath,
  parsePrettyContactPath,
  parsePrettyResourceLibraryItemPath,
  slugifySegment,
  CALENDAR_TASKS_PATH,
} from '../utils/prettyRoutes';
import { decideHistoryWrite, isPreferencesSettle } from './historyWrite';
import { isProjectAdminTab } from '../features/projects/admin/navigation';
import {
  COMPANIES_NEW_EMPLOYER_EVENT,
  COMPANIES_OPEN_EMPLOYER_EVENT,
  isLeftCompanyAddress,
  isPushedClientEntry,
  parseCompanyPath,
} from '../features/contacts/companies/companyRoutes';
import { keepOpenDocumentFolder } from '../features/workspaces/workspaceDocumentRoutes';
import { projectReportIdFromTab } from '../features/reports/project/projectReportCatalog';
import { useReportHeaderState } from '../features/reports/reportNavigation';
import { PROJECT_INTERIM_ROUTE_CHANGE, type ProjectInterimSelection, type ProjectInterimRouteChange } from '../features/projects/useProjectInterimRoute';
import {
  buildPersonalAccountPath,
  parsePersonalAccountPath,
} from '../utils/personalAccountRoutes';
import {
  buildWorkspaceFinancePath,
  parseWorkspaceFinancePath,
  type WorkspaceFinanceHub,
} from '../utils/workspaceFinanceRoutes';
import {
  buildNativeHeaderState,
  buildNativeNavItems,
  buildNativePipelineNavigation,
  buildNativeAccountingNavigation,
  nativeAccountingNavigationTarget,
  ACCOUNTING_AREA_PREFIX,
  nativePipelineNavigationTarget,
  PIPELINE_SECTION_PREFIX,
  WORKSPACES_TAB_PREFIX,
  WORKSPACE_DETAIL_TAB_PREFIX,
  WORKSPACES_CLOSE_DETAIL_SENTINEL,
  EMPLOYER_DETAIL_TAB_PREFIX,
  EMPLOYER_FORM_TAB_PREFIX,
  EMPLOYERS_CLOSE_DETAIL_SENTINEL,
  RESOURCE_LIBRARY_ITEM_BACK_SENTINEL,
  DOCUMENTS_FOLDER_UP_SENTINEL,
  NOTES_CLOSE_DETAIL_SENTINEL,
  REPORTS_CLOSE_REPORT_SENTINEL,
  PLANNER_CLOSE_EDITOR_SENTINEL,
  INTERIM_SECTION_PREFIX,
  INTERIM_CLOSE_APPLICATION_SENTINEL,
  HS_BACK_SENTINEL,
  PLANT_CLOSE_DETAIL_SENTINEL,
  HS_EDITOR_SECTION_PREFIX,
  HS_DOCUMENT_SECTION_PREFIX,
  HS_DOCUMENT_SECTION_ITEMS,
  PAYROLL_TAB_PREFIX,
  ACCOUNTING_TAB_PREFIX,
  SALES_TAB_PREFIX,
  PURCHASES_TAB_PREFIX,
  TAX_TAB_PREFIX,
  getNativeRouteItemIdentifier,
  isNativeContextualSearchRoute,
  nativeContextualSearchPrompt,
  isAppView,
  NATIVE_TAB_TO_VIEW,
  PROJECT_TAB_PREFIX,
  SYSTEM_SETTINGS_TAB_PREFIX,
  PREFERENCES_TAB_PREFIX,
  PREFERENCES_LIST_ID,
  PROFILE_TAB_PREFIX,
  EMAIL_TAB_PREFIX,
  EMAIL_SHARED_TAB_PREFIX,
  VIEW_TO_NATIVE_TAB,
  TAKEOFF_CLOSE_DRAWING_SENTINEL,
  SITE_PHOTO_CLOSE_FULL_VIEW_SENTINEL,
  NOTIFICATIONS_BACK_SENTINEL,
  TAKEOFF_CLOSE_UPLOADER_SENTINEL,
  TAKEOFF_UPLOADER_UP_SENTINEL,
  TAKEOFF_UPLOADER_BACK_TO_SOURCE_SENTINEL,
  TAKEOFF_CLOSE_SHEET_DETAILS_SENTINEL,
  TEMPLATE_PREVIEW_CLOSE_SENTINEL,
  EMAIL_THREAD_BACK_SENTINEL,
  type AppView,
} from './navigation';
import {
  WORKSPACE_USERS_CREATE_EVENT,
  WORKSPACE_USERS_INVITE_CANCEL_EVENT,
  WORKSPACE_USERS_INVITE_SUBMIT_EVENT,
  WORKSPACE_USERS_STATE_EVENT,
  WORKSPACE_USER_FORM_STATE_EVENT,
} from '../features/users/userRoles';
import {
  EMPLOYERS_CLEAR_SELECTION_EVENT,
  EMPLOYERS_CREATE_EVENT,
  EMPLOYERS_NATIVE_FILTER_PREFIX,
  EMPLOYERS_SELECT_EVENT,
  EMPLOYERS_STATE_EVENT,
  EMPLOYER_DETAIL_CANCEL_EVENT,
  EMPLOYER_DETAIL_SAVE_EVENT,
  EMPLOYER_DETAIL_SET_SECTION_EVENT,
  EMPLOYER_DETAIL_STATE_EVENT,
  EMPLOYER_FORM_CANCEL_EVENT,
  EMPLOYER_FORM_SAVE_EVENT,
  EMPLOYER_FORM_SET_SECTION_EVENT,
  EMPLOYER_FORM_STATE_EVENT,
  EMPLOYER_STATUS_FILTER_ALL,
  EMPLOYER_STATUS_FILTER_OPTIONS,
} from '../features/employers/constants';
import {
  PLANT_CLEAR_SELECTION_EVENT,
  PLANT_CREATE_EVENT,
  PLANT_SELECT_EVENT,
  PLANT_STATE_EVENT,
  PLANT_DETAIL_STATE_EVENT,
  PLANT_DETAIL_SAVE_EVENT,
  type PlantListState,
  type PlantDetailState,
} from '../features/plant/constants';
import {
  OUTREACH_OPEN_SECTION_EVENT,
  buildOutreachSlug,
  isOutreachSectionId,
  OUTREACH_SET_SECTION_EVENT,
  OUTREACH_STATE_EVENT,
  PROSPECT_DETAIL_CANCEL_EVENT,
  PROSPECT_DETAIL_SAVE_EVENT,
  PROSPECT_DETAIL_STATE_EVENT,
  parseOutreachSlug,
} from '../features/outreach/constants';
import {
  buildEmailSidebarTabItems,
  isSelfServiceWorkspaceRoute,
  getSelfServiceWorkspaceBotSlug,
  WORKSPACE_PROFILE_TAB_PREFIX,
} from '../platform/navigation/sidebarNavConfig';
import { useNativeBridge } from '../hooks/useNativeBridge';
import { API_BASE } from '../utils/supabase/client';
import { useNotifications } from '../features/notifications/NotificationContext';
import { useMessaging } from '../features/messages/MessagingContext';
import { useEmailUnread } from '../features/email/EmailUnreadContext';
import { resolveNotificationNavigationIntent } from '../features/notifications/notificationNavigation';
import { readDiscussionLink } from '../features/messages/discussionLink';
import { queueCalendarEventTarget } from '../features/calendar/calendarEventNavigation';
import { toast } from 'sonner';
import { queueEmailNotificationOpen } from '../features/email/emailNotificationNavigation';
import { resolveLibraryName, formatLibraryName } from '../features/resource-library/utils';
import {
  OPEN_WORKSPACE_RESOURCE_LIBRARY_EVENT,
  type OpenWorkspaceResourceLibraryDetail,
} from '../features/resource-library/types';
import type { ResourceLibraryItemsHeaderState } from '../platform/navigation/headerActions/resourceLibrary';
import type { Project, Company, ClientRequest, MainContractor } from '../types/database';
import type { UserRole } from '../types/auth';
import type { Task } from '../types/tasks';
import type { PresenceStatus } from '../types/presence';
import type { NativePresenceDisplayId, NativePresenceStatusId, NativeHeaderState } from '../types/native';
import { canEditProjectPlanner, canManageActiveWorkspaceSettings, isAdministratorRole, isInternalRole } from '../utils/permissions';
import { isSiteSupervisor } from '../utils/siteRegisterAccess';
import { getContractRoleProfile } from '../contract-administration/roleProfile';
import { userRunsTender } from '../features/projects/projectTabAccess';
import { SUB_ROUTES, buildViewPath, matchViewPath, resolveLegacyPreferencesPath } from '../utils/subRoutes';
import { openSettingsModal, usesSettingsModal } from '../features/preferences/settingsModalStore';
import { documentLinkFromSegment, parseDocumentDeepLink, rememberDocumentDeepLink } from '../features/documents/documentDeepLink';

/**
 * Maps contract ids from the native account menu back to internal PresenceStatus
 * values. Used when the native shell sends a set-status accountAction.
 * The reverse mapping (internal → native) lives in AppSidebar's PRESENCE_TO_NATIVE.
 */
const NATIVE_STATUS_TO_PRESENCE: Record<NativePresenceStatusId, PresenceStatus> = {
  available: 'available',
  busy:      'busy',
  dnd:       'dnd',
  brb:       'be_right_back',
  away:      'away',
  offline:   'offline',
};

/** Routes the macOS H&S record overflow menu onto the same events as the web
 *  header. RAMS ids are unprefixed (legacy shape); Toolbox Talks, Plant
 *  Checks and Near Miss reports are namespaced ('toolbox-'/'plant-'/
 *  'near-miss-') since their lifecycles use different statuses and window
 *  events (see navigation.ts's hsMenuItems and headerActions/hs.ts's
 *  per-section builders). */
const dispatchHsNativeMenuAction = (id: string): boolean => {
  if (!id.startsWith('hs-menu:')) return false;

  const key = id.slice('hs-menu:'.length);

  if (key.startsWith('toolbox-') || key.startsWith('near-miss-')) {
    window.dispatchEvent(new CustomEvent(`hs:${key}`));
    return true;
  }
  if (key.startsWith('plant-')) {
    window.dispatchEvent(new CustomEvent(`plant:check-${key.slice('plant-'.length)}`));
    return true;
  }

  if (key.startsWith('transition:')) {
    window.dispatchEvent(new CustomEvent('hs:transition', {
      detail: { to: key.slice('transition:'.length) },
    }));
  } else if (key === 'edit') {
    window.dispatchEvent(new CustomEvent('hs:edit'));
  } else if (key === 'new-version') {
    window.dispatchEvent(new CustomEvent('hs:new-version'));
  } else if (key === 'download-pdf') {
    window.dispatchEvent(new CustomEvent('hs:download-pdf'));
  } else if (key === 'save-template') {
    window.dispatchEvent(new CustomEvent('hs:save-template'));
  } else if (key === 'delete') {
    window.dispatchEvent(new CustomEvent('hs:delete'));
  }
  return true;
};

// The Gmail labels that exist as native sidebar folder rows — derived from the
// same builder buildNativeNavItems uses, so the badge push can't drift from
// the rows it targets.
const EMAIL_FOLDER_LABELS = buildEmailSidebarTabItems().map((item) => item.id);

// Native toolbar ids for the Resource Library page actions. Namespaced so the
// headerControl subscriber can claim them ahead of its catch-all BOQ forward.
const RESOURCE_LIBRARY_NATIVE_PREFIX = 'rl:';

// The same window events buildResourceLibraryHeaderActions gives the web pills,
// so LibraryItemsPane keeps one set of listeners rather than growing a native
// twin that can drift.
const RESOURCE_LIBRARY_NATIVE_ACTION_EVENTS: Record<string, string> = {
  'rl:collapse':     'resource-library:toggle-all-sections',
  'rl:rate-profile': 'resource-library:open-rate-profile',
};

// Email folder choices are rendered as a native anchored menu when the
// webview's global header is replaced by the iOS/macOS shell. Keep these
// action names separate from the sidebar's `email-tab:` route ids: selecting a
// mailbox here is an in-place change, not navigation away from Email.
const EMAIL_NATIVE_MAILBOX_PREFIX = 'email:mailbox:';

const dispatchEmailNativeMenuAction = (id: string): boolean => {
  if (id === 'email:select') {
    window.dispatchEvent(new CustomEvent('email:toggle-select'));
    return true;
  }
  if (id === 'email:mark-all-read') {
    window.dispatchEvent(new CustomEvent('email:mark-all-read'));
    return true;
  }
  if (id === 'email:refresh') {
    window.dispatchEvent(new CustomEvent('email:refresh'));
    return true;
  }
  if (id === 'email:forward' || id === 'email:print') {
    window.dispatchEvent(new CustomEvent(id));
    return true;
  }
  if (!id.startsWith(EMAIL_NATIVE_MAILBOX_PREFIX)) return false;
  const mailbox = id.slice(EMAIL_NATIVE_MAILBOX_PREFIX.length);
  if (!mailbox) return true;
  window.dispatchEvent(new CustomEvent('email:mailbox-change', { detail: { id: mailbox } }));
  return true;
};

/**
 * Routes a Resource Library native toolbar press onto the window event its web
 * pill fires. Returns whether the id belonged to this view at all, so a caller
 * can stop before its catch-all forward swallows the press.
 */
const dispatchResourceLibraryNativeAction = (id: string): boolean => {
  if (!id.startsWith(RESOURCE_LIBRARY_NATIVE_PREFIX)) return false;
  const actionEvent = RESOURCE_LIBRARY_NATIVE_ACTION_EVENTS[id];
  if (actionEvent) {
    window.dispatchEvent(new CustomEvent(actionEvent));
  }
  return true;
};

interface AuthenticatedControllerProps {
  auth: any;
  currentView: AppView;
  pageTitle: string;
  projectActiveTab: string;
  projectPermissions: any;
  projectTabProps: any;
  workspacesActiveTab: 'clients' | 'requests' | 'add-client';
  workspacesStats: any;
  emailActiveTab: string;
  setEmailActiveTab: (value: string) => void;
  mainContractorInitialTab: 'contractors' | 'add-contractor';
  setMainContractorInitialTab: (value: 'contractors' | 'add-contractor') => void;
  systemSettingsActiveTab: string;
  preferencesSection: string | null;
  selectedProjectId: string | null;
  selectedProjectName: string | null;
  selectedClientId: string | null;
  selectedClientName: string | null;
  clientDetailActiveTab: string;
  selectedTaskId: string | null;
  calendarTasksTab: boolean;
  calendarProjectScope: string | null;
  setCalendarProjectScope: (scope: string | null) => void;
  documentRouteSlug: string | null;
  eventsRouteSlug: string | null;
  messagesChatSlug: string | null;
  taskRouteSlug: string | null;
  showContextualSearch: boolean;
  notificationOpen: boolean;
  messagePanelOpen: boolean;
  logoFullLight: string;
  logoFullDark: string;
  setNativeShellHidesWebMainHeader: (value: boolean) => void;
  setProjectTabProps: (value: any) => void;
  setWorkspacesStats: (value: any) => void;
  setSelectedProjectId: (value: string | null) => void;
  setSelectedProjectName: (value: string | null) => void;
  setSelectedClientId: (value: string | null) => void;
  setSelectedClientName: (value: string | null) => void;
  setClientDetailActiveTab: (value: string) => void;
  setSelectedTaskId: (value: string | null) => void;
  setCalendarTasksTab: (value: boolean) => void;
  setDocumentRouteSlug: (value: string | null) => void;
  setEventsRouteSlug: (value: string | null) => void;
  setMessagesChatSlug: (value: string | null) => void;
  /** Write-once boot target consumed by MessagesView — separate from
   *  messagesChatSlug/setMessagesChatSlug, which is the live URL echo. See
   *  the App.tsx comment by pendingChatSlug's declaration for why these must
   *  not be the same state. */
  setPendingChatSlug: (value: string | null) => void;
  setTaskRouteSlug: (value: string | null) => void;
  setProjectActiveTab: (value: string) => void;
  setSystemSettingsActiveTab: (value: string) => void;
  setPreferencesSection: (value: string | null) => void;
  profileActiveTab: string;
  outreachActiveTab: string | null;
  setOutreachActiveTab: (value: string | null) => void;
  setProfileActiveTab: (value: string) => void;
  setWorkspacesActiveTab: (value: 'clients' | 'requests' | 'add-client') => void;
  setShareToken: (value: string | null) => void;
  navigateToView: (value: AppView) => void;
  setCurrentViewImmediate: (value: AppView) => void;
  getCompany: (id?: string | null) => any;
  isPopStateRef: { current: boolean };
  handleOpenChatThread: (chatId: string, messageId?: string | null) => void;
  handleShowCalendarWithTask: (taskId?: string) => void;
  handleSelectProject: (projectId: string, projectName: string) => void;
  toggleMessagesPanel: (chatId?: string, messageId?: string | null) => void;
  toggleNotificationsPanel: () => void;
  setNotificationOpen: (value: boolean | ((value: boolean) => boolean)) => void;
  setMessagePanelOpen: (value: boolean | ((value: boolean) => boolean)) => void;
  setPanelChatId: (value: string | null) => void;
  setPanelThreadMessageId: (value: string | null) => void;
}

export function AuthenticatedController(props: AuthenticatedControllerProps) {
  const { unreadCount: unreadNotificationCount } = useNotifications();
  const { unreadCount: messagesUnreadCount } = useMessaging();
  const { inboxUnread: unreadEmailCount, unreadByLabel: emailUnreadByLabel } = useEmailUnread();
  const {
    auth,
    currentView,
    pageTitle,
    projectActiveTab,
    projectPermissions,
    projectTabProps,
    workspacesActiveTab,
    workspacesStats,
    emailActiveTab,
    setEmailActiveTab,
    mainContractorInitialTab,
    setMainContractorInitialTab,
    systemSettingsActiveTab,
    preferencesSection,
    profileActiveTab,
    outreachActiveTab,
    setOutreachActiveTab,
    selectedProjectId,
    selectedProjectName,
    selectedClientId,
    selectedClientName,
    clientDetailActiveTab,
    selectedTaskId,
    calendarTasksTab,
    calendarProjectScope,
    setCalendarProjectScope,
    documentRouteSlug,
    eventsRouteSlug,
    taskRouteSlug,
    messagesChatSlug,
    showContextualSearch,
    notificationOpen,
    messagePanelOpen,
    logoFullLight,
    logoFullDark,
    setNativeShellHidesWebMainHeader,
    setProjectTabProps,
    setWorkspacesStats,
    setSelectedProjectId,
    setSelectedProjectName,
    setSelectedClientId,
    setSelectedClientName,
    setClientDetailActiveTab,
    setSelectedTaskId,
    setCalendarTasksTab,
    setDocumentRouteSlug,
    setEventsRouteSlug,
    setTaskRouteSlug,
    setMessagesChatSlug,
    setPendingChatSlug,
    setProjectActiveTab,
    setSystemSettingsActiveTab,
    setPreferencesSection,
    setProfileActiveTab,
    setWorkspacesActiveTab,
    setShareToken,
    navigateToView,
    setCurrentViewImmediate,
    getCompany,
    isPopStateRef,
    handleOpenChatThread,
    handleShowCalendarWithTask,
    handleSelectProject,
    toggleMessagesPanel,
    toggleNotificationsPanel,
    setNotificationOpen,
    setMessagePanelOpen,
    setPanelChatId,
    setPanelThreadMessageId,
  } = props;
  const didApplyInitialLocationRef = useRef(false);
  const pendingLocationRestoreRef = useRef<{
    pathname: string;
    search: string;
    historyState?: any;
    // What the app looked like when this location was parked. The URL writer
    // holds off while a location is still resolving, so it needs to tell "still
    // waiting" from "the user gave up waiting and navigated somewhere else" —
    // otherwise a deep link that never resolves would freeze the address bar.
    viewWhenParked?: AppView;
    projectIdWhenParked?: string | null;
  } | null>(null);
  const lastStableNativeTitleRef = useRef(pageTitle);
  const workspaceMainNavigation = useWorkspaceNavigation();
  const canManageWorkspaceSettings = canManageActiveWorkspaceSettings(
    auth.activeWorkspaceId,
    auth.workspaceMemberships,
  ) || workspaceMainNavigation.navigation?.canManage === true;
  const canManagePlatformWorkspaces =
    auth.user?.role === 'super_admin' || auth.user?.role === 'internal';
  const accountWorkspaceName =
    auth.activeWorkspace?.name ||
    auth.activeWorkspace?.companyName ||
    auth.activeWorkspace?.slug ||
    auth.company?.name ||
    'workspace';
  const [resourceLibraryWorkspaceSlug, setResourceLibraryWorkspaceSlug] = useState(
    () => slugifySegment(accountWorkspaceName),
  );
  useEffect(() => {
    const handleWorkspaceLibraryOpen = (event: Event) => {
      const detail = (event as CustomEvent<OpenWorkspaceResourceLibraryDetail>).detail;
      if (detail?.workspaceName) {
        setResourceLibraryWorkspaceSlug(slugifySegment(detail.workspaceName));
      }
    };
    window.addEventListener(OPEN_WORKSPACE_RESOURCE_LIBRARY_EVENT, handleWorkspaceLibraryOpen);
    return () => window.removeEventListener(OPEN_WORKSPACE_RESOURCE_LIBRARY_EVENT, handleWorkspaceLibraryOpen);
  }, []);
  const accountUserName =
    auth.user?.displayName ||
    auth.user?.name ||
    auth.user?.email?.split('@')[0] ||
    'user';
  const buildCurrentAccountPath = useCallback(
    (section?: string | null) => buildPersonalAccountPath({
      workspaceName: accountWorkspaceName,
      userName: accountUserName,
      section,
    }),
    [accountUserName, accountWorkspaceName],
  );
  const buildCurrentFinancePath = useCallback(
    (hub: WorkspaceFinanceHub, section: string) => buildWorkspaceFinancePath({
      hub,
      workspaceName: accountWorkspaceName,
      section,
    }),
    [accountWorkspaceName],
  );

  // A persisted view or any direct view change can still select the
  // Preferences page; in the browser that means the modal, over Home.
  useEffect(() => {
    if (currentView !== 'preferences' || !usesSettingsModal()) return;
    openSettingsModal(preferencesSection);
    setCurrentViewImmediate('dashboard');
  }, [currentView, preferencesSection, setCurrentViewImmediate]);

  // A persisted view, deep link, or native command can select a route without
  // going through the sidebar. Keep the platform directory internal-only and
  // keep the Profile workspace panels scoped to this active owner/admin
  // membership. `/organisation` is retained only as a legacy redirect.
  useEffect(() => {
    if (!auth.user) return;
    const blockedPlatformDirectory =
      !canManagePlatformWorkspaces &&
      ['workspaces', 'add-workspace', 'client-detail'].includes(currentView);

    if (currentView === 'organisation') {
      // AuthContext hydrates memberships/workspaces after the cached user.
      // Keep the deep link intact during that gap instead of incorrectly
      // treating an empty first render as an access denial.
      if (auth.activeWorkspaceId && (!auth.activeWorkspace || workspaceMainNavigation.loading)) return;
      if (canManageWorkspaceSettings && auth.activeWorkspace?.companyId) {
        const tab = `${WORKSPACE_PROFILE_TAB_PREFIX}overview`;
        setProfileActiveTab(tab);
        setCurrentViewImmediate('profile');
        window.history.replaceState({ view: 'profile', profileActiveTab: tab }, document.title, `/profile/${tab}`);
      } else {
        setCurrentViewImmediate('dashboard');
        window.history.replaceState({ view: 'dashboard' }, document.title, '/');
      }
      return;
    }

    // Trade details now share Organisation's draft and header actions.
    if ((currentView === 'profile' || currentView === 'account') && profileActiveTab === `${WORKSPACE_PROFILE_TAB_PREFIX}trade-profile`) {
      const tab = `${WORKSPACE_PROFILE_TAB_PREFIX}overview`;
      setProfileActiveTab(tab);
      setCurrentViewImmediate('profile');
      window.history.replaceState(
        { view: 'profile', profileActiveTab: tab },
        document.title,
        buildViewPath('profile', tab),
      );
      return;
    }

    // The retired self-service credit report returns to Settings.
    if ((currentView === 'profile' || currentView === 'account') && profileActiveTab === `${WORKSPACE_PROFILE_TAB_PREFIX}credit`) {
      setProfileActiveTab('overview');
      setCurrentViewImmediate('account');
      window.history.replaceState({view:'account',profileActiveTab:'overview'},document.title,buildCurrentAccountPath('overview'));
      return;
    }

    // Keep old Account links useful now that Gmail lives in the Plugins inspector.
    if ((currentView === 'profile' || currentView === 'account') && profileActiveTab === 'gmail-addon') {
      const tab = `${WORKSPACE_PROFILE_TAB_PREFIX}integrations/gmail-addon`;
      setProfileActiveTab(tab);
      setCurrentViewImmediate('profile');
      window.history.replaceState(
        { view: 'profile', profileActiveTab: tab },
        document.title,
        buildViewPath('profile', tab),
      );
      return;
    }

    // Personal settings now have their own Account address. Keep `/profile`
    // working as a legacy entry point, while leaving the nested workspace
    // settings routes under Profile where they belong.
    if (currentView === 'profile' && !profileActiveTab.startsWith(WORKSPACE_PROFILE_TAB_PREFIX)) {
      setCurrentViewImmediate('account');
      window.history.replaceState(
        { view: 'account', profileActiveTab },
        document.title,
        buildCurrentAccountPath(profileActiveTab),
      );
      return;
    }

    if (currentView === 'account' && profileActiveTab.startsWith(WORKSPACE_PROFILE_TAB_PREFIX)) {
      setCurrentViewImmediate('profile');
      window.history.replaceState(
        { view: 'profile', profileActiveTab },
        document.title,
        buildViewPath('profile', profileActiveTab),
      );
      return;
    }

    // Notifications, Devices and Privacy moved to Preferences. A native
    // command or in-app link that still selects one as a profile section is
    // sent there rather than left on a section Profile no longer renders.
    const movedToPreferences = (currentView === 'profile' || currentView === 'account')
      ? { notifications: 'notifications', devices: 'notifications', privacy: 'privacy' }[profileActiveTab as 'notifications' | 'devices' | 'privacy']
      : undefined;
    if (movedToPreferences && usesSettingsModal()) {
      setProfileActiveTab('overview');
      openSettingsModal(movedToPreferences);
      return;
    }
    if (movedToPreferences) {
      setProfileActiveTab('overview');
      setPreferencesSection(movedToPreferences);
      setCurrentViewImmediate('preferences');
      window.history.replaceState(
        { view: 'preferences', preferencesSection: movedToPreferences },
        document.title,
        buildViewPath('preferences', movedToPreferences),
      );
      return;
    }

    const requestedWorkspaceTab =
      currentView === 'profile' && profileActiveTab.startsWith(WORKSPACE_PROFILE_TAB_PREFIX)
        ? profileActiveTab.slice(WORKSPACE_PROFILE_TAB_PREFIX.length)
        : null;
    if (requestedWorkspaceTab !== null) {
      if (auth.user?.role === 'super_admin' && (requestedWorkspaceTab === 'bots'
        || getSelfServiceWorkspaceBotSlug(requestedWorkspaceTab) !== null)) return;
      if (auth.activeWorkspaceId && (!auth.activeWorkspace || workspaceMainNavigation.loading)) return;
      // Plugin visibility and detail access come from the workspace catalogue API.
      if (auth.activeWorkspaceId && (requestedWorkspaceTab === 'integrations' || requestedWorkspaceTab.startsWith('integrations/')) && isSelfServiceWorkspaceRoute(requestedWorkspaceTab)) return;
      if (!canManageWorkspaceSettings || !auth.activeWorkspace?.companyId) {
        setProfileActiveTab('overview');
        setCurrentViewImmediate('account');
        window.history.replaceState(
          { view: 'account', profileActiveTab: 'overview' },
          document.title,
          buildCurrentAccountPath('overview'),
        );
      } else if (!isSelfServiceWorkspaceRoute(requestedWorkspaceTab)) {
        const tab = `${WORKSPACE_PROFILE_TAB_PREFIX}overview`;
        setProfileActiveTab(tab);
        window.history.replaceState({ view: 'profile', profileActiveTab: tab }, document.title, `/profile/${tab}`);
      }
      return;
    }

    if (!blockedPlatformDirectory) return;

    setSelectedClientId(null);
    setSelectedClientName(null);
    setDocumentRouteSlug(null);
    setTaskRouteSlug(null);
    setCurrentViewImmediate('dashboard');
    window.history.replaceState({ view: 'dashboard' }, document.title, '/');
  }, [
    auth.user,
    auth.activeWorkspace,
    canManagePlatformWorkspaces,
    canManageWorkspaceSettings,
    workspaceMainNavigation.loading,
    buildCurrentAccountPath,
    currentView,
    profileActiveTab,
    setCurrentViewImmediate,
    setDocumentRouteSlug,
    setSelectedClientId,
    setSelectedClientName,
    setProfileActiveTab,
    setPreferencesSection,
    setTaskRouteSlug,
  ]);

  const [projectEmailHeaderState, setProjectEmailHeaderState] = useState<{
    hasThreads: boolean;
    loading: boolean;
    isClient: boolean;
    detailOpen: boolean;
    subject: string;
    subtitle: string;
  }>({ hasThreads: false, loading: false, isClient: false, detailOpen: false, subject: '', subtitle: '' });
  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<Partial<typeof projectEmailHeaderState>>).detail;
      if (!detail) return;
      setProjectEmailHeaderState((current) => ({
        ...current,
        ...detail,
        hasThreads: !!detail.hasThreads,
        loading: !!detail.loading,
        isClient: !!detail.isClient,
        detailOpen: !!detail.detailOpen,
      }));
    };
    window.addEventListener('project-email:state-change', onChange);
    window.dispatchEvent(new CustomEvent('project-email:request-state'));
    return () => window.removeEventListener('project-email:state-change', onChange);
  }, []);

  const [sitePhotosView, setSitePhotosView] = useState<'gallery' | 'map'>('gallery');
  const [sitePhotoFullViewOpen, setSitePhotoFullViewOpen] = useState(false);
  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ view?: 'gallery' | 'map'; fullView?: boolean }>).detail;
      const view = detail?.view;
      if (view === 'gallery' || view === 'map') setSitePhotosView(view);
      if (typeof detail?.fullView === 'boolean') setSitePhotoFullViewOpen(detail.fullView);
    };
    window.addEventListener('site-photos:state-change', onChange);
    window.dispatchEvent(new CustomEvent('site-photos:request-state'));
    return () => window.removeEventListener('site-photos:state-change', onChange);
  }, []);

  // Admin save/cancel state — ProjectAdminTab broadcasts dirty/saving
  // state via project-admin:state-change so the macOS toolbar can surface
  // Save/Cancel navButtons when the Admin tab has unsaved edits.
  const [projectAdminHeaderState, setProjectAdminHeaderState] = useState<{
    dirty: boolean;
    isSaving: boolean;
  } | null>(null);

  // Takeoff drawing route — seeded from the URL on a cold load, then kept in
  // sync by TakeoffView. The stable id disambiguates repeated drawing
  // titles (a set can contain several "Elevations" sheets); the name keeps the
  // route readable. A non-null value also drives the native Back treatment.
  const [takeoffDrawingRoute, setTakeoffDrawingRoute] = useState<{
    sheetId: string | null;
    sheetName: string;
  } | null>(() => {
    if (typeof window === 'undefined') return null;
    const route = parsePrettyProjectTakeoffPath(window.location.pathname);
    if (!route) return null;
    return {
      sheetId: new URLSearchParams(window.location.search).get('sheet'),
      sheetName: route.sheetSlug,
    };
  });
  const takeoffDrawingOpen = takeoffDrawingRoute !== null;
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ sheetId?: string | null; sheetName: string | null } | null>).detail;
      const next = detail?.sheetName
        ? { sheetId: detail.sheetId ?? null, sheetName: detail.sheetName }
        : null;
      setTakeoffDrawingRoute(previous =>
        previous?.sheetId === next?.sheetId && previous?.sheetName === next?.sheetName
          ? previous
          : next
      );
    };
    window.addEventListener('takeoff:drawing-route-change', handler);
    return () => window.removeEventListener('takeoff:drawing-route-change', handler);
  }, []);

  // How many folder levels deep the Add Drawings uploader is (0 = project
  // root). TakeoffUploader broadcasts this so the native back chevron steps up
  // a folder while browsing and only closes the uploader at the root — mirrors
  // documentsFolderDepth for the Documents tab.
  const [takeoffUploaderDepth, setTakeoffUploaderDepth] = useState(0);
  const [takeoffUploaderSelected, setTakeoffUploaderSelected] = useState(0);
  // True only on the uploader's source (file-picking) step — gates the native
  // Browse button. The uploader owns the gate and also zeroes selectedCount
  // off that step, so Browse/Clear/Continue can't fire against a batch that's
  // already being processed or imported.
  const [takeoffUploaderCanBrowse, setTakeoffUploaderCanBrowse] = useState(false);
  // Details-step (title-block region drawing) payload — which field a drawn
  // box assigns to, which fields have one, and the confirmed sheet count.
  // Feeds the native field-toggle + Import toolbar buttons below. Null
  // outside the details step.
  const [takeoffUploaderDetails, setTakeoffUploaderDetails] = useState<{
    activeField: 'drawingNumber' | 'title' | 'revision';
    fieldsSet: Record<'drawingNumber' | 'title' | 'revision', boolean>;
    sheetCount: number;
  } | null>(null);
  // Raw step string, kept alongside the details payload so the back-chevron
  // sentinel logic (buildNativeHeaderState) can tell the review step apart from
  // "source" without re-deriving it from that payload.
  const [takeoffUploaderStep, setTakeoffUploaderStep] = useState<string | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{
        depth?: number;
        selectedCount?: number;
        canBrowse?: boolean;
        step?: string;
        activeField?: 'drawingNumber' | 'title' | 'revision';
        fieldsSet?: Record<'drawingNumber' | 'title' | 'revision', boolean>;
        sheetCount?: number;
      } | null>).detail;
      setTakeoffUploaderDepth(detail?.depth ?? 0);
      setTakeoffUploaderSelected(detail?.selectedCount ?? 0);
      setTakeoffUploaderCanBrowse(!!detail?.canBrowse);
      setTakeoffUploaderStep(detail?.step ?? null);
      setTakeoffUploaderDetails(
        detail?.step === 'details' && detail.activeField && detail.fieldsSet
          ? { activeField: detail.activeField, fieldsSet: detail.fieldsSet, sheetCount: detail.sheetCount ?? 0 }
          : null,
      );
    };
    window.addEventListener('takeoff:upload-nav', handler);
    return () => window.removeEventListener('takeoff:upload-nav', handler);
  }, []);

  // Documents folder depth — DocumentManagementTab broadcasts
  // documents:folder-state-change whenever its folderPath changes (0 = the
  // register root, dispatched again on unmount). Feeds buildNativeHeaderState
  // so the native back chevron steps UP one folder instead of leaving the
  // project while a folder is open.
  const [documentsFolderDepth, setDocumentsFolderDepth] = useState(0);
  /** Name of the open documents folder, for the native search placeholder. */
  const [documentsFolderName, setDocumentsFolderName] = useState<string | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ depth: number; name?: string | null } | null>).detail;
      setDocumentsFolderDepth(detail?.depth ?? 0);
      setDocumentsFolderName(detail?.name ?? null);
    };
    window.addEventListener('documents:folder-state-change', handler);
    return () => window.removeEventListener('documents:folder-state-change', handler);
  }, []);

  /** A note is open in the Notes tab — the native chevron closes it back to
   *  the notes table rather than leaving the project. */
  const [notesDetailOpen, setNotesDetailOpen] = useState(false);
  const reportHeaderState = useReportHeaderState();
  const [notesUnreadCount, setNotesUnreadCount] = useState(0);
  const [notesPageRoute, setNotesPageRoute] = useState<{ projectId: string | null; pageSlug: string | null }>(() => ({
    projectId: selectedProjectId, pageSlug: parsePrettyProjectNotePath(window.location.pathname)?.pageSlug ?? null,
  }));
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ detailOpen?: boolean; unreadCount?: number; pageSlug?: string | null; projectId?: string } | null>).detail;
      setNotesDetailOpen(!!detail?.detailOpen);
      setNotesUnreadCount(detail?.unreadCount ?? 0);
      // Unmount and cold-load broadcasts must not erase a pending page address.
      if (detail && Object.prototype.hasOwnProperty.call(detail, 'pageSlug')) {
        setNotesPageRoute({ projectId: detail.projectId ?? selectedProjectIdRef.current, pageSlug: detail.pageSlug ?? null });
      }
    };
    window.addEventListener('project-notes:state-change', handler);
    return () => window.removeEventListener('project-notes:state-change', handler);
  }, []);

  // Interim application state - InterimApplication broadcasts
  // interim:app-state-change while an application is open inside the
  // Applications tab (null detail on unmount). Drives the sidebar drill-in
  // to the application's sections and the back chevron's close-application
  // sentinel.
  const [interimAppState, setInterimAppState] = useState<{ activeSection: string } | null>(null);
  const [interimRoute, setInterimRoute] = useState<ProjectInterimSelection | null>(() => {
    const route = parsePrettyProjectInterimPath(window.location.pathname);
    return route ? { projectId: '', applicationId: route.applicationId, applicationNumber: route.applicationNumber, section: route.section } : null;
  });
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ProjectInterimRouteChange>).detail;
      if (detail?.projectId !== selectedProjectId) return;
      setInterimRoute(detail.selection);
    };
    window.addEventListener(PROJECT_INTERIM_ROUTE_CHANGE, handler);
    return () => window.removeEventListener(PROJECT_INTERIM_ROUTE_CHANGE, handler);
  }, [selectedProjectId]);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ open: boolean; activeSection: string } | null>).detail;
      setInterimAppState(detail?.open ? { activeSection: detail.activeSection } : null);
    };
    window.addEventListener('interim:app-state-change', handler);
    return () => window.removeEventListener('interim:app-state-change', handler);
  }, []);

  // H&S view state - ProjectHSTab broadcasts hs:state-change (null detail on
  // unmount). Past the hub grid it drives the native back chevron's HS_BACK
  // sentinel and gates the native + (RAMS/Toolbox Talks list only) — the web
  // header pills are suppressed on both shells, so this is their only H&S
  // create/back. `section` distinguishes which list/record is open; absent
  // is treated as 'rams' for backward compatibility.
  const [hsHeaderState, setHsHeaderState] = useState<{
    view: 'hub' | 'list' | 'record' | 'editor';
    count: number;
    section?: 'rams' | 'toolbox' | 'plant' | 'register' | 'near-miss';
    status?: string;
    canIssue?: boolean;
  } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{
        view: 'hub' | 'list' | 'record' | 'editor';
        count: number;
        section?: 'rams' | 'toolbox' | 'plant' | 'register' | 'near-miss';
        status?: string;
        canIssue?: boolean;
      } | null>).detail;
      setHsHeaderState(detail ?? null);
    };
    window.addEventListener('hs:state-change', handler);
    return () => window.removeEventListener('hs:state-change', handler);
  }, []);
  // Read inside the native primaryAction subscriber below (registers once) so
  // the '+' dispatch always targets whichever H&S section is actually open —
  // a plain closure over hsHeaderState would go stale after the first tab visit.
  const hsHeaderStateRef = useRef(hsHeaderState);
  hsHeaderStateRef.current = hsHeaderState;

  // The open RAMS editor publishes its document outline separately from the
  // H&S header state. On native sidebar shells this replaces the project
  // toolbox in the actual sidebar; activeSection also keeps the native
  // selection in sync with the editor page currently mounted.
  const [hsEditorState, setHsEditorState] = useState<{
    activeSection: string;
    sectionIds: readonly string[];
    isSaving?: boolean;
    saveLabel?: string;
    canAddItems?: boolean;
  } | null>(null);
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{
        activeSection: string;
        sectionIds: readonly string[];
        isSaving?: boolean;
        saveLabel?: string;
        canAddItems?: boolean;
      } | null>).detail;
      setHsEditorState(detail ?? null);
    };
    window.addEventListener('hs:editor-state-change', handler);
    return () => window.removeEventListener('hs:editor-state-change', handler);
  }, []);

  const [hsDocumentState, setHsDocumentState] = useState<{
    activeSection: string;
    sectionIds: readonly string[];
  } | null>(null);
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{
        activeSection: string;
        sectionIds: readonly string[];
      } | null>).detail;
      setHsDocumentState(detail ?? null);
    };
    window.addEventListener('hs:document-state-change', handler);
    return () => window.removeEventListener('hs:document-state-change', handler);
  }, []);

  // Takeoff toolbar action state — TakeoffView broadcasts takeoff:state-change
  // with canUpload/canRunAI/isAILoading/canExport/isExporting (detail is null
  // on unmount). Feeds the macOS-only takeoff navButtons block below, which
  // renders upload/fit/run-AI/export + the Types inspector toggle as native
  // window-toolbar buttons (the web header never mounts on the macOS shell).
  const [takeoffHeaderState, setTakeoffHeaderState] = useState<{
    gallery?: TakeoffHeaderState['gallery'];
    canUpload: boolean;
    canRunAI: boolean;
    isAILoading: boolean;
    canExport: boolean;
    isExporting: boolean;
    // True while the full-page "Add Drawings" uploader is open. Feeds
    // buildNativeHeaderState so the native back chevron closes the uploader
    // (back to the sheet gallery) instead of leaving the project.
    uploaderOpen: boolean;
    // At least one sheet exists — gates the native "Sheet details" toolbar
    // button, same as the web pill's hasSheets gate.
    hasSheets: boolean;
    // True while the after-the-fact Sheet details editor is open — hides its
    // own entry-point button so it can't be re-opened onto itself.
    sheetDetailsOpen: boolean;
    // Gallery multi-select — count + ids of currently-selected sheets, so the
    // native "Edit details" button can open TakeoffSheetDetailsEditor scoped
    // to just that subset (see the web pill's takeoff:sheet-details detail).
    selectedSheetCount: number;
    selectedSheetIds: string[];
    // Sheets on screen under the gallery's search + status tab — gates the
    // native "Select All" toolbar button the same way as the web pill (see
    // buildTakeoffHeaderActions' canSelectAll).
    visibleSheetCount: number;
    // Drawings the gallery's bulk Download would actually build (selection if
    // any, else everything visible, measured sheets only) and whether a build
    // is already running — gates and labels the native Download button, same
    // as the web pill (see buildTakeoffHeaderActions' canDownloadSheets).
    downloadableSheetCount: number;
    isDownloading: boolean;
  } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{
        canUpload: boolean;
        canRunAI: boolean;
        isAILoading: boolean;
        canExport: boolean;
        isExporting: boolean;
        uploaderOpen?: boolean;
        hasSheets?: boolean;
        sheetDetailsOpen?: boolean;
        selectedSheetCount?: number;
        selectedSheetIds?: string[];
        visibleSheetCount?: number;
        downloadableSheetCount?: number;
        isDownloading?: boolean;
      } | null>).detail;
      setTakeoffHeaderState(detail
        ? {
            ...detail,
            uploaderOpen: !!detail.uploaderOpen,
            hasSheets: !!detail.hasSheets,
            sheetDetailsOpen: !!detail.sheetDetailsOpen,
            selectedSheetCount: detail.selectedSheetCount ?? 0,
            selectedSheetIds: detail.selectedSheetIds ?? [],
            visibleSheetCount: detail.visibleSheetCount ?? 0,
            downloadableSheetCount: detail.downloadableSheetCount ?? 0,
            isDownloading: !!detail.isDownloading,
          }
        : null);
    };
    window.addEventListener('takeoff:state-change', handler);
    return () => window.removeEventListener('takeoff:state-change', handler);
  }, []);

  // Workspaces detail nav bridge — WorkspaceList broadcasts
  // 'workspaces-list:detail-change' with { clientId, clientName, activeTab }
  // whenever a workspace is opened on lg+ (null when closed, also on
  // cleanup). Feeds buildNativeNavItems/buildNativeHeaderState so the macOS
  // sidebar drills further in to the workspace's own section nav
  // (Organisation / Accounts / Quantis / Workspace) and the toolbar shows the
  // workspace name with a chevron that closes the detail.
  const [workspaceDetail, setWorkspaceDetail] = useState<{
    clientId: string;
    clientName: string;
    activeTab: string;
  } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<typeof workspaceDetail>).detail;
      setWorkspaceDetail(detail ?? null);
    };
    window.addEventListener('workspaces-list:detail-change', handler);
    return () => window.removeEventListener('workspaces-list:detail-change', handler);
  }, []);
  // The workspace file browser, under a company (staff) or Profile > Workspace (owners and admins).
  const workspaceDocumentsOpen =
    (currentView === 'workspaces' && workspaceDetail?.activeTab === 'documents')
    || (currentView === 'profile' && profileActiveTab.startsWith('workspace/documents'));

  // Whether the workspace Users tab is showing its LIST rather than an add /
  // edit form. Gates the native Add User toolbar action the same way it gates
  // the web header pills — the shells suppress the web header, so this is the
  // only place that decision can be made for them.
  const [workspaceUsersListVisible, setWorkspaceUsersListVisible] = useState(false);
  const [workspaceInviteState, setWorkspaceInviteState] = useState<{
    visible: boolean;
    actionsInHeader: boolean;
    canSubmit: boolean;
    submitting: boolean;
  } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{
        listVisible: boolean;
        inviteVisible?: boolean;
        inviteActionsInHeader?: boolean;
        inviteCanSubmit?: boolean;
        inviteSubmitting?: boolean;
      } | null>).detail;
      setWorkspaceUsersListVisible(!!detail?.listVisible);
      setWorkspaceInviteState(detail?.inviteVisible
        ? {
            visible: true,
            actionsInHeader: !!detail.inviteActionsInHeader,
            canSubmit: !!detail.inviteCanSubmit,
            submitting: !!detail.inviteSubmitting,
          }
        : null);
    };
    window.addEventListener(WORKSPACE_USERS_STATE_EVENT, handler);
    return () => window.removeEventListener(WORKSPACE_USERS_STATE_EVENT, handler);
  }, []);

  const [plannerEditorState, setPlannerEditorState] = useState<{
    visible: boolean;
    editing: boolean;
    canEdit: boolean;
    canSubmit: boolean;
    saving: boolean;
    presentation?: 'dialog' | 'drawer' | 'native-header';
  } | null>(null);
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<typeof plannerEditorState>).detail;
      setPlannerEditorState(detail ?? null);
    };
    window.addEventListener('planner:editor-state-change', handler);
    return () => window.removeEventListener('planner:editor-state-change', handler);
  }, []);

  // Drawer state is feature-owned, but native chrome is app-owned. Clear a
  // stale broadcast as soon as navigation leaves Planner so an interrupted
  // transition, hot reload or unmount can never leave the shell's controls
  // disabled/hidden over another route.
  useEffect(() => {
    if (currentView !== 'project-detail' || projectActiveTab !== 'planner') {
      setPlannerEditorState(null);
    }
  }, [currentView, projectActiveTab]);

  // Whether the edit-user form is open, so the native sidebar can drill into
  // its sections the way the web one does. Separate channel from the list
  // visibility above: the form broadcasts its own state.
  const [workspaceUserFormOpen, setWorkspaceUserFormOpen] = useState(false);
  const [profileSignatureState, setProfileSignatureState] = useState<SignatureHeaderState | null>(null);
  useLayoutEffect(() => {
    const handler = (event: Event) => setProfileSignatureState((event as CustomEvent<SignatureHeaderState | null>).detail ?? null);
    window.addEventListener('profile-signature:state-change', handler);
    return () => window.removeEventListener('profile-signature:state-change', handler);
  }, []);
  useEffect(() => {
    const handler = (e: Event) => {
      setWorkspaceUserFormOpen(!!(e as CustomEvent<unknown | null>).detail);
    };
    window.addEventListener(WORKSPACE_USER_FORM_STATE_EVENT, handler);
    return () => window.removeEventListener(WORKSPACE_USER_FORM_STATE_EVENT, handler);
  }, []);

  // What the Employers screen is showing — EmployerManagement broadcasts
  // { listVisible, statusFilter, selectedName } (null on unmount). Feeds four
  // native decisions the shells cannot make for themselves: whether to publish
  // the Add action, whether the contextual search bar belongs on screen, the
  // toolbar filter menu's checkmarks, and whether the sidebar drills in to the
  // profile's sections.
  // Employer named by the URL, not yet applied. Deliberately NOT threaded down
  // as a prop: App.tsx:202 records that documentRouteSlug and eventsRouteSlug
  // are already two hand-threaded sub-route slugs and that a third should force
  // a proper per-tab registry instead. This stays local and is delivered as an
  // intent once the screen is listening, which needs no new plumbing.
  const [employerRouteSlug, setEmployerRouteSlug] = useState<string | null>(null);
  const [employerRouteSection, setEmployerRouteSection] = useState<string | null>(null);

  const [employersListState, setEmployersListState] = useState<{
    listVisible: boolean;
    statusFilter: string;
    selectedName: string | null;
    // Both null unless a profile is open. They exist so the URL can name the
    // page the user is actually looking at rather than sitting on /employers.
    selectedId: string | null;
    activeSection: string | null;
  } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<typeof employersListState>).detail;
      setEmployersListState(detail ?? null);
    };
    window.addEventListener(EMPLOYERS_STATE_EVENT, handler);
    return () => window.removeEventListener(EMPLOYERS_STATE_EVENT, handler);
  }, []);

  // A drilled-in profile's unsaved-changes state. The ref exists because the
  // native action subscribers below are registered once and must read the value
  // at tap time: 'saveEmployer' is one native id serving two different saves
  // (the add/edit form and the profile), and only this tells them apart.
  const [employerDetailState, setEmployerDetailState] = useState<{
    isSubmitting: boolean;
    hasChanges: boolean;
  } | null>(null);
  const employerDetailStateRef = useRef(employerDetailState);
  employerDetailStateRef.current = employerDetailState;
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<typeof employerDetailState>).detail;
      setEmployerDetailState(detail ?? null);
    };
    window.addEventListener(EMPLOYER_DETAIL_STATE_EVENT, handler);
    return () => window.removeEventListener(EMPLOYER_DETAIL_STATE_EVENT, handler);
  }, []);

  // Outreach's drilled-in prospect, same always-live shape as the employer
  // profile above. The shells never render the web header that draws its
  // Save and Cancel, so this feeds the native equivalents instead.
  const [prospectDetailState, setProspectDetailState] = useState<{
    hasChanges: boolean;
    saving: boolean;
  } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      setProspectDetailState((e as CustomEvent<typeof prospectDetailState>).detail ?? null);
    };
    window.addEventListener(PROSPECT_DETAIL_STATE_EVENT, handler);
    return () => window.removeEventListener(PROSPECT_DETAIL_STATE_EVENT, handler);
  }, []);

  const employerOpenPendingRef = useRef(false);

  // Apply a URL-named employer once it can actually land. Two things have to be
  // true and neither is guaranteed when the route parses: EmployerManagement is
  // lazily imported, so an intent fired at parse time has no listener; and on a
  // cold load DataService may not hold the employer yet. `employersListState`
  // going non-null is the screen announcing itself, and the DataService
  // subscription covers the data arriving second.
  //
  // Gated on `listVisible`, not merely on the screen being mounted. A cold
  // deep-link can leave the slug unresolved for seconds while the cache loads;
  // if the user opens the Add form in the meantime, an unguarded late resolve
  // unmounts that half-filled form and replaces it with the profile. Waiting for
  // the directory to be the live state makes the intent land only where it is
  // harmless.
  useEffect(() => {
    if (!employerRouteSlug) return;
    // Abandon the pending route entirely once the user leaves Employers — coming
    // back should show the directory they asked for, not silently drill into a
    // slug they navigated away from minutes ago.
    if (currentView !== 'employers') {
      // An open requested from Companies sets the slug and the view in separate
      // transitions; wait for the view rather than abandoning the request.
      if (employerOpenPendingRef.current) return;
      setEmployerRouteSlug(null);
      setEmployerRouteSection(null);
      return;
    }
    employerOpenPendingRef.current = false;
    if (!employersListState?.listVisible) return;
    let done = false;
    const tryResolve = () => {
      if (done) return;
      const matched = DataService.getMainContractors().find(
        (c: MainContractor) => slugifySegment(c.name) === employerRouteSlug,
      ) || DataService.getMainContractor(employerRouteSlug) || null;
      if (!matched) return;
      done = true;
      window.dispatchEvent(new CustomEvent(EMPLOYERS_SELECT_EVENT, {
        detail: { id: matched.id, section: employerRouteSection ?? undefined },
      }));
      // Consumed. Clearing it stops a later back-to-directory being overridden
      // by this same slug on the next render.
      setEmployerRouteSlug(null);
      setEmployerRouteSection(null);
    };
    tryResolve();
    if (done) return;
    const unsubscribe = DataService.subscribe(tryResolve);
    return () => unsubscribe();
  }, [employerRouteSlug, employerRouteSection, employersListState, currentView]);

  // The Companies page asks for an employer profile or the add form. The route state is set inside the
  // same transition as the view change so the abandon check above never sees one without the other.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ id?: string; section?: string }>).detail;
      if (!detail?.id) return;
      employerOpenPendingRef.current = true;
      startTransition(() => {
        setMainContractorInitialTab('contractors');
        setEmployerRouteSlug(detail.id!);
        setEmployerRouteSection(detail.section ?? null);
      });
      navigateToView('employers');
    };
    const onNew = () => {
      setMainContractorInitialTab('add-contractor');
      navigateToView('employers');
    };
    window.addEventListener(COMPANIES_OPEN_EMPLOYER_EVENT, onOpen);
    window.addEventListener(COMPANIES_NEW_EMPLOYER_EVENT, onNew);
    return () => {
      window.removeEventListener(COMPANIES_OPEN_EMPLOYER_EVENT, onOpen);
      window.removeEventListener(COMPANIES_NEW_EMPLOYER_EVENT, onNew);
    };
  }, [navigateToView, setMainContractorInitialTab]);

  // The employer add/edit form's dirty/submitting state. Separate channel from
  // the screen state above, same split as the workspace user form.
  const [employerFormState, setEmployerFormState] = useState<{
    isEdit: boolean;
    isSubmitting: boolean;
    hasChanges: boolean;
    // The form's own section nav, which the sidebar draws in place of the app
    // nav. `sectionIds` is the visible set, not the full one: Credit Check comes
    // and goes with the entity type and the company name, so the rows have to be
    // built from what the form says exists.
    activeSection: string;
    sectionIds: readonly string[];
  } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<typeof employerFormState>).detail;
      setEmployerFormState(detail ?? null);
    };
    window.addEventListener(EMPLOYER_FORM_STATE_EVENT, handler);
    return () => window.removeEventListener(EMPLOYER_FORM_STATE_EVENT, handler);
  }, []);

  // Plant register — same bridge shape as Employers above, simpler because
  // the URL carries the machine's own id rather than a name slug: the
  // register isn't part of the DataService cache those slugs resolve
  // against, so there is nothing here to match, only to pass through once
  // PlantRegisterView is mounted and listening.
  const [plantRouteAssetId, setPlantRouteAssetId] = useState<string | null>(null);
  // The batch or prospect drilled into under outreachActiveTab, which itself
  // stays a bare section id for the sidebar and the native section menu.
  const [outreachRecordId, setOutreachRecordId] = useState<string | null>(null);
  // Set when Outreach drops a record the server rejected: the table replaces
  // that dead entry instead of pushing over it, or Back would reopen the bad
  // id and bounce forward again.
  const outreachReplaceRef = useRef(false);
  const [plantListState, setPlantListState] = useState<PlantListState | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PlantListState | null>).detail;
      setPlantListState(detail ?? null);
    };
    window.addEventListener(PLANT_STATE_EVENT, handler);
    return () => window.removeEventListener(PLANT_STATE_EVENT, handler);
  }, []);
  // A drilled-in machine's unsaved-changes state — same shape as
  // employerDetailState above, feeding the native Save (savePlant), since
  // the web header's Cancel/Save pills (buildPlantHeaderActions) are
  // suppressed on both shells.
  const [plantDetailState, setPlantDetailState] = useState<PlantDetailState | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PlantDetailState | null>).detail;
      setPlantDetailState(detail ?? null);
    };
    window.addEventListener(PLANT_DETAIL_STATE_EVENT, handler);
    return () => window.removeEventListener(PLANT_DETAIL_STATE_EVENT, handler);
  }, []);
  useEffect(() => {
    if (!plantRouteAssetId) return;
    if (currentView !== 'plant') { setPlantRouteAssetId(null); return; }
    if (!plantListState?.listVisible) return;
    window.dispatchEvent(new CustomEvent(PLANT_SELECT_EVENT, { detail: { id: plantRouteAssetId } }));
    setPlantRouteAssetId(null);
  }, [plantRouteAssetId, plantListState, currentView]);

  // Workspace form dirty/saving state — WorkspaceOverview broadcasts
  // 'workspace:state-change' with { hasChanges, isSaving } (null on unmount).
  // The web header pills that normally surface Save/Cancel are suppressed on
  // the macOS shell, so this feeds buildNativeHeaderState's Save primary
  // action + Cancel secondary action, otherwise the form has no save
  // affordance at all on that platform.
  const [workspaceSaveState, setWorkspaceSaveState] = useState<{
    hasChanges: boolean;
    isSaving: boolean;
  } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<typeof workspaceSaveState>).detail;
      setWorkspaceSaveState(detail ?? null);
    };
    window.addEventListener('workspace:state-change', handler);
    return () => window.removeEventListener('workspace:state-change', handler);
  }, []);

  // 'profile-details:state-change' with { hasChanges } (null when clean).
  // Same shape as the workspace form above: useProfileDraft broadcasts its
  // staged-edit state and buildNativeHeaderState turns it into the native
  // Save/Cancel toolbar pair. It must ride THIS central publish rather than a
  // sendHeaderState of its own — the shell resets absent header fields (no
  // merge), so a partial publish from the hook stripped the profile section
  // back chevron.
  const [profileDetailsDirty, setProfileDetailsDirty] = useState(false);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ hasChanges?: boolean } | null>).detail;
      setProfileDetailsDirty(!!detail?.hasChanges);
    };
    window.addEventListener('profile-details:state-change', handler);
    return () => window.removeEventListener('profile-details:state-change', handler);
  }, []);

  // Workspaces directory list state — WorkspaceList broadcasts
  // 'workspaces-list:state-change' with { filterStatus, activeFilterCount }
  // (plus web-only fields; null on unmount). Feeds the macOS toolbar's
  // Filter menu (live checkmarks + badge) so the native header carries the
  // same filter the web header shows for this view.
  const [workspaceCreateState, setWorkspaceCreateState] = useState({ busy: false });
  useEffect(() => {
    const onState = (event: Event) => {
      const detail = (event as CustomEvent<{ step: number; busy: boolean }>).detail;
      if (detail) setWorkspaceCreateState(detail);
    };
    window.addEventListener('workspaces:create-state', onState);
    window.dispatchEvent(new CustomEvent('workspaces:create-request-state'));
    return () => window.removeEventListener('workspaces:create-state', onState);
  }, []);
  const [workspacesListState, setWorkspacesListState] = useState<{
    filterStatus: string;
    classificationFilter?: string;
    activeFilterCount: number;
  } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<typeof workspacesListState>).detail;
      setWorkspacesListState(detail ?? null);
    };
    window.addEventListener('workspaces-list:state-change', handler);
    return () => window.removeEventListener('workspaces-list:state-change', handler);
  }, []);

  // Enquiries board Filter + Sort — ConsultancyPipelineView broadcasts its
  // serviceFilter/sortKey + service list; we turn each into a navButton that
  // carries menuItems, which the macOS shell renders as an anchored SwiftUI
  // Menu (native popover). Item taps round back via quantis:native:menuAction,
  // which ConsultancyPipelineView routes into filter/sort state.
  const [pipelineBoardState, setPipelineBoardState] = useState<{
    serviceFilter: string;
    sortKey: 'newest' | 'oldest' | 'value' | 'idle';
    services: [string, string][];
  } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<typeof pipelineBoardState>).detail;
      setPipelineBoardState(detail ?? null);
    };
    window.addEventListener('pipeline:state-change', handler);
    return () => window.removeEventListener('pipeline:state-change', handler);
  }, []);

  // Takeoff export-template preview (Settings → Templates) — the screen
  // broadcasts its sample preview state; we turn the actions into toolbar
  // buttons. The web header never mounts on the
  // macOS shell, so without this the screen's controls have nowhere to go.
  const [templatePreviewState, setTemplatePreviewState] = useState<{
    busy: boolean;
    zoomMode: 'fit' | 'width';
    canBack: boolean;
  } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<typeof templatePreviewState>).detail;
      setTemplatePreviewState(detail ?? null);
    };
    window.addEventListener('takeoff-template:state-change', handler);
    // The screen can mount before us on a cold load; ask it to resend.
    window.dispatchEvent(new CustomEvent('takeoff-template:request-state'));
    return () => window.removeEventListener('takeoff-template:state-change', handler);
  }, []);

  // Hub sub-tab tracking — hubs broadcast their active section via
  // window events so the native route-state can reflect the selection.
  const [payrollActiveTab, setPayrollActiveTab] = useState<string>('overview');
  const [accountingActiveTab, setAccountingActiveTab] = useState<string>('bank-accounts');
  const [salesActiveTab, setSalesActiveTab] = useState<string>('overview');
  const [purchasesActiveTab, setPurchasesActiveTab] = useState<string>('overview');
  const [taxActiveTab, setTaxActiveTab] = useState<string>('vat-returns');
  // Refs keep the navigate-listener closure in sync without re-subscribing.
  const payrollActiveTabRef = useRef(payrollActiveTab);
  payrollActiveTabRef.current = payrollActiveTab;
  const accountingActiveTabRef = useRef(accountingActiveTab);
  accountingActiveTabRef.current = accountingActiveTab;
  const salesActiveTabRef = useRef(salesActiveTab);
  salesActiveTabRef.current = salesActiveTab;
  const purchasesActiveTabRef = useRef(purchasesActiveTab);
  purchasesActiveTabRef.current = purchasesActiveTab;
  const taxActiveTabRef = useRef(taxActiveTab);
  taxActiveTabRef.current = taxActiveTab;
  const nativeBridge = useNativeBridge();
  // EmailView broadcasts thread state so the native header can switch between
  // inbox tools and the thread's own reading/actions chrome. The web header
  // already listens to the same event; the shell needs the state too because
  // it replaces the web header wholesale.
  const [emailThreadState, setEmailThreadState] = useState<{
    subject: string;
    isStarred: boolean;
  } | null>(null);
  useEffect(() => {
    const onThreadState = (event: Event) => {
      const detail = (event as CustomEvent<{ subject?: string; isStarred?: boolean } | null>).detail;
      setEmailThreadState(
        detail?.subject
          ? { subject: detail.subject, isStarred: !!detail.isStarred }
          : null,
      );
    };
    window.addEventListener('email:thread-state', onThreadState);
    return () => window.removeEventListener('email:thread-state', onThreadState);
  }, []);
  // Shared mailbox membership is loaded by AppSidebar, which already owns the
  // permission-scoped email_groups query used by the web sidebar. Mirror that
  // result into the native nav rather than issuing a second registry request.
  const [emailSharedMailboxes, setEmailSharedMailboxes] = useState<
    { id: string; label: string }[]
  >([]);
  useEffect(() => {
    const onSharedMailboxes = (event: Event) => {
      const detail = (event as CustomEvent<{ mailboxes?: { id: string; label: string }[] } | null>).detail;
      setEmailSharedMailboxes(Array.isArray(detail?.mailboxes) ? detail.mailboxes : []);
    };
    window.addEventListener('email:shared-mailboxes-state', onSharedMailboxes);
    window.dispatchEvent(new CustomEvent('email:request-shared-mailboxes-state'));
    return () => window.removeEventListener('email:shared-mailboxes-state', onSharedMailboxes);
  }, []);
  // macOS/iPad shells suppress AppSidebar because the native shell owns the
  // visible sidebar. In that mode the event above has no publisher, so load
  // the same permission-scoped registry here as a headless bridge. Keeping
  // this query in the controller also means the native nav is populated on a
  // hard reload before the first sidebar payload is sent.
  useEffect(() => {
    if (!NativeBridge.isRunningNatively() || !auth.user?.id) return;
    const workspaceId = (auth.user as any)?.workspace_id ?? (auth.user as any)?.workspaceId ?? null;
    if (!workspaceId) {
      setEmailSharedMailboxes([]);
      return;
    }
    let cancelled = false;
    import('../services/EmailGroupsService').then(({ emailGroupsService }) => {
      emailGroupsService
        .list(workspaceId, { onlyMine: true, userId: auth.user!.id })
        .then((rows) => {
          if (cancelled) return;
          setEmailSharedMailboxes(rows.map(({ id, label }) => ({ id, label })));
        })
        .catch(() => {
          if (!cancelled) setEmailSharedMailboxes([]);
        });
    }).catch(() => {
      if (!cancelled) setEmailSharedMailboxes([]);
    });
    return () => { cancelled = true; };
  }, [auth.user?.id, (auth.user as any)?.workspace_id, (auth.user as any)?.workspaceId]);
  // No live shell renders the side panels natively; the macOS shell draws a
  // native sidebar but panels stay web-rendered inside the webview.
  const nativeOwnsSidePanel = false;
  const [nativeProfileAvatarUrl, setNativeProfileAvatarUrl] = useState<string | null>(null);
  // Presence status received from AppSidebar via the quantis:presence:status-change
  // window event. Kept as native contract id so it drops straight into sidebarState.
  const [nativePresenceStatus, setNativePresenceStatus] = useState<NativePresenceDisplayId | null>(null);
  // macOS only: BOQ bill/summary picker published into the native toolbar when
  // the active project tab is 'pricing'. Updated by listening to boq:state-change.
  const [boqViewSwitcher, setBoqViewSwitcher] = useState<
    { options: { id: string; label: string }[]; selectedId: string; style?: 'segmented' | 'menu' } | null
  >(null);
  // macOS only: Resource Library picker published into the native toolbar when
  // that view is active. The web LibraryPicker only ever renders inside the
  // header row the native shell suppresses, so without this there is no way to
  // change library on macOS at all. Updated from resource-library:libraries-change.
  const [resourceLibraryViewSwitcher, setResourceLibraryViewSwitcher] = useState<
    { options: { id: string; label: string }[]; selectedId: string; style: 'menu' } | null
  >(null);
  // Read inside the native headerControl subscriber to tell a library id from a
  // BOQ bill id. A ref keeps it out of that effect's deps, because re-subscribing
  // every time the library list changes would drop presses in the gap.
  const resourceLibraryViewSwitcherRef = useRef(resourceLibraryViewSwitcher);
  resourceLibraryViewSwitcherRef.current = resourceLibraryViewSwitcher;
  // True while a rate item is open, so the toolbar picker can stand down the way
  // the web one does rather than switching library out from under unsaved edits.
  const [resourceLibraryItemFormOpen, setResourceLibraryItemFormOpen] = useState(false);
  // macOS only: the rate-item list's own toolbar state (collapse,
  // permissions), broadcast by LibraryItemsPane. Every page action on this view
  // is a pill in the web header row the native shell replaces wholesale, so
  // without mirroring this state into navButtons none of them exist there.
  const [resourceLibraryItemsState, setResourceLibraryItemsState] =
    useState<ResourceLibraryItemsHeaderState | null>(null);

  // Refs so the native navigate listener closure always sees the latest values
  // without needing them in the effect dependency array (which would cause the
  // listener to unsubscribe/re-subscribe on every navigation, leaving a window
  // where incoming native events queue as pendingPayloads and replay).
  const currentViewRef = useRef(currentView);
  const workspaceDocumentsOpenRef = useRef(false);
  workspaceDocumentsOpenRef.current = workspaceDocumentsOpen;
  // Where the Notifications bell was tapped from, so its back chevron can
  // return there (see NOTIFICATIONS_BACK_SENTINEL). Notifications has no
  // sidebar row, so "back" is the page's only first-class exit.
  const notificationsReturnViewRef = useRef<AppView | null>(null);
  currentViewRef.current = currentView;
  // Read inside the native primaryAction subscriber, which registers once —
  // a plain closure over workspaceDetail would go stale on every tab change.
  const workspaceDetailRef = useRef(workspaceDetail);
  workspaceDetailRef.current = workspaceDetail;
  const projectActiveTabRef = useRef(projectActiveTab);
  projectActiveTabRef.current = projectActiveTab;
  const notificationOpenRef = useRef(notificationOpen);
  notificationOpenRef.current = notificationOpen;
  const messagePanelOpenRef = useRef(messagePanelOpen);
  messagePanelOpenRef.current = messagePanelOpen;
  const selectedProjectIdRef = useRef(selectedProjectId);
  selectedProjectIdRef.current = selectedProjectId;
  // handleSelectProject closes over currentView/selectedProjectId (unstable useCallback),
  // so capture it in a ref to keep the navigate listener effect stable.
  const handleSelectProjectRef = useRef(handleSelectProject);
  handleSelectProjectRef.current = handleSelectProject;

  const urlParams = new URLSearchParams(window.location.search);
  const navigateImmediatelyForNative = useCallback((view: AppView) => {
    setCalendarProjectScope(null);
    setCurrentViewImmediate(view);
  }, [setCalendarProjectScope, setCurrentViewImmediate]);

  const handleNativeViewNavigation = useCallback((targetView: AppView) => {
    switch (targetView) {
      case 'dashboard':
        setSelectedProjectId(null);
        setSelectedProjectName(null);
        setSelectedClientId(null);
        setSelectedClientName(null);
        setSelectedTaskId(null);
        setDocumentRouteSlug(null);
        setTaskRouteSlug(null);
        setProjectActiveTab('overview');
        setNotificationOpen(false);
        setMessagePanelOpen(false);
        setPanelChatId(null);
        setPanelThreadMessageId(null);
        navigateImmediatelyForNative('dashboard');
        return;
      case 'projects':
        window.history.replaceState(
          {
            view: 'projects',
            projectId: null,
            projectName: null,
            projectActiveTab: null,
            documentRouteSlug: null,
          },
          document.title,
          buildViewPath('projects')
        );
        setSelectedProjectId(null);
        setSelectedProjectName(null);
        setDocumentRouteSlug(null);
        setTaskRouteSlug(null);
        setProjectActiveTab('overview');
        setNotificationOpen(false);
        setMessagePanelOpen(false);
        setPanelChatId(null);
        setPanelThreadMessageId(null);
        navigateImmediatelyForNative('projects');
        return;
      case 'tasks':
        // WHY: native shells still send 'tasks' as the view identifier; we remap
        // to 'calendar' which now hosts the Tasks tab.
        setSelectedTaskId(null);
        setCalendarTasksTab(true);
        setTaskRouteSlug(null);
        setDocumentRouteSlug(null);
        setNotificationOpen(false);
        setMessagePanelOpen(false);
        setPanelChatId(null);
        setPanelThreadMessageId(null);
        navigateImmediatelyForNative('calendar');
        return;
      case 'workspaces':
        setSelectedClientId(null);
        setSelectedClientName(null);
        setNotificationOpen(false);
        setMessagePanelOpen(false);
        setPanelChatId(null);
        setPanelThreadMessageId(null);
        navigateImmediatelyForNative('workspaces');
        return;
      case 'notifications':
        setNotificationOpen(false);
        setMessagePanelOpen(false);
        setPanelChatId(null);
        setPanelThreadMessageId(null);
        navigateImmediatelyForNative('notifications');
        return;
      case 'messages':
        setNotificationOpen(false);
        setMessagePanelOpen(false);
        setPanelChatId(null);
        setPanelThreadMessageId(null);
        navigateImmediatelyForNative('messages');
        return;
      default:
        setNotificationOpen(false);
        setMessagePanelOpen(false);
        setPanelChatId(null);
        setPanelThreadMessageId(null);
        navigateImmediatelyForNative(targetView);
    }
  }, [
    navigateImmediatelyForNative,
    setDocumentRouteSlug,
    setMessagePanelOpen,
    setNotificationOpen,
    setPanelChatId,
    setPanelThreadMessageId,
    setProjectActiveTab,
    setSelectedClientId,
    setSelectedClientName,
    setSelectedProjectId,
    setSelectedProjectName,
    setSelectedTaskId,
    setTaskRouteSlug,
  ]);

  useEffect(() => {
    // Native-chrome shells (iOS tab bar, macOS sidebar + toolbar) replace the
    // web main header; the native toolbar renders headerState instead.
    setNativeShellHidesWebMainHeader(nativeBridge.shouldHideWebNavigation);
  }, [nativeBridge.shouldHideWebNavigation, setNativeShellHidesWebMainHeader]);

  // The profile row carries supervisor_role once it hydrates, after the
  // first render, so the tab props recompute when it arrives.
  const userIsSiteSupervisor = isSiteSupervisor(auth.user);

  useEffect(() => {
    if (currentView !== 'project-detail' || !selectedProjectId) {
      setProjectTabProps(null);
      return;
    }

    const computeTabProps = () => {
      const project = DataService.getProject(selectedProjectId);
      const isClient =
        auth.user?.role === 'workspace_owner' || auth.user?.role === 'workspace_admin' ||
        auth.user?.role === 'workspace_user'  ||
        auth.user?.role === 'member'          || auth.user?.role === 'client';
      const isStaff =
        auth.user?.role === 'super_admin' || auth.user?.role === 'internal' ||
        auth.user?.role === 'admin'       || auth.user?.role === 'staff';
      const isEstimatingPhase = project?.stage === 'estimating' || project?.stage === 'tender';
      const tasks = DataService.getTasksByProject(selectedProjectId);
      const claims = DataService.getClaimsByProject(selectedProjectId);
      const activeTasksCount = tasks.filter((task: any) => task.status !== 'completed').length;
      const activeClaimsCount = claims.filter((claim: any) =>
        !['approved', 'rejected', 'withdrawn', 'settled'].includes(claim.status)
      ).length;
      // isStaff above also admits the customer-side `admin` account-holder
      // role (see its own comment history) - the Tendering tab shows a
      // competing bidder's prices, so it needs the narrower Contract Group
      // staff-only check instead.
      const roleProfile = getContractRoleProfile({ actingFor: project?.actingFor, appointment: project?.appointment });
      const runsTender = userRunsTender(auth.user?.role, { actingFor: project?.actingFor, appointment: project?.appointment });
      // The contract document register carries the Employer's own notices and
      // the CA's certificates, so it is Contract Group staff only for the same
      // reason tendering is, and only where there is an appointment to issue
      // them under.
      const tenderDocumentKinds = new Set(['itt_letter', 'tender_report', 'award_letter', 'unsuccessful_letter']);
      const issuesContractDocuments =
        isInternalRole(auth.user?.role as UserRole)
        && roleProfile.issuableKinds.some((kind) => !tenderDocumentKinds.has(kind));

      setProjectTabProps({
        isClient: !!isClient,
        isEstimatingPhase: !!isEstimatingPhase,
        activeTasksCount,
        activeClaimsCount,
        isStaff: !!isStaff,
        projectId: selectedProjectId,
        projectName: selectedProjectName || project?.name || 'Project',
        projectStage: project?.stage,
        internalUserIds: project?.assignedStaffIds || [],
        runsTender,
        issuesContractDocuments,
        applicationWorkflow: roleProfile.applicationWorkflow,
        canGivePayLessNotice: roleProfile.issuableKinds.includes('pay_less_notice'),
        isSiteSupervisor: userIsSiteSupervisor,
      });
    };

    computeTabProps();
    const unsubscribe = DataService.subscribe(computeTabProps);
    return unsubscribe;
  }, [auth.user?.role, userIsSiteSupervisor, currentView, selectedProjectId, selectedProjectName, setProjectTabProps]);

  useEffect(() => {
    const openProjectCalendar = (event: Event) => {
      const detail = (event as CustomEvent<{ projectId: string | null }>).detail;
      if (!detail || (detail.projectId !== null && typeof detail.projectId !== 'string')) return;
      if (detail.projectId === null) {
        setCalendarProjectScope(null);
      } else {
        const project = DataService.getProjects().find((candidate: Project) => candidate.id === detail.projectId);
        const company = project ? getCompany(project.clientId) : null;
        if (!project || !company) return;
        setCalendarProjectScope(projectCalendarScope(company.name, project.name));
      }
      setSelectedTaskId(null);
      setTaskRouteSlug(null);
      setCalendarTasksTab(false);
      navigateToView('calendar');
    };
    window.addEventListener('calendar:open-project', openProjectCalendar);
    return () => window.removeEventListener('calendar:open-project', openProjectCalendar);
  }, [getCompany, navigateToView, setCalendarProjectScope, setSelectedTaskId, setTaskRouteSlug, setCalendarTasksTab]);

  useEffect(() => {
    const resolveProjectFromSlugs = (workspaceSlug: string, projectSlug: string) => {
      const projects = DataService.getProjects();
      const companies = DataService.getCompanies();

      return (
        projects.find((project: Project) => {
          const company =
            companies.find((candidate: Company) => candidate.id === project.clientId) ||
            getCompany(project.clientId);
          if (!company) return false;

          return (
            slugifySegment(company.name) === workspaceSlug &&
            slugifySegment(project.name) === projectSlug
          );
        }) || null
      );
    };

    const resolveMembershipCompanyFromSlug = (companySlug: string) => {
      const companies = DataService.getCompanies();
      return (
        companies.find(
          (company: Company) =>
            company.type === 'client' &&
            slugifySegment(company.name) === companySlug
        ) || null
      );
    };

    const getProjectTabFromSearch = (search: string) => {
      const params = new URLSearchParams(search);
      const tab = params.get('tab')?.trim();
      return tab || null;
    };

    const getProjectItemFromSearch = (search: string) => {
      const params = new URLSearchParams(search);
      const item = params.get('item')?.trim();
      return item || null;
    };

    // URL restoration must commit the view with its entity setters. A transition
    // can leave Dashboard active after a pending slug resolves, allowing an
    // intervening auth/data render to overwrite the restored project URL.
    const applyLocationState = (pathname: string, search: string, historyState?: any): boolean => {
      // Addresses for settings that became personal Preferences are rewritten
      // in place first, so every later check sees only the new grammar and Back
      // does not return to a dead address.
      const preferencesPath = resolveLegacyPreferencesPath(pathname);
      if (preferencesPath) {
        window.history.replaceState(null, '', `${preferencesPath}${search}`);
        return applyLocationState(preferencesPath, search);
      }
      // In the browser Preferences is a modal, so its address opens the modal
      // over Home; the native shells keep the full page below.
      const preferencesRoute = usesSettingsModal() ? matchViewPath(pathname) : null;
      if (preferencesRoute?.view === 'preferences') {
        window.history.replaceState(null, '', `/${search}`);
        const applied = applyLocationState('/', search);
        // Opened after Home settles, whose address writes would otherwise drop the modal's #preferences hash.
        openSettingsModal(preferencesRoute.slug);
        return applied;
      }
      if (/^\/calendar(?:\/tasks(?:\/[^/]+)?)?\/?$/.test(pathname)) {
        setCalendarProjectScope(readProjectCalendarScope(pathname, search));
      }
      const queryTab = getProjectTabFromSearch(search);
      const queryItem = getProjectItemFromSearch(search);
      const commentParams = new URLSearchParams(search);
      const commentProjectId = commentParams.get('boqProjectId');
      if ((pathname === '/projects' || pathname === '/projects/') && commentProjectId
        && commentParams.has('boqDocumentId') && commentParams.has('boqItemId') && commentParams.has('boqCommentId')) {
        isPopStateRef.current = true;
        setSelectedProjectId(commentProjectId);
        setSelectedProjectName(DataService.getProject(commentProjectId)?.name ?? 'Project');
        setProjectActiveTab('pricing');
        setCurrentViewImmediate('project-detail');
        return true;
      }

      // Contact detail owns its selection locally because the directory has to
      // merge workspace users and employer contacts before it can resolve the
      // stable key. Keep the route intact while the lazy screen hydrates it.
      if (pathname === '/contacts' || pathname === '/contacts/' || parsePrettyContactPath(pathname) || parseCompanyPath(pathname)) {
        isPopStateRef.current = true;
        // The list address owns the page. selectedClientId is persisted, so without this a reload or a
        // Back from a client leaves the Workspaces screen a client to preselect and re-address.
        setSelectedClientId(null);
        setSelectedClientName(null);
        navigateToView('contacts');
        return true;
      }

      // The platform Workspaces directory spans every customer account. It is
      // not the customer's own Workspace Settings screen (`/organisation`).
      // Block legacy aliases and every nested direct URL before resolving any
      // company data. Rendering and sidebar visibility carry the same gate.
      const isPlatformWorkspacePath =
        /^\/(?:workspaces|businesses|client-management)(?:\/|$)/.test(pathname) ||
        /^\/add-workspace\/?$/.test(pathname);
      if (isPlatformWorkspacePath && !canManagePlatformWorkspaces) {
        isPopStateRef.current = true;
        setSelectedClientId(null);
        setSelectedClientName(null);
        setDocumentRouteSlug(null);
        setTaskRouteSlug(null);
        navigateToView('dashboard');
        window.history.replaceState({ view: 'dashboard' }, document.title, '/');
        return true;
      }

      if (pathname === '/employers/add' || pathname === '/employers/add/') {
        isPopStateRef.current = true;
        setMainContractorInitialTab('add-contractor');
        navigateToView('employers');
        return true;
      }

      if (pathname === '/employers' || pathname === '/employers/') {
        // The Employers directory is now the Contacts page; keep the old address alive.
        isPopStateRef.current = true;
        setMainContractorInitialTab('contractors');
        setEmployerRouteSlug(null);
        setEmployerRouteSection(null);
        window.history.replaceState({ view: 'contacts' }, document.title, '/contacts');
        navigateToView('contacts');
        return true;
      }

      // /employers/[slug] and /employers/[slug]/[section] — a drilled-in profile
      // on a named page. The slug is handed to EmployerManagement as a prop
      // rather than resolved here: the screen is lazily imported, so an event
      // fired at parse time has nobody listening yet, and on a cold load
      // DataService may not have the employer either. As a prop it resolves
      // whenever both are ready, which is how requestedDocumentSlug works.
      const employerRoute = parsePrettyEmployerPath(pathname);
      if (employerRoute) {
        isPopStateRef.current = true;
        setMainContractorInitialTab('contractors');
        setEmployerRouteSlug(employerRoute.employerSlug);
        setEmployerRouteSection(employerRoute.section ?? null);
        navigateToView('employers');
        return true;
      }

      if (pathname === '/workspaces' || pathname === '/workspaces/' || pathname === '/businesses' || pathname === '/businesses/' || pathname === '/client-management' || pathname === '/client-management/') {
        isPopStateRef.current = true;
        // The Workspaces directory is now the Contacts page. Requests keep their own address.
        if (queryTab !== 'requests') {
          setSelectedClientId(null);
          setSelectedClientName(null);
          window.history.replaceState({ view: 'contacts' }, document.title, '/contacts');
          navigateToView('contacts');
          return true;
        }
        setSelectedClientId(null);
        setSelectedClientName(null);
        setDocumentRouteSlug(null);
        setTaskRouteSlug(null);
        setWorkspacesActiveTab('requests');
        navigateToView('workspaces');
        return true;
      }

      if (
        pathname === '/workspaces/add-workspace' ||
        pathname === '/workspaces/add-workspace/' ||
        // Legacy / direct-entry alias — the URL builder canonicalises
        // to the nested path on the very next state push.
        pathname === '/add-workspace' ||
        pathname === '/add-workspace/'
      ) {
        isPopStateRef.current = true;
        setSelectedClientId(null);
        setSelectedClientName(null);
        setDocumentRouteSlug(null);
        setTaskRouteSlug(null);
        navigateToView('add-workspace');
        return true;
      }

      // Pretty-path branches below: when the URL parses but DataService can't
      // resolve the entity yet (initial mount before data loads), return false
      // so pendingLocationRestoreRef + the DataService.subscribe retry takes
      // over. Falling through to the historyState fallback can bounce the
      // user to Project List on browser refresh.
      const prettyTakeoffRoute = parsePrettyProjectTakeoffPath(pathname);
      if (prettyTakeoffRoute) {
        const matchedProject = resolveProjectFromSlugs(
          prettyTakeoffRoute.workspaceSlug,
          prettyTakeoffRoute.projectSlug
        );
        if (matchedProject) {
          isPopStateRef.current = true;
          setDocumentRouteSlug(null);
          setEventsRouteSlug(null);
          setTakeoffDrawingRoute({
            sheetId: new URLSearchParams(search).get('sheet'),
            sheetName: prettyTakeoffRoute.sheetSlug,
          });
          setSelectedProjectId(matchedProject.id);
          setSelectedProjectName(matchedProject.name);
          setProjectActiveTab('takeoff');
          setCurrentViewImmediate('project-detail');
          return true;
        }
        return false;
      }

      const prettyDocumentRoute = parsePrettyProjectDocumentPath(pathname);
      if (prettyDocumentRoute) {
        const matchedProject = resolveProjectFromSlugs(
          prettyDocumentRoute.workspaceSlug,
          prettyDocumentRoute.projectSlug
        );
        if (matchedProject) {
          isPopStateRef.current = true;
          setTakeoffDrawingRoute(null);
          // A workflow file link names the file by id (`name--id`); hold the id for the screen.
          const documentTarget = documentLinkFromSegment(prettyDocumentRoute.documentSlug);
          setDocumentRouteSlug(documentTarget.slug);
          rememberDocumentDeepLink(documentTarget.link);
          setEventsRouteSlug(null);
          setSelectedProjectId(matchedProject.id);
          setSelectedProjectName(matchedProject.name);
          setProjectActiveTab('documents');
          setCurrentViewImmediate('project-detail');
          return true;
        }
        return false;
      }

      // /projects/[client]/[project]/changes/events/[...tail] — events tab with an
      // open detail/form. Must be checked before the 3-segment tab parser.
      const prettyEventsRoute = parsePrettyProjectEventsPath(pathname);
      if (prettyEventsRoute) {
        const matchedProject = resolveProjectFromSlugs(
          prettyEventsRoute.workspaceSlug,
          prettyEventsRoute.projectSlug
        );
        if (matchedProject) {
          isPopStateRef.current = true;
          setTakeoffDrawingRoute(null);
          setDocumentRouteSlug(null);
          setEventsRouteSlug(prettyEventsRoute.eventsRoute);
          setSelectedProjectId(matchedProject.id);
          setSelectedProjectName(matchedProject.name);
          setProjectActiveTab('events');
          if (/^\/projects\/[^/]+\/[^/]+\/events\//.test(pathname)) {
            window.history.replaceState(window.history.state, '', `${pathname.replace(/^(\/projects\/[^/]+\/[^/]+)\/events\//, '$1/changes/events/')}${search}`);
          }
          setCurrentViewImmediate('project-detail');
          return true;
        }
        return false;
      }

      const prettyTeamMemberRoute = parsePrettyProjectTeamMemberPath(pathname);
      if (prettyTeamMemberRoute) {
        const matchedProject = resolveProjectFromSlugs(
          prettyTeamMemberRoute.workspaceSlug,
          prettyTeamMemberRoute.projectSlug
        );
        if (matchedProject) {
          isPopStateRef.current = true;
          setTakeoffDrawingRoute(null);
          setDocumentRouteSlug(null);
          setEventsRouteSlug(null);
          setSelectedProjectId(matchedProject.id);
          setSelectedProjectName(matchedProject.name);
          setProjectActiveTab('team');
          setCurrentViewImmediate('project-detail');
          return true;
        }
        return false;
      }

      // /calendar/tasks is the calendar's Tasks tab with nothing open; the
      // older bare /tasks still lands there. 'tasks' is still an AppView
      // (native shells and cached menus send it), so without this branch the
      // bare-view fallthrough below would select a view nothing renders, and
      // persist it, so even / booted blank afterwards.
      if (/^\/(?:calendar\/)?tasks\/?$/.test(pathname)) {
        isPopStateRef.current = true;
        setDocumentRouteSlug(null);
        setTaskRouteSlug(null);
        setSelectedTaskId(null);
        setCalendarTasksTab(true);
        navigateToView('calendar');
        return true;
      }

      const prettyTaskRoute = parsePrettyTaskPath(pathname);
      if (prettyTaskRoute) {
        const matchedTask =
          (prettyTaskRoute.taskId
            ? DataService.getTasks().find((task: Task) => task.id === prettyTaskRoute.taskId)
            : DataService.getTasks().find(
                (task: Task) => slugifySegment(task.title) === prettyTaskRoute.taskSlug
              )) || null;
        if (matchedTask) {
          isPopStateRef.current = true;
          setDocumentRouteSlug(null);
          setTaskRouteSlug(prettyTaskRoute.taskSlug);
          setSelectedTaskId(matchedTask.id);
          setCalendarTasksTab(true);
          // WHY: deep-linked task URLs now open inside the calendar's Tasks tab.
          navigateToView('calendar');
          return true;
        }
        return false;
      }

      // /calendar is the grid with no task open. Back from /calendar/tasks lands here,
      // so the tab bit and any selected task must be dropped or the writer
      // would put the address straight back.
      if (pathname === '/calendar' || pathname === '/calendar/') {
        isPopStateRef.current = true;
        setTaskRouteSlug(null);
        setSelectedTaskId(null);
        setCalendarTasksTab(false);
        navigateToView('calendar');
        return true;
      }

      const sharedDocumentRoute = parsePrettySharedPath(pathname);
      if (sharedDocumentRoute) {
        isPopStateRef.current = true;
        setShareToken(sharedDocumentRoute.token);
        return true;
      }

      // /workspaces/{slug}/edit — directory split with the named workspace
      // pre-selected. Stays on the workspaces view (not client-detail) so
      // the inline edit pane and section nav are what the user sees.
      const prettyEditRoute = parsePrettyWorkspaceEditPath(pathname);
      if (prettyEditRoute) {
        const matchedClient =
          DataService.getCompanies().find(
            (company: Company) =>
              company.type === 'client' &&
              slugifySegment(company.name) === prettyEditRoute.workspaceSlug
          ) || null;
        if (matchedClient) {
          isPopStateRef.current = true;
          setDocumentRouteSlug(null);
          setTaskRouteSlug(null);
          setSelectedClientId(matchedClient.id);
          setSelectedClientName(matchedClient.name);
          setClientDetailActiveTab('overview');
          setWorkspacesActiveTab('clients');
          navigateToView(window.matchMedia('(min-width: 1024px)').matches ? 'workspaces' : 'client-detail');
          return true;
        }
        // Intentionally fall through: buildWorkspacePathById emits a
        // /workspaces/{clientId} fallback when the company isn't in
        // DataService yet, and that UUID never matches a slug. The
        // historyState fallback below restores clientId, so don't trap
        // the retry on an impossible slug lookup.
      }

      const prettyClientRoute = parsePrettyWorkspacePath(pathname);
      if (prettyClientRoute) {
        // Projects already has a first-class global destination. The former
        // workspace summary duplicated it, so retain old bookmarks as a
        // compatibility redirect instead of reopening the removed tab.
        if (prettyClientRoute.activeTab === 'projects') {
          isPopStateRef.current = true;
          window.history.replaceState({ view: 'projects' }, document.title, buildViewPath('projects'));
          setSelectedClientId(null);
          setSelectedClientName(null);
          setClientDetailActiveTab('overview');
          navigateToView('projects');
          return true;
        }
        const matchedClient =
          DataService.getCompanies().find(
            (company: Company) =>
              company.type === 'client' &&
              slugifySegment(company.name) === prettyClientRoute.workspaceSlug
          ) || null;
        if (matchedClient) {
          isPopStateRef.current = true;
          setDocumentRouteSlug(null);
          setTaskRouteSlug(null);
          setSelectedClientId(matchedClient.id);
          setSelectedClientName(matchedClient.name);
          setClientDetailActiveTab(prettyClientRoute.activeTab);
          // Same surface as the legacy /edit route: the Workspaces split with the
          // section nav in the app sidebar. The legacy client-detail view
          // (its own inner nav panel) is no longer routed to.
          setWorkspacesActiveTab('clients');
          navigateToView(window.matchMedia('(min-width: 1024px)').matches ? 'workspaces' : 'client-detail');
          return true;
        }
        // See prettyEditRoute above — fall through to historyState
        // for the /workspaces/{clientId} fallback URL.
      }

      if (pathname === '/billing' || pathname === '/billing/') {
        isPopStateRef.current = true;
        // Platform billing moved into the Sales hub as its Subscriptions and
        // Platform Revenue sections, so /billing forwards there. Old links and
        // bookmarks keep working rather than 404ing.
        //
        // Two views have always shared this URL: the admin screen, and the
        // client-facing `billing` view labelled Membership. Sending everyone to
        // Sales would strand clients, who cannot see it — so the fork is by
        // role. That also fixes the existing behaviour, which sent clients to
        // the admin screen and showed them an access-denied message.
        navigateToView(isAdministratorRole(auth.user?.role) ? 'sales' : 'billing');
        return true;
      }

      // Every view whose open sub-route is part of its address parses here, out
      // of the same SUB_ROUTES declaration the URL builder writes from — so the
      // address this reads back is by construction the one that was written.
      // These were three hand-kept regexes, and they had already drifted: the
      // settings one accepted a nested section the route table then called
      // unknown, and carried an invisible character that made its trailing
      // slash mandatory. One declaration cannot disagree with itself.
      const personalAccountRoute = parsePersonalAccountPath(pathname);
      if (personalAccountRoute) {
        isPopStateRef.current = true;
        setProfileActiveTab(personalAccountRoute.section || 'overview');
        navigateToView('account');
        return true;
      }

      const workspaceFinanceRoute = parseWorkspaceFinancePath(pathname);
      if (workspaceFinanceRoute) {
        isPopStateRef.current = true;
        const { hub, section } = workspaceFinanceRoute;
        if (hub === 'payroll') setPayrollActiveTab(section);
        if (hub === 'accounting') setAccountingActiveTab(section);
        if (hub === 'tax') setTaxActiveTab(section);
        window.dispatchEvent(new CustomEvent(`${hub}:navigate-section`, {
          detail: { sectionId: section },
        }));
        navigateToView(hub);
        return true;
      }

      const subRoute = matchViewPath(pathname);
      if (subRoute && isAppView(subRoute.view)) {
        isPopStateRef.current = true;
        if (subRoute.view === 'sales') {
          // Set optimistically so the URL-writing effect does not rewrite the
          // address back to /sales in the gap before SalesView mounts. If the
          // section is one this role cannot see, SalesView falls back to the hub
          // and announces that, correcting both the state and the URL.
          const section = subRoute.slug || 'overview';
          setSalesActiveTab(section);
          // SalesView is lazily imported, so at boot it is not listening yet and
          // reads the address itself on mount. This is for when it IS mounted:
          // Back and Forward between two sections never remount it.
          window.dispatchEvent(
            new CustomEvent('sales:navigate-section', { detail: { sectionId: section } })
          );
        } else if (subRoute.view === 'purchases') {
          // Same shape as Sales above, for the same reason.
          const section = subRoute.slug || 'overview';
          setPurchasesActiveTab(section);
          window.dispatchEvent(
            new CustomEvent('purchases:navigate-section', { detail: { sectionId: section } })
          );
        } else if (subRoute.view === 'system-settings') {
          setSystemSettingsActiveTab(subRoute.slug || 'integrations');
        } else if (subRoute.view === 'preferences') {
          setPreferencesSection(subRoute.slug);
        } else if (subRoute.view === 'profile' || subRoute.view === 'account') {
          setProfileActiveTab(subRoute.slug || 'overview');
        } else if (subRoute.view === 'payroll') {
          const section = subRoute.slug || 'overview';
          setPayrollActiveTab(section);
          window.dispatchEvent(new CustomEvent('payroll:navigate-section', { detail: { sectionId: section } }));
        } else if (subRoute.view === 'accounting') {
          const section = subRoute.slug || 'bank-accounts';
          setAccountingActiveTab(section);
          window.dispatchEvent(new CustomEvent('accounting:navigate-section', { detail: { sectionId: section } }));
        } else if (subRoute.view === 'tax') {
          const section = subRoute.slug || 'vat-returns';
          setTaxActiveTab(section);
          window.dispatchEvent(new CustomEvent('tax:navigate-section', { detail: { sectionId: section } }));
        } else if (subRoute.view === 'messages') {
          const discussion = readDiscussionLink(pathname, search);
          if (discussion) {
            setMessagesChatSlug(null);
            setPendingChatSlug(null);
            handleOpenChatThread(discussion.chatId, discussion.messageId);
            navigateToView('messages');
            return true;
          }
          // The URL carries a name-derived slug, not the chat's uuid, so it
          // cannot be resolved to a real chat id here. messagesChatSlug is
          // set optimistically so the URL-writing effect below doesn't
          // rewrite the address before MessagesView mounts; pendingChatSlug
          // is the SEPARATE one-shot target MessagesView actually resolves
          // against its loaded chat list — see the App.tsx comment on why
          // these cannot be the same state.
          setMessagesChatSlug(subRoute.slug);
          setPendingChatSlug(subRoute.slug);
        } else if (subRoute.view === 'plant') {
          // Set optimistically, same reason as Messages above: PlantRegisterView
          // is lazily imported and not listening yet at parse time, so the
          // resolve effect (near employerRouteSlug) re-dispatches once it is.
          // A bare /plant clears any drilled-in machine left from before Back.
          setPlantRouteAssetId(subRoute.slug);
          if (!subRoute.slug) window.dispatchEvent(new CustomEvent(PLANT_CLEAR_SELECTION_EVENT));
        } else if (subRoute.view === 'projects') {
          if (pathname === '/projects' || pathname === '/projects/') {
            window.history.replaceState(historyState ?? null, '', `${buildViewPath('projects')}${search}`);
          }
        } else if (subRoute.view === 'outreach') {
          // Canonicalize accepted legacy bookmarks in place, preserving the
          // existing history entry and its Back/Forward behavior.
          const route = parseOutreachSlug(subRoute.slug);
          if (route?.section === 'templates') {
            setSystemSettingsActiveTab('templates/outreach');
            window.history.replaceState(historyState ?? null, '', `${buildViewPath('system-settings', 'templates/outreach')}${search}`);
            navigateToView('system-settings');
            return true;
          }
          if (pathname === '/outreach' || pathname.startsWith('/outreach/')) {
            window.history.replaceState(historyState ?? null, '', `${buildViewPath('outreach', buildOutreachSlug(route?.section, route?.recordId))}${search}`);
          }
          // OutreachView is lazily imported and not mounted yet at parse
          // time, so local state alone has no path to an already-mounted
          // view — every sibling hub also dispatches the bridge event. Without
          // it, drilling into /outreach/prospects and pressing Back moved the
          // address bar to /outreach while the content stayed on Prospects.
          // The record rides along so Back from /approvals/[id] lands on the
          // batch table rather than leaving the batch open.
          setOutreachActiveTab(route?.section ?? null);
          setOutreachRecordId(route?.recordId ?? null);
          window.dispatchEvent(new CustomEvent(OUTREACH_SET_SECTION_EVENT, {
            detail: { sectionId: route?.section ?? 'approvals', recordId: route?.recordId ?? null },
          }));
        }
        navigateToView(subRoute.view);
        return true;
      }

      const membershipMatch = pathname.match(/^\/membership(?:\/([^/]+))?\/*$/);
      if (membershipMatch) {
        if (!auth.isClient()) {
          return false;
        }

        const [, companySlug] = membershipMatch;
        const matchedCompany = companySlug
          ? resolveMembershipCompanyFromSlug(decodeURIComponent(companySlug))
          : null;
        const fallbackCompanyId = auth.user?.companyId || (auth.user as any)?.company_id || null;
        const fallbackCompany =
          fallbackCompanyId ? getCompany(fallbackCompanyId) : auth.company || null;

        const resolvedMembershipCompany =
          matchedCompany?.type === 'client'
            ? matchedCompany
            : fallbackCompany?.type === 'client'
              ? fallbackCompany
              : null;

        if (resolvedMembershipCompany) {
          isPopStateRef.current = true;
          setSelectedProjectId(null);
          setSelectedProjectName(null);
          setSelectedClientId(null);
          setSelectedClientName(null);
          setSelectedTaskId(null);
          setDocumentRouteSlug(null);
          setTaskRouteSlug(null);
          navigateToView('billing');
          return true;
        }
      }

      const projectMatch = pathname.match(/^\/projects\/([^/]+)\/?$/);
      if (projectMatch) {
        const projectId = decodeURIComponent(projectMatch[1]);
        const project = DataService.getProject(projectId);
        if (project) {
          isPopStateRef.current = true;
          const nextTab = queryTab || 'overview';
          setTakeoffDrawingRoute(null);
          setDocumentRouteSlug(nextTab === 'documents' ? queryItem : null);
          rememberDocumentDeepLink(nextTab === 'documents' ? parseDocumentDeepLink(search) : null);
          setEventsRouteSlug(null);
          setTaskRouteSlug(null);
          setSelectedProjectId(project.id);
          setSelectedProjectName(project.name);
          setProjectActiveTab(nextTab);
          setCurrentViewImmediate('project-detail');
          return true;
        }
        return false;
      }

      const prettyProjectMatch = pathname.match(/^\/projects\/([^/]+)\/([^/]+)\/?$/);
      if (prettyProjectMatch && !pathname.includes('/documents/')) {
        const [, workspaceSlug, projectSlug] = prettyProjectMatch;
        const matchedProject = resolveProjectFromSlugs(
          decodeURIComponent(workspaceSlug),
          decodeURIComponent(projectSlug)
        );
        if (matchedProject) {
          isPopStateRef.current = true;
          const nextTab = queryTab || 'overview';
          setTakeoffDrawingRoute(null);
          setDocumentRouteSlug(nextTab === 'documents' ? queryItem : null);
          rememberDocumentDeepLink(nextTab === 'documents' ? parseDocumentDeepLink(search) : null);
          setEventsRouteSlug(null);
          setTaskRouteSlug(null);
          setSelectedProjectId(matchedProject.id);
          setSelectedProjectName(matchedProject.name);
          setProjectActiveTab(nextTab);
          setCurrentViewImmediate('project-detail');
          return true;
        }
        return false;
      }

      // /projects/[client]/[project]/[tab] — e.g. /pricing, /documents, /tasks
      const prettyProjectTabRoute = parsePrettyProjectTabPath(pathname);
      if (prettyProjectTabRoute) {
        const matchedProject = resolveProjectFromSlugs(
          prettyProjectTabRoute.workspaceSlug,
          prettyProjectTabRoute.projectSlug
        );
        if (matchedProject) {
          isPopStateRef.current = true;
          const applicationRoute = parsePrettyProjectInterimPath(pathname);
          setInterimRoute(applicationRoute ? {
            projectId: matchedProject.id, applicationId: applicationRoute.applicationId,
            applicationNumber: applicationRoute.applicationNumber, section: applicationRoute.section,
          } : null);
          setTakeoffDrawingRoute(null);
          setDocumentRouteSlug(
            prettyProjectTabRoute.tab === 'documents' ? queryItem : null
          );
          rememberDocumentDeepLink(prettyProjectTabRoute.tab === 'documents' ? parseDocumentDeepLink(search) : null);
          setEventsRouteSlug(null);
          setTaskRouteSlug(null);
          setSelectedProjectId(matchedProject.id);
          setSelectedProjectName(matchedProject.name);
          setProjectActiveTab(prettyProjectTabRoute.tab);
          const noteRoute = parsePrettyProjectNotePath(pathname);
          setNotesPageRoute({ projectId: matchedProject.id, pageSlug: noteRoute?.pageSlug ?? null });
          window.dispatchEvent(new CustomEvent('project-notes:route-change', {
            detail: { projectId: matchedProject.id, pageSlug: noteRoute?.pageSlug ?? null },
          }));
          if (/\/(tasks|notes)\/?$/.test(pathname) && !pathname.includes('/notebook/')) {
            const section = prettyProjectTabRoute.tab === 'tasks' ? 'notebook/tasks' : 'notebook';
            window.history.replaceState(window.history.state, '', `${pathname.replace(/\/(tasks|notes)\/?$/, `/${section}`)}${search}`);
          }
          if (/^\/projects\/[^/]+\/[^/]+\/(events|claims|rfis)\/?$/.test(pathname)) {
            const section = prettyProjectTabRoute.tab === 'rfis' ? 'changes/rfis' : prettyProjectTabRoute.tab === 'claims' ? 'changes/claims' : 'changes';
            window.history.replaceState(window.history.state, '', `${pathname.replace(/\/(events|claims|rfis)\/?$/, `/${section}`)}${search}`);
          }
          if (/^\/projects\/[^/]+\/[^/]+\/activity\/?$/.test(pathname)) {
            window.history.replaceState(window.history.state, '', `${pathname.replace(/\/activity\/?$/, '/admin/activity')}${search}`);
          }
          if (/^\/projects\/[^/]+\/[^/]+\/interim(?:\/|$)/.test(pathname)) {
            window.history.replaceState(window.history.state, '', `${pathname.replace(/^(\/projects\/[^/]+\/[^/]+)\/interim(?=\/|$)/, '$1/valuations')}${search}`);
          }
          setCurrentViewImmediate('project-detail');
          return true;
        }
        return false;
      }

      // /{workspace}/resource-library — both the main entry and another
      // company's Workspace route carry the owning workspace in the URL.
      const resourceLibraryItemRoute = parsePrettyResourceLibraryItemPath(pathname);
      const resourceLibraryRoute = resourceLibraryItemRoute
        ? [pathname, resourceLibraryItemRoute.workspaceSlug]
        : pathname.match(/^\/([^/]+)\/resource-library\/?$/);
      if (resourceLibraryRoute) {
        const routeSlug = decodeURIComponent(resourceLibraryRoute[1]);
        const ownWorkspaceId = (auth.user as any)?.workspace_id ?? (auth.user as any)?.workspaceId ?? null;
        const ownWorkspaceNames = [
          auth.activeWorkspace?.name,
          auth.activeWorkspace?.companyName,
          auth.activeWorkspace?.slug,
          auth.company?.name,
        ].filter((name): name is string => !!name);
        const matchedCompany = DataService.getCompanies().find(
          (company: Company) => slugifySegment(company.name) === routeSlug,
        );
        const isOwnWorkspace = ownWorkspaceNames.some((name) => slugifySegment(name) === routeSlug);
        const workspaceId = matchedCompany?.workspaceId ?? (isOwnWorkspace ? ownWorkspaceId : null);
        const workspaceName = matchedCompany?.name
          ?? ownWorkspaceNames.find((name) => slugifySegment(name) === routeSlug)
          ?? routeSlug;
        if (workspaceId && isOwnWorkspace) {
          setResourceLibraryWorkspaceSlug(routeSlug);
          window.dispatchEvent(new CustomEvent<OpenWorkspaceResourceLibraryDetail>(
            OPEN_WORKSPACE_RESOURCE_LIBRARY_EVENT,
            { detail: { workspaceId, workspaceName } },
          ));
          isPopStateRef.current = true;
          navigateToView('resource-library');
          return true;
        }
        if (workspaceId && matchedCompany) {
          isPopStateRef.current = true;
          setSelectedClientId(matchedCompany.id);
          setSelectedClientName(matchedCompany.name);
          setClientDetailActiveTab('resource-library');
          setWorkspacesActiveTab('clients');
          navigateToView('workspaces');
          return true;
        }
      }

      // Legacy bookmark or a focused rate-item editor.
      if (pathname === '/workspace/resource-library' || pathname === '/workspace/resource-library/' ||
          pathname === '/resource-library' || pathname === '/resource-library/' ||
          pathname.match(/^\/resource-library\/[^/]+\/items\/(new|edit)\/?$/)) {
        isPopStateRef.current = true;
        navigateToView('resource-library');
        return true;
      }

      // A bare /{view} address: /payroll, /messages, /email and most of the
      // rest. Every view is addressable that way — routeTable derives its whole
      // known-route set from APP_VIEWS, and the URL writer emits `/${view}` for
      // anything without a prettier form — but only views with their own branch
      // above ever RESOLVED one. The rest fell through to historyState and then
      // to whatever localStorage last held, so opening /payroll quietly
      // showed the previous view instead. The route table said the address was
      // real, nothing 404'd, and the app simply went somewhere else.
      //
      // Last, deliberately: every richer branch above claims its own path first,
      // so /sales and /purchases keep their section restore and /billing keeps
      // its role fork.
      const bareView = pathname.replace(/\/+$/, '').slice(1);
      if (bareView && isAppView(bareView)) {
        isPopStateRef.current = true;
        navigateToView(bareView);
        return true;
      }

      if (historyState) {
        isPopStateRef.current = true;
        const historyView = historyState.view;
        const historyProjectId = historyState.projectId ?? null;
        const historyProjectName = historyState.projectName ?? null;
        if (historyView === 'project-detail' && !historyProjectId) {
          navigateToView('projects');
          setSelectedProjectId(null);
          setSelectedProjectName(null);
        } else if (historyView && isAppView(historyView)) {
          navigateToView(historyView);
          if (historyState.projectId !== undefined) setSelectedProjectId(historyProjectId);
          if (historyState.projectName !== undefined) setSelectedProjectName(historyProjectName);
        }
        setDocumentRouteSlug(historyState.documentRouteSlug ?? null);
        setEventsRouteSlug(historyState.eventsRouteSlug ?? null);
        setNotesPageRoute({ projectId: historyProjectId, pageSlug: historyState.notesPageSlug ?? null });
        if (historyProjectId) window.dispatchEvent(new CustomEvent('project-notes:route-change', {
          detail: { projectId: historyProjectId, pageSlug: historyState.notesPageSlug ?? null },
        }));
        setTakeoffDrawingRoute(
          historyState.takeoffSheetName
            ? {
                sheetId: historyState.takeoffSheetId ?? null,
                sheetName: historyState.takeoffSheetName,
              }
            : null
        );
        setSelectedTaskId(historyState.taskId ?? null);
        setTaskRouteSlug(historyState.taskRouteSlug ?? null);
        setSelectedClientId(historyState.clientId ?? null);
        setSelectedClientName(historyState.clientName ?? null);
        if (historyState.projectActiveTab !== undefined) setProjectActiveTab(historyState.projectActiveTab);
        return true;
      }

      return false;
    };

    if (!didApplyInitialLocationRef.current) {
      didApplyInitialLocationRef.current = true;

      // On native WKWebView, the page may reload mid-navigation (the native shell
      // loads a new URL as part of top-level tab switching). In that case
      // window.history.state still reflects the PRE-navigation view (e.g. dashboard)
      // and would override the correct currentView already written to localStorage
      // by the interrupted navigation. Skip historyState on native so localStorage
      // remains the source of truth across reloads.
      const historyStateToRestore = NativeBridge.isRunningNatively()
        ? null
        : window.history.state;

      const didResolve = applyLocationState(
        window.location.pathname,
        window.location.search,
        historyStateToRestore
      );

      // If the native shell reloaded the page while an appNavigate was in-flight,
      // the sessionStorage entry written by forwardAppNavigate survives the reload
      // and lets us resume the intended navigation here — after auth is ready and
      // AFTER applyLocationState so we always win over any stale historyState.
      try {
        const raw = sessionStorage.getItem('quantis:pending-appnavigate');
        if (raw) {
          sessionStorage.removeItem('quantis:pending-appnavigate');
          const { detail, ts } = JSON.parse(raw) as { detail: Record<string, any>; ts: number };
          if (Date.now() - ts < 8000 && detail?.view && isAppView(detail.view)) {
            isPopStateRef.current = false;
            navigateToView(detail.view);
          }
        }
      } catch (_) {
        // sessionStorage may be unavailable in some contexts
      }

      pendingLocationRestoreRef.current = didResolve
        ? null
        : {
            pathname: window.location.pathname,
            search: window.location.search,
            historyState: window.history.state,
            viewWhenParked: currentViewRef.current,
            projectIdWhenParked: selectedProjectIdRef.current,
          };
    }

    const handlePopState = (event: PopStateEvent) => {
      const didResolve = applyLocationState(
        window.location.pathname,
        window.location.search,
        event.state
      );

      pendingLocationRestoreRef.current = didResolve
        ? null
        : {
            pathname: window.location.pathname,
            search: window.location.search,
            historyState: event.state,
            viewWhenParked: currentViewRef.current,
            projectIdWhenParked: selectedProjectIdRef.current,
          };
    };

    const unsubscribe = DataService.subscribe(() => {
      const pendingLocation = pendingLocationRestoreRef.current;
      if (!pendingLocation) return;

      const didResolve = applyLocationState(
        pendingLocation.pathname,
        pendingLocation.search,
        pendingLocation.historyState
      );

      if (didResolve) {
        pendingLocationRestoreRef.current = null;
      }
    });

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      unsubscribe();
    };
  // Intentionally run once on mount. Replaying initial location parsing on every
  // state change causes deep-link project routes to trap the app in project detail.
  }, []);

  useEffect(() => {
    // Consumed before any early return so a hint never outlives this run.
    const outreachReplace = outreachReplaceRef.current;
    outreachReplaceRef.current = false;
    if (isPopStateRef.current) {
      isPopStateRef.current = false;
      return;
    }

    // The address is the source of truth for which entity is open. A pretty
    // project path resolves its slugs through DataService, which on a cold boot
    // has not loaded yet — applyLocationState parks the location in
    // pendingLocationRestoreRef and retries on the DataService subscription.
    // Until that retry lands, app state is NOT yet this location, so writing the
    // URL from it here would overwrite the deep link with wherever the previous
    // session left off. That is how a project route could end up showing one
    // project's name over another project's id.
    const pendingLocation = pendingLocationRestoreRef.current;
    if (pendingLocation) {
      const movedDeliberately =
        pendingLocation.viewWhenParked !== currentView ||
        pendingLocation.projectIdWhenParked !== selectedProjectId;
      if (!movedDeliberately) return;
      // The user went somewhere else rather than wait, so the parked address is
      // no longer what they want. Drop it and let this run own the URL.
      pendingLocationRestoreRef.current = null;
    }

    // When running inside a WKWebView (iOS or macOS native shell), always use
    // replaceState instead of pushState. pushState with a URL change causes
    // WKWebView to fire its navigation delegate, spawning a new WebContent
    // process and reloading the page — which traps users on Dashboard because
    // the fresh load reinitializes to the default view. replaceState updates
    // the URL in-place without triggering a WKWebView navigation. The native
    // shell manages its own navigation stack via routeState, so browser history
    // entries are not needed.
    const useReplaceStateForNative = NativeBridge.isRunningNatively();

    const isProjectDetail = currentView === 'project-detail';
    // WHY: tasks now live inside the calendar view; treat calendar + selectedTaskId
    // the same way as the old tasks-view for URL/history-state purposes. Gated on
    // calendarTasksTab (the Tasks panel's own open/closed flag) too, not just
    // selectedTaskId — closing the panel (its X, or the header pill toggle-close)
    // never clears selectedTaskId, so without this gate the address stays pinned
    // at /calendar/tasks/[task] after a deep link even once the panel is gone.
    const isCalendarTasksTab = currentView === 'calendar' && calendarTasksTab;
    const isCalendarWithTask = isCalendarTasksTab && !!selectedTaskId;
    const isClientDetail = currentView === 'client-detail';
    const state = {
      view: currentView,
      projectId: isProjectDetail ? selectedProjectId : null,
      projectName: isProjectDetail ? selectedProjectName : null,
      projectActiveTab: isProjectDetail ? (projectActiveTab || 'overview') : null,
      interimApplicationId: isProjectDetail && projectActiveTab === 'interim' ? interimRoute?.applicationId ?? null : null,
      interimApplicationNumber: isProjectDetail && projectActiveTab === 'interim' ? interimRoute?.applicationNumber ?? null : null,
      interimSection: isProjectDetail && projectActiveTab === 'interim' ? interimRoute?.section ?? null : null,
      documentRouteSlug: isProjectDetail ? documentRouteSlug : null,
      eventsRouteSlug: isProjectDetail ? eventsRouteSlug : null,
      notesPageSlug: isProjectDetail && (projectActiveTab === 'notes' || projectActiveTab === 'tasks') && notesPageRoute.projectId === selectedProjectId
        ? notesPageRoute.pageSlug : null,
      takeoffSheetId: isProjectDetail && projectActiveTab === 'takeoff'
        ? takeoffDrawingRoute?.sheetId ?? null
        : null,
      takeoffSheetName: isProjectDetail && projectActiveTab === 'takeoff'
        ? takeoffDrawingRoute?.sheetName ?? null
        : null,
      taskId: isCalendarWithTask ? selectedTaskId : null,
      taskRouteSlug: isCalendarWithTask ? taskRouteSlug : null,
      calendarTasksTab: isCalendarTasksTab ? true : null,
      calendarProjectScope: currentView === 'calendar' ? calendarProjectScope : null,
      clientId: isClientDetail ? selectedClientId : null,
      clientName: isClientDetail ? selectedClientName : null,
      systemSettingsActiveTab: currentView === 'system-settings' ? systemSettingsActiveTab : null,
      preferencesSection: currentView === 'preferences' ? preferencesSection : null,
      profileActiveTab: currentView === 'profile' || currentView === 'account' ? profileActiveTab : null,
      // The Sales hub's open section is part of the address, so it has to be
      // part of the state this effect compares. Without it isSameState stays
      // true when only the section changes and the URL never moves — the same
      // trap the employers keys below were added to escape.
      salesActiveTab: currentView === 'sales' ? salesActiveTab : null,
      // Purchases made the same move as Sales — see above.
      purchasesActiveTab: currentView === 'purchases' ? purchasesActiveTab : null,
      payrollActiveTab: currentView === 'payroll' ? payrollActiveTab : null,
      accountingActiveTab: currentView === 'accounting' ? accountingActiveTab : null,
      taxActiveTab: currentView === 'tax' ? taxActiveTab : null,
      // Same trap as Sales/Purchases above: without this in the compared
      // state, opening or switching a chat never moves the URL.
      messagesActiveChatId: currentView === 'messages' ? messagesChatSlug : null,
      // Employers is one view with several URLs (/employers, /employers/add,
      // /employers/[slug] and /employers/[slug]/[section]). Without these in the
      // state, drilling in or changing page alters nothing this effect compares,
      // so isSameState stays true and the URL never moves.
      employersTab: currentView === 'employers' ? mainContractorInitialTab : null,
      employerId: currentView === 'employers' ? employersListState?.selectedId ?? null : null,
      employerSection: currentView === 'employers' ? employersListState?.activeSection ?? null : null,
      // Plant register: /plant vs /plant/[id]. Same trap as Employers above —
      // without this key, drilling into a machine changes nothing isSameState
      // compares and the URL sits on /plant regardless of what is open.
      plantAssetId: currentView === 'plant' ? plantListState?.selectedId ?? null : null,
      // Outreach's open section, same trap as Sales/Purchases above: without
      // this key, switching section changes nothing isSameState compares and
      // the URL never moves off /outreach.
      outreachSection: currentView === 'outreach' ? outreachActiveTab ?? null : null,
      // The drilled-in batch or prospect. Same trap again: without it,
      // opening a record moves nothing isSameState compares.
      outreachRecordId: currentView === 'outreach' ? outreachRecordId : null,
      resourceLibraryWorkspaceSlug: currentView === 'resource-library'
        ? resourceLibraryWorkspaceSlug
        : null,
      workspaceDetailTab: currentView === 'workspaces'
        ? workspaceDetail?.activeTab ?? clientDetailActiveTab
        : currentView === 'client-detail'
          ? clientDetailActiveTab
          : null,
      workspaceDetailClientId: currentView === 'workspaces'
        ? workspaceDetail?.clientId ?? null
        : null,
    };

    let url = currentView === 'dashboard' ? '/' : `/${currentView}`;
    if (currentView === 'finance-billing') url = '/billing';
    if (currentView === 'resource-library') {
      url = `/${resourceLibraryWorkspaceSlug || slugifySegment(accountWorkspaceName)}/resource-library`;
      if (parsePrettyResourceLibraryItemPath(window.location.pathname)) {
        // ResourceLibraryManagement resolves the library/item ids after its
        // permission-scoped catalogues hydrate. Do not collapse a cold deep
        // link back to the library root while that happens.
        url = window.location.pathname;
      }
    }
    if (currentView === 'contacts' && (parsePrettyContactPath(window.location.pathname) || parseCompanyPath(window.location.pathname))) {
      // CompaniesView owns the open company or person. Preserve its route until
      // that screen closes the record or browser history returns to the list.
      url = window.location.pathname;
    }
    // Back from a record: the address still names it, and preserving it would reopen the record.
    const leftRecordAddress = currentView === 'contacts' && isLeftCompanyAddress();
    if (leftRecordAddress) url = '/contacts';
    // Views whose open sub-route belongs in the address get it from SUB_ROUTES,
    // which is also what the boot parser and the route table read — so the three
    // halves of the grammar cannot drift apart the way the hand-written ones did.
    // buildViewPath leaves a view's DEFAULT section off where the spec names
    // one (profile, sales...). System Settings and Preferences name none, so
    // their open section is always written out.
    if (currentView === 'account') {
      url = buildCurrentAccountPath(profileActiveTab);
    } else if (currentView === 'payroll') {
      url = buildCurrentFinancePath('payroll', payrollActiveTab);
    } else if (currentView === 'accounting') {
      url = buildCurrentFinancePath('accounting', accountingActiveTab);
    } else if (currentView === 'tax') {
      url = buildCurrentFinancePath('tax', taxActiveTab);
    } else if (SUB_ROUTES[currentView]) {
      const openSubRoute =
        currentView === 'sales' ? salesActiveTab
        : currentView === 'purchases' ? purchasesActiveTab
        : currentView === 'system-settings' ? systemSettingsActiveTab
        : currentView === 'preferences' ? preferencesSection
        : currentView === 'profile' ? profileActiveTab
        : currentView === 'messages' ? messagesChatSlug
        : currentView === 'plant' ? plantListState?.selectedId ?? null
        : currentView === 'outreach' ? buildOutreachSlug(outreachActiveTab, outreachRecordId)
        : null;
      url = buildViewPath(currentView, openSubRoute);
    }
    if (currentView === 'project-detail' && selectedProjectId) {
      const selectedProject = DataService.getProject(selectedProjectId);
      const client = selectedProject ? getCompany(selectedProject.clientId) : null;
      const openTeamMemberRoute = projectActiveTab === 'team'
        ? parsePrettyProjectTeamMemberPath(window.location.pathname)
        : null;
      const openPricingRoute = projectActiveTab === 'pricing'
        ? parsePrettyProjectPricingPath(window.location.pathname)
        : null;
      const legacyPricingView = projectActiveTab === 'pricing'
        ? new URLSearchParams(window.location.search).get('view')
        : null;
      if (selectedProject && client) {
        url = projectActiveTab === 'interim' && interimRoute && (!interimRoute.projectId || interimRoute.projectId === selectedProjectId)
          ? interimRoute.applicationNumber
            ? buildPrettyProjectInterimPath({
                clientName: client.name, projectName: selectedProject.name,
                applicationNumber: interimRoute.applicationNumber, section: interimRoute.section,
              })
            // Keep an older ID-only link intact until its project record hydrates.
            : window.location.pathname
          : projectActiveTab === 'notes' || projectActiveTab === 'tasks'
          ? buildPrettyProjectNotePath({
              clientName: client.name, projectName: selectedProject.name,
              pageSlug: state.notesPageSlug, tasksOpen: projectActiveTab === 'tasks',
            })
          : projectActiveTab === 'takeoff' && takeoffDrawingRoute
          ? `${buildPrettyProjectTakeoffPath({
              clientName: client.name,
              projectName: selectedProject.name,
              sheetName: takeoffDrawingRoute.sheetName,
            })}${takeoffDrawingRoute.sheetId ? `?sheet=${encodeURIComponent(takeoffDrawingRoute.sheetId)}` : ''}`
          : documentRouteSlug
          ? buildPrettyProjectDocumentPath({
              clientName: client.name,
              projectName: selectedProject.name,
              documentName: documentRouteSlug,
            })
          : projectActiveTab === 'events' && eventsRouteSlug
          ? buildPrettyProjectEventsPath({
              clientName: client.name,
              projectName: selectedProject.name,
              eventsRoute: eventsRouteSlug,
            })
          : openTeamMemberRoute
          ? buildPrettyProjectTeamMemberPath({
              clientName: client.name,
              projectName: selectedProject.name,
              memberName: openTeamMemberRoute.memberSlug,
            })
          : openPricingRoute
          ? buildPrettyProjectPricingPath({
              clientName: client.name,
              projectName: selectedProject.name,
              billName: openPricingRoute.billSlug,
              page: openPricingRoute.page,
            })
          : legacyPricingView
          ? `${buildPrettyProjectTabPath({
              clientName: client.name,
              projectName: selectedProject.name,
              tab: 'pricing',
            })}?view=${encodeURIComponent(legacyPricingView)}`
          : buildPrettyProjectTabPath({
              clientName: client.name,
              projectName: selectedProject.name,
              tab: projectActiveTab || 'overview',
            });
      } else {
        url = buildProjectPathById(selectedProjectId);
      }
    } else if (currentView === 'project-detail') {
      url = '/projects';
    } else if (currentView === 'billing') {
      const membershipCompanyName =
        auth.activeWorkspace?.companyName ||
        auth.company?.name ||
        (() => {
          const companyId = auth.user?.companyId || (auth.user as any)?.company_id;
          return companyId ? getCompany(companyId)?.name : null;
        })();
      url = membershipCompanyName
        ? `/membership/${slugifySegment(membershipCompanyName)}`
        : '/membership';
    } else if (isCalendarWithTask && selectedTaskId) {
      url = buildTaskPathById(selectedTaskId);
    } else if (isCalendarTasksTab) {
      url = CALENDAR_TASKS_PATH;
    } else if (currentView === 'workspaces' && workspaceDetail) {
      url = buildWorkspacePathById(workspaceDetail.clientId, {
        activeTab: workspaceDetail.activeTab,
      });
    } else if ((currentView === 'client-detail' || currentView === 'workspaces') && selectedClientId) {
      // selectedClientId is set synchronously before the view transition completes,
      // so this handles both the settled state and the in-flight transition period.
      url = buildWorkspacePathById(selectedClientId, {
        activeTab: currentView === 'workspaces'
          ? workspaceDetail?.activeTab ?? clientDetailActiveTab
          : clientDetailActiveTab,
      });
    } else if (currentView === 'workspaces') {
      // 'add-client' tab is now its own page at /workspaces/add-workspace
      // (handled by the 'add-workspace' branch below). Keep the query
      // param only for the 'requests' sub-tab — the rest reads cleanest
      // as the bare list URL.
      const params =
        workspacesActiveTab === 'requests'
          ? `?tab=${encodeURIComponent(workspacesActiveTab)}`
          : '';
      url = `/workspaces${params}`;
    } else if (currentView === 'add-workspace') {
      url = '/workspaces/add-workspace';
    } else if (currentView === 'employers') {
      if (mainContractorInitialTab === 'add-contractor') {
        url = '/employers/add';
      } else if (employersListState?.selectedId) {
        // A drilled-in profile gets its own address, and the section names the
        // page. Falls back to the id when the employer isn't resolvable to a
        // name — same shape as buildWorkspacePathById.
        url = buildEmployerPathById(employersListState.selectedId, {
          section: employersListState.activeSection ?? undefined,
        });
      } else if (employerRouteSlug) {
        // Still resolving: hold the address the record will settle on rather than flashing /employers.
        url = buildEmployerPathById(employerRouteSlug, { section: employerRouteSection ?? undefined });
      } else {
        url = '/employers';
      }
    }

    // A client opening from Companies lives at /contacts/<slug> throughout. Before the Workspaces screen
    // has announced its detail the computed address is the bare directory; writing it would add an entry
    // and flash /workspaces, so keep the record address already in the bar.
    if (currentView === 'workspaces' && url === '/workspaces' && parseCompanyPath(window.location.pathname)) {
      url = window.location.pathname;
    }

    if (currentView === 'calendar') url = withProjectCalendarScope(url, calendarProjectScope);

    // A full-size Site Photo keeps its stable record ID while the canonical
    // project path is repaired after reload or a project rename.
    if (currentView === 'project-detail' && projectActiveTab === 'site_photos' && selectedProjectId) {
      const currentPhotoRoute = parsePrettyProjectTabPath(window.location.pathname);
      const canonicalPhotoRoute = parsePrettyProjectTabPath(new URL(url, window.location.origin).pathname);
      const params = new URLSearchParams(window.location.search);
      const belongsToCurrentProject = currentPhotoRoute?.tab === 'site_photos'
        && (window.history.state?.projectId === selectedProjectId
          || (currentPhotoRoute.workspaceSlug === canonicalPhotoRoute?.workspaceSlug
            && currentPhotoRoute.projectSlug === canonicalPhotoRoute?.projectSlug));
      if (belongsToCurrentProject && params.get('view') === 'full' && params.get('photo')) {
        url += `?${new URLSearchParams({ photo: params.get('photo')!, view: 'full' }).toString()}`;
      }
    }

    // Keep a channel huddle invitation until Messages resolves its access-
    // scoped chat list and consumes the link into the prejoin screen.
    if (currentView === 'messages') {
      const huddleId = new URLSearchParams(window.location.search).get('huddle');
      if (huddleId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(huddleId)) {
        url += `?huddle=${encodeURIComponent(huddleId)}`;
      }
    }

    url = keepOpenDocumentFolder(url, window.location.pathname);

    const currentState = window.history.state;
    const isSameState =
      currentState &&
      currentState.view === currentView &&
      currentState.projectId === state.projectId &&
      currentState.projectActiveTab === state.projectActiveTab &&
      ((currentState.interimApplicationNumber ?? null) === state.interimApplicationNumber ||
        (state.interimApplicationId !== null && currentState.interimApplicationId === state.interimApplicationId)) &&
      (currentState.interimSection ?? null) === state.interimSection &&
      currentState.documentRouteSlug === state.documentRouteSlug &&
      currentState.eventsRouteSlug === state.eventsRouteSlug &&
      (currentState.notesPageSlug ?? null) === state.notesPageSlug &&
      (currentState.takeoffSheetId ?? null) === state.takeoffSheetId &&
      (currentState.takeoffSheetName ?? null) === state.takeoffSheetName &&
      currentState.taskId === state.taskId &&
      currentState.taskRouteSlug === state.taskRouteSlug &&
      (currentState.calendarTasksTab ?? null) === state.calendarTasksTab &&
      (currentState.calendarProjectScope ?? null) === state.calendarProjectScope &&
      currentState.clientId === state.clientId &&
      currentState.systemSettingsActiveTab === state.systemSettingsActiveTab &&
      (currentState.preferencesSection ?? null) === state.preferencesSection &&
      currentState.profileActiveTab === state.profileActiveTab &&
      (currentState.salesActiveTab ?? null) === state.salesActiveTab &&
      (currentState.purchasesActiveTab ?? null) === state.purchasesActiveTab &&
      (currentState.payrollActiveTab ?? null) === state.payrollActiveTab &&
      (currentState.accountingActiveTab ?? null) === state.accountingActiveTab &&
      (currentState.taxActiveTab ?? null) === state.taxActiveTab &&
      (currentState.messagesActiveChatId ?? null) === state.messagesActiveChatId &&
      (currentState.employersTab ?? null) === state.employersTab &&
      (currentState.employerId ?? null) === state.employerId &&
      (currentState.employerSection ?? null) === state.employerSection &&
      (currentState.plantAssetId ?? null) === state.plantAssetId &&
      (currentState.outreachSection ?? null) === state.outreachSection &&
      (currentState.outreachRecordId ?? null) === state.outreachRecordId &&
      (currentState.resourceLibraryWorkspaceSlug ?? null) === state.resourceLibraryWorkspaceSlug &&
      (currentState.workspaceDetailTab ?? null) === state.workspaceDetailTab &&
      (currentState.workspaceDetailClientId ?? null) === state.workspaceDetailClientId;

    // The state object identifies the entity by id, but these URLs are built from
    // its NAME, so renaming leaves the address bar on a slug that no longer
    // resolves — share or reload it and the lookup misses. Comparing the computed
    // url as well catches that. Applies equally to workspaces and projects, whose
    // paths are name-slugged against an id-only state comparison in the same way.
    const urlIsStale = `${window.location.pathname}${window.location.search}` !== url;

    if (!isSameState || urlIsStale) {
      // Sync document.title BEFORE the history update so the WKWebView navigation
      // delegate reads the correct page title when it auto-generates a routeState
      // message (not the stale "Quantis | Contract Group" from NotificationsPanel).
      document.title = pageTitle ? `${pageTitle} | Quantis` : 'Quantis';
      // `isSameState && urlIsStale` is a rename, not navigation — the user did not
      // move anywhere, the entity's name did, so correct the address in place
      // rather than pushing an entry whose Back goes nowhere meaningful.
      const writeKind = decideHistoryWrite({
        currentUrl: `${window.location.pathname}${window.location.search}`,
        nextUrl: url,
        replaceRequested: useReplaceStateForNative || isSameState || leftRecordAddress || outreachReplace
          || isPushedClientEntry(currentState, currentView, selectedClientId, urlIsStale)
          || isPreferencesSettle(currentState, currentView, state.preferencesSection ?? null, window.matchMedia('(min-width: 1024px)').matches, window.location.pathname),
      });
      if (writeKind === 'replace') {
        // replaceState updates the URL in-place — WKWebView does not fire its
        // navigation delegate for replaceState, so no new WebContent process is
        // spawned. Native still receives the correct href via reportRouteState.
        window.history.replaceState(state, '', url);
      } else {
        window.history.pushState(state, '', url);
      }
    }
  }, [
    currentView,
    interimRoute,
    notesPageRoute,
    documentRouteSlug,
    eventsRouteSlug,
    takeoffDrawingRoute,
    auth.activeWorkspace?.companyName,
    auth.company?.name,
    auth.user,
    buildCurrentAccountPath,
    buildCurrentFinancePath,
    getCompany,
    isPopStateRef,
    pageTitle,
    projectActiveTab,
    selectedClientId,
    selectedClientName,
    selectedProjectId,
    selectedProjectName,
    selectedTaskId,
    calendarTasksTab,
    calendarProjectScope,
    taskRouteSlug,
    systemSettingsActiveTab,
    preferencesSection,
    profileActiveTab,
    salesActiveTab,
    purchasesActiveTab,
    payrollActiveTab,
    accountingActiveTab,
    taxActiveTab,
    messagesChatSlug,
    workspacesActiveTab,
    mainContractorInitialTab,
    // Carries the drilled-in employer and its page. Without it the URL never
    // moves off /employers, which is the whole point of the branch above.
    employersListState,
    employerRouteSlug,
    employerRouteSection,
    // Same trap, same fix, for the drilled-in machine on /plant.
    plantListState,
    outreachActiveTab,
    outreachRecordId,
    resourceLibraryWorkspaceSlug,
    accountWorkspaceName,
    workspaceDetail,
    clientDetailActiveTab,
  ]);

  useEffect(() => {
    document.title = pageTitle ? `${pageTitle} | Quantis` : 'Quantis';
  }, [pageTitle]);

  useEffect(() => {
    const recalcStats = () => {
      const allCompanies = DataService.getCompanies();
      const clients = allCompanies.filter((company: Company) => company.type === 'client');
      const clientRequests = DataService.getClientRequests();

      setWorkspacesStats({
        total: clients.length,
        active: clients.filter((client: Company) => client.status === 'active').length,
        invited: clients.filter((client: Company) => client.status === 'invited').length,
        pending: clients.filter((client: Company) => client.status === 'pending_invitation').length,
        pendingRequests: clientRequests.filter((request: ClientRequest) => request.status === 'pending').length,
      });
    };

    recalcStats();
    const unsubscribe = DataService.subscribe(recalcStats);
    return unsubscribe;
  }, [setWorkspacesStats]);

  // Listen for finance-hub section changes on every platform. Native needs the
  // selected row identifier; web additionally writes the section into the URL.
  useEffect(() => {
    const onPayrollNav = (e: Event) => {
      const sectionId = (e as CustomEvent<{ sectionId: string }>).detail?.sectionId;
      if (sectionId) setPayrollActiveTab(sectionId);
    };
    const onAccountingNav = (e: Event) => {
      const sectionId = (e as CustomEvent<{ sectionId: string }>).detail?.sectionId;
      if (sectionId) setAccountingActiveTab(sectionId);
    };
    const onTaxNav = (e: Event) => {
      const sectionId = (e as CustomEvent<{ sectionId: string }>).detail?.sectionId;
      if (sectionId) setTaxActiveTab(sectionId);
    };

    window.addEventListener('payroll:section-change', onPayrollNav);
    window.addEventListener('accounting:section-change', onAccountingNav);
    window.addEventListener('tax:section-change', onTaxNav);

    return () => {
      window.removeEventListener('payroll:section-change', onPayrollNav);
      window.removeEventListener('accounting:section-change', onAccountingNav);
      window.removeEventListener('tax:section-change', onTaxNav);
    };
  }, []);

  // Sales and Purchases use the same event-to-address contract as the finance
  // hubs above, but keep their existing dedicated listeners and permissions.
  useEffect(() => {
    const onSalesNav = (e: Event) => {
      const { sectionId, initial } = (e as CustomEvent<{ sectionId: string; initial?: boolean }>).detail ?? {};
      if (!sectionId) return;
      // SalesView reads the address on mount, but the address is stale on the
      // one path that does not go through it: re-entering Sales from another
      // view, where the screen mounts before the URL has moved. So when its
      // MOUNT announcement disagrees with the section we still remember, answer
      // with ours instead of recording theirs — otherwise coming back to Sales
      // silently drops you on Overview, which is not what the two other views
      // on this registry do. Only the mount is second-guessed; every later
      // announcement is the user actually choosing a section.
      if (initial && salesActiveTabRef.current && salesActiveTabRef.current !== sectionId) {
        const restoreTo = salesActiveTabRef.current;
        // Deferred by a microtask because this runs INSIDE SalesView's mount
        // effect, and the effect that subscribes it to sales:navigate-section
        // has not run yet — a synchronous reply lands with nothing listening,
        // leaving the address on one section and the screen on another. React
        // flushes the whole effect list before microtasks, so by then it is up.
        queueMicrotask(() => {
          window.dispatchEvent(new CustomEvent('sales:navigate-section', {
            detail: { sectionId: restoreTo },
          }));
        });
        return;
      }
      setSalesActiveTab(sectionId);
    };
    window.addEventListener('sales:section-change', onSalesNav);
    return () => window.removeEventListener('sales:section-change', onSalesNav);
  }, []);

  // Purchases, same shape as Sales above — see its comments for why the
  // mount-only restore exists and why the microtask defer matters.
  useEffect(() => {
    const onPurchasesNav = (e: Event) => {
      const { sectionId, initial } = (e as CustomEvent<{ sectionId: string; initial?: boolean }>).detail ?? {};
      if (!sectionId) return;
      if (initial && purchasesActiveTabRef.current && purchasesActiveTabRef.current !== sectionId) {
        const restoreTo = purchasesActiveTabRef.current;
        queueMicrotask(() => {
          window.dispatchEvent(new CustomEvent('purchases:navigate-section', {
            detail: { sectionId: restoreTo },
          }));
        });
        return;
      }
      setPurchasesActiveTab(sectionId);
    };
    window.addEventListener('purchases:section-change', onPurchasesNav);
    return () => window.removeEventListener('purchases:section-change', onPurchasesNav);
  }, []);

  // Pipeline sections can open Outreach before its lazy chunk is mounted.
  // Retain the section in controller state instead of relying on a missed SET.
  useEffect(() => {
    const onOpenOutreach = (event: Event) => {
      const sectionId = (event as CustomEvent<{ sectionId?: string }>).detail?.sectionId;
      if (!isInternalRole(auth.user?.role) || !isOutreachSectionId(sectionId)) return;
      if (sectionId === 'templates') { setSystemSettingsActiveTab('templates/outreach'); navigateToView('system-settings'); return; }
      setOutreachActiveTab(sectionId);
      setOutreachRecordId(null);
      navigateToView('outreach');
    };
    window.addEventListener(OUTREACH_OPEN_SECTION_EVENT, onOpenOutreach);
    return () => window.removeEventListener(OUTREACH_OPEN_SECTION_EVENT, onOpenOutreach);
  }, [auth.user?.role, navigateToView, setOutreachActiveTab]);

  // Outreach closes the same loop as Sales/Purchases above, without their
  // mount-mismatch restore dance (OutreachView carries no `initial` flag on
  // its broadcast). Without this, the sidebar drill-in (Task 12b) is now the
  // primary desktop nav for this hub, but every click left the address on
  // whatever section had been open before it — outreachSection and its
  // isSameState comparison had nothing feeding them. detail is null on
  // OutreachView's unmount, which correctly clears the sub-route too.
  useEffect(() => {
    const onOutreachNav = (e: Event) => {
      const detail = (e as CustomEvent<{ activeSection?: string; recordId?: string | null; replace?: boolean } | null>).detail;
      if (detail?.activeSection === 'templates') {
        setSystemSettingsActiveTab('templates/outreach');
        navigateToView('system-settings');
        return;
      }
      if (detail?.replace) outreachReplaceRef.current = true;
      setOutreachActiveTab(detail?.activeSection ?? null);
      setOutreachRecordId(detail?.recordId ?? null);
    };
    window.addEventListener(OUTREACH_STATE_EVENT, onOutreachNav);
    return () => window.removeEventListener(OUTREACH_STATE_EVENT, onOutreachNav);
  }, [navigateToView]);

  // MessagesView announces its open chat the same way the hubs above announce
  // their section, so the URL-writing effect below can put it in the address.
  // chatSlug is the name-derived pretty slug — chatId itself never reaches
  // the URL.
  useEffect(() => {
    const onMessagesChatChange = (e: Event) => {
      const chatSlug = (e as CustomEvent<{ chatSlug: string | null }>).detail?.chatSlug ?? null;
      setMessagesChatSlug(chatSlug);
    };
    window.addEventListener('messages:chat-change', onMessagesChatChange);
    return () => window.removeEventListener('messages:chat-change', onMessagesChatChange);
  }, [setMessagesChatSlug]);

  // Track admin dirty/saving state published by ProjectAdminTab.
  // Unconditional so we always reset when the tab unmounts (it dispatches
  // null detail on cleanup) and never miss the first dispatch on mount.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ dirty: boolean; isSaving: boolean } | null>).detail;
      setProjectAdminHeaderState(detail ?? null);
    };
    window.addEventListener('project-admin:state-change', handler);
    return () => window.removeEventListener('project-admin:state-change', handler);
  }, []);

  // Writes admin-targeted notifications (archive/deletion candidates), so
  // only worth running for internal users — a client's own RLS write would
  // just fail.
  useEffect(() => {
    if (!isInternalRole(auth.user?.role as UserRole)) return;
    setTimeout(() => {
      initializeDataLifecycle();
    }, 0);
  }, [auth.user?.role]);

  useEffect(() => {
    const throttleMs = 60_000;
    let lastRefresh = 0;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const now = Date.now();
        if (now - lastRefresh < throttleMs) return;
        lastRefresh = now;
        DataService.refreshFromBackend().catch((error: unknown) =>
          console.error('Failed to refresh data from backend:', error)
        );
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const view = urlParams.get('view');
    if (view === 'system-settings' || view === 'admin') {
      navigateToView('admin');
      const xeroConnected = urlParams.get('xero_connected');
      const xeroError = urlParams.get('xero_error');
      if (xeroConnected === 'true') {
        setTimeout(() => {
          const notification = buildNotificationTemplate('xero-connected');
          NotificationService.success(notification.message, {
            title: notification.title,
            userId: auth.user?.id,
            userName: auth.user?.name,
            metadata: { integration: 'xero' },
          });
          window.history.replaceState({}, document.title, '/');
        }, 100);
      } else if (xeroError) {
        setTimeout(() => {
          const notification = buildNotificationTemplate('xero-connection-failed', {
            reason: decodeURIComponent(xeroError),
          });
          NotificationService.error(notification.message, {
            title: notification.title,
            userId: auth.user?.id,
            userName: auth.user?.name,
            metadata: { integration: 'xero', error: xeroError },
          });
          window.history.replaceState({}, document.title, '/');
        }, 100);
      }
    } else if (view === 'workspaces' || view === 'businesses') {
      navigateToView('workspaces');
      const xeroConnected = urlParams.get('xero_connected');
      if (xeroConnected === 'true') {
        setTimeout(() => {
          const notification = buildNotificationTemplate('xero-connected');
          NotificationService.success(notification.message, {
            title: notification.title,
            userId: auth.user?.id,
            userName: auth.user?.name,
            metadata: { integration: 'xero', context: 'client' },
          });
          window.history.replaceState({}, document.title, '/');
        }, 100);
      }
    }

    const googleDriveConnected = urlParams.get('google_drive_connected');
    if (googleDriveConnected === 'true') {
      navigateToView('admin');
      setTimeout(() => {
        const notification = buildNotificationTemplate('drive-connected');
        NotificationService.success(notification.message, {
          title: notification.title,
          userId: auth.user?.id,
          userName: auth.user?.name,
          metadata: { integration: 'google_drive' },
        });
        window.history.replaceState({}, document.title, '/');
      }, 100);
    }
  }, [auth.user, navigateToView, urlParams]);

  // Expose the data + create bridge the native New Project form uses (single
  // source of truth stays in the web DataService). Re-installs when the user
  // changes so createProject stamps the right createdBy.
  useEffect(() => {
    if (!NativeBridge.isIOSShell()) return;
    installNativeAddProjectData(auth.user ? { id: auth.user.id } : null);
  }, [auth.user]);

  const [payrollProfileMenu, setPayrollProfileMenu] = useState<{ name: string } | null>(null);
  useEffect(() => {
    const onPayrollProfile = (event: Event) => {
      const detail = (event as CustomEvent<{ hub?: string; profileOpen?: boolean; sectionLabel?: string } | null>).detail;
      setPayrollProfileMenu(detail?.hub === 'payroll' && detail.profileOpen
        ? { name: detail.sectionLabel || 'Employee' }
        : null);
    };
    window.addEventListener('payroll-tax:state-change', onPayrollProfile);
    return () => window.removeEventListener('payroll-tax:state-change', onPayrollProfile);
  }, []);

  const nativeAccountingNavigation = useMemo(() => buildNativeAccountingNavigation({
    role: auth.user?.role ?? 'member', currentView,
    resolvedMainItems: workspaceMainNavigation.authoritative ? workspaceMainNavigation.navigation?.main ?? null : undefined,
  }), [auth.user?.role, currentView, workspaceMainNavigation.authoritative, workspaceMainNavigation.navigation]);

  const nativePipelineNavigation = useMemo(() => buildNativePipelineNavigation({
    internalWorkspace: workspaceMainNavigation.internalWorkspace,
    role: auth.user?.role ?? 'staff', currentView, outreachActiveTab,
    resolvedMainItems: workspaceMainNavigation.authoritative ? workspaceMainNavigation.navigation?.main ?? null : undefined,
  }), [auth.user?.role, currentView, outreachActiveTab, workspaceMainNavigation.internalWorkspace, workspaceMainNavigation.authoritative, workspaceMainNavigation.navigation]);

  const workspaceNavigation = useWorkspaceNavigation(projectTabProps?.projectId);
  const nativeProfilePlugins = useWorkspacePlugins((nativeBridge.hasNativeSidebar || nativeBridge.hasNativeTabBar) && (currentView === 'profile' || currentView === 'account'));
  const canViewWorkspacePlugins = nativeProfilePlugins.catalogue?.pageVisible === true;
  useEffect(() => {
    if (!auth.user || !NativeBridge.isRunningNatively()) return;

    const role = auth.user.role ?? 'staff';
    const items = buildNativeNavItems({
      role,
      internalWorkspace: workspaceMainNavigation.internalWorkspace,
      resolvedMainItems: workspaceMainNavigation.authoritative ? workspaceMainNavigation.navigation?.main ?? null : undefined,
      resolvedProjectItems: workspaceNavigation.authoritative ? workspaceNavigation.navigation?.project ?? null : undefined,
      currentView: currentView === 'add-workspace' ? 'workspaces' : currentView,
      projectActiveTab,
      projectTabProps,
      projectPermissions,
      workspacesStats,
      workspaceDetail,
      hasNativeSidebar: nativeBridge.hasNativeSidebar,
      hasNativeTabBar: nativeBridge.hasNativeTabBar,
      interimApplicationOpen: !!interimAppState,
      hsEditorSectionIds:
        currentView === 'project-detail' && projectActiveTab === 'hs'
          ? hsEditorState?.sectionIds ?? null
          : null,
      hsDocumentSectionIds:
        currentView === 'project-detail' && projectActiveTab === 'hs'
          ? hsDocumentState?.sectionIds ?? null
          : null,
      // The web sidebar drills into the edit-user form's sections; the native
      // one has to follow or it sits a level behind the content.
      userFormOpen: workspaceUserFormOpen,
      // Same reason: the web sidebar drills in to an open employer's sections,
      // so the native one has to follow.
      employerDetailOpen: !!employersListState?.selectedName,
      // And one level deeper again for the add/edit form.
      employerFormSectionIds: employerFormState?.sectionIds ?? null,
      emailSharedMailboxes,
      canManageWorkspaceSettings,
      canViewWorkspacePlugins,
    });

    const profileItems = currentView === 'payroll' && payrollProfileMenu
      ? [
          { id: 'employees', label: 'Employees', sfSymbol: 'person.2' },
          { id: 'employee-overview', label: 'Overview', sfSymbol: 'person.crop.circle' },
          { id: 'employee-pay', label: 'Pay', sfSymbol: 'banknote' },
          { id: 'employee-emergency', label: 'Emergency and notes', sfSymbol: 'phone' },
          { id: 'employee-documents', label: 'Documents', sfSymbol: 'doc' },
          { id: 'employee-timesheets', label: 'Timesheets', sfSymbol: 'clock' },
        ].map((item) => ({ ...item, id: `payroll-tab:${item.id}`, section: 'payroll' }))
      : null;
    NativeBridge.sendNavItems(profileItems
      ? [...items.filter((item) => !item.id.startsWith('payroll-tab:')), ...profileItems]
      : items);
  }, [auth.user, workspacesStats, workspaceDetail, currentView, nativeBridge.hasNativeSidebar, nativeBridge.hasNativeTabBar, projectPermissions, projectTabProps, projectActiveTab, interimAppState, hsEditorState?.sectionIds, hsDocumentState?.sectionIds, workspaceUserFormOpen, employersListState?.selectedName, employerFormState?.sectionIds, emailSharedMailboxes, canManageWorkspaceSettings, canViewWorkspacePlugins, payrollProfileMenu, workspaceNavigation.authoritative, workspaceNavigation.navigation, workspaceMainNavigation.authoritative, workspaceMainNavigation.internalWorkspace, workspaceMainNavigation.navigation]);

  useEffect(() => {
    if (!NativeBridge.isRunningNatively()) return;

    const searchVisible =
      showContextualSearch ||
      isNativeContextualSearchRoute({
        currentView,
        projectActiveTab,
        employersListVisible: !!employersListState?.listVisible,
        workspaceDocumentsOpen,
      });
    // Carry a per-route placeholder so the native search field reads
    // "Search projects" / "Search conversations" etc., not a generic "Search".
    // Takeoff additionally flips "Search sheets" -> "Search drawing" once a
    // sheet is open — the same search box now searches the drawing's PDF
    // text layer instead of filtering the gallery by name.
    NativeBridge.sendSearchState(
      searchVisible,
      searchVisible
        ? nativeContextualSearchPrompt({
            currentView,
            projectActiveTab,
            takeoffDrawingOpen,
            workspaceDetailActiveTab: workspaceDetail?.activeTab ?? null,
            documentsFolderName,
            workspaceDocumentsOpen,
          })
        : undefined,
      // Project sections are a detail context by the shell's test (canGoBack is
      // set for the whole project-detail route, to get the chevron back to
      // Projects), so without this the native bar is suppressed on exactly the
      // two project tabs that declare themselves searchable — Documents and
      // Takeoff. Messages, the reason that suppression exists, is unaffected.
      currentView === 'project-detail',
    );
  }, [currentView, projectActiveTab, showContextualSearch, takeoffDrawingOpen, workspaceDetail, employersListState?.listVisible, documentsFolderName, workspaceDocumentsOpen]);

  // Scroll-driven minimize for the native iOS search bar. The shell content
  // scrolls inside a nested element — the scroll outlet itself on normal routes
  // (.qs-shell-content-outlet--scroll) or an inner list on shell-locked routes
  // like Messages (whose outlet is .qs-shell-content-outlet--locked, overflow
  // hidden). The native side can't observe either, so we watch in the capture
  // phase and report a collapse boolean. The native shell minimizes the search
  // bar past ~64px and expands it within 8px of the top; hysteresis avoids
  // flicker. Re-created on route change (deps below) so `collapsed` re-baselines
  // to expanded — without it a bar scrolled away on one list would stay hidden
  // at the top of the next search route.
  useEffect(() => {
    if (!NativeBridge.isRunningNatively() || nativeBridge.platform !== 'ios') return;
    let collapsed = false;
    let frame = 0;
    const onScroll = (event: Event) => {
      const el = event.target as HTMLElement | null;
      // The scrolled element is the outlet itself (scroll routes) or sits inside
      // the locked outlet (Messages etc.); read scrollTop off whichever scrolled.
      if (!el?.closest?.('.qs-shell-content-outlet--scroll, .qs-shell-content-outlet--locked')) return;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const top = el.scrollTop;
        const next = collapsed ? top > 8 : top > 64;
        if (next !== collapsed) {
          collapsed = next;
          NativeBridge.sendSearchBarCollapsed(next);
        }
        // Separate, near-zero signal for the native header's background.
        // Deliberately NOT reusing `collapsed` above: that has 64/8 hysteresis
        // tuned for hiding a search bar, and is force-reset by search-route
        // changes — a bar that stayed transparent for the first 64px of every
        // scroll, and flipped when an unrelated search route changed, would be
        // wrong. The bar needs "is the content still at rest" instead, so it
        // can sit transparent at the top and take a material the instant
        // anything passes underneath it.
        //
        // `progress` ramps over the first 20px so native can fade rather than
        // cut, without the web needing to know what it does with it.
        // sendDeduped drops repeats, so this is one message per state change.
        const scrolled = top > 0;
        NativeBridge.sendHeaderScrollState(
          scrolled,
          Math.min(1, Math.max(0, top / 20)),
        );
      });
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      document.removeEventListener('scroll', onScroll, { capture: true } as EventListenerOptions);
    };
  }, [nativeBridge.platform, currentView, projectActiveTab]);

  useEffect(() => {
    let cancelled = false;

    resolveAvatarSourceUrl(auth.user?.avatar ?? null)
      .then((resolvedUrl) => {
        if (!cancelled) {
          setNativeProfileAvatarUrl(resolvedUrl);
        }
      })
      .catch((error: unknown) => {
        console.error('[AuthenticatedController] Failed to resolve native profile avatar', error);
        if (!cancelled) {
          setNativeProfileAvatarUrl(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [auth.user?.avatar]);

  // Listen for presence changes broadcast by AppSidebar so we can include the
  // current status in the next sidebarState send without prop-threading.
  useEffect(() => {
    if (!NativeBridge.isRunningNatively()) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ status: NativePresenceDisplayId }>).detail;
      if (detail?.status) setNativePresenceStatus(detail.status);
    };
    window.addEventListener('quantis:presence:status-change', handler);
    return () => window.removeEventListener('quantis:presence:status-change', handler);
  }, []);

  useEffect(() => {
    if (!auth.user || !NativeBridge.isRunningNatively()) return;
    const profileDisplayName =
      auth.user.display_name?.trim() ||
      auth.user.displayName?.trim() ||
      [auth.user.first_name, auth.user.last_name].filter(Boolean).join(' ').trim() ||
      auth.user.name?.trim() ||
      auth.user.email?.split('@')[0] ||
      'User';

    NativeBridge.sendSidebarState({
      appName: nativeBridge.hasNativeSidebar ? '' : 'Quantis',
      organizationName: auth.activeWorkspace?.companyName || auth.company?.name || 'Contract Group',
      workspaceName: auth.activeWorkspace?.name || null,
      logoFullLight,
      logoFullDark,
      profileDisplayName,
      profileAvatarUrl: nativeProfileAvatarUrl,
      presenceStatus: nativePresenceStatus,
      canManageWorkspaceSettings,
    });
  }, [auth.activeWorkspace, auth.company, auth.user, canManageWorkspaceSettings, logoFullDark, logoFullLight, nativeBridge.hasNativeSidebar, nativeProfileAvatarUrl, nativePresenceStatus]);

  useEffect(() => {
    const unsubscribeNavigate = NativeBridge.on('navigate', ({ view, resetSelection }) => {
      if (view.startsWith('workspace-setup:')) {
        window.dispatchEvent(new CustomEvent('workspaces:create-step', { detail: { step: Number(view.split(':')[1]) } }));
        return;
      }
      // Sentinel from buildNativeHeaderState's takeoffDrawingOpen branch: the
      // native chevron's goBack() calls navigate(toViewId: sentinel) instead
      // of routing anywhere. Intercept BEFORE any prefix/isAppView handling
      // below — the sentinel is not a real AppView and must never reach
      // handleNativeViewNavigation or the router's history calls. Closing the
      // drawing is delegated to TakeoffView via a plain event so there is a
      // single reliable close path shared with the browser header (see
      // UnifiedAppHeader's inTakeoffDrawing branch).
      if (view === TAKEOFF_CLOSE_DRAWING_SENTINEL) {
        window.dispatchEvent(new CustomEvent('takeoff:close-drawing'));
        return;
      }
      if (view === SITE_PHOTO_CLOSE_FULL_VIEW_SENTINEL) {
        window.location.assign(window.location.pathname);
        return;
      }

      // Email thread back is a native global-header chevron, not a browser
      // history operation. EmailView owns the selection and clears it through
      // the same event used by the web header.
      if (view === EMAIL_THREAD_BACK_SENTINEL) {
        window.dispatchEvent(new CustomEvent('email:back-to-list'));
        window.dispatchEvent(new CustomEvent('project-email:back-to-list'));
        return;
      }

      // Notifications back chevron: return to wherever the bell was tapped
      // from (recorded in the global:notifications handler below), falling
      // back to Home when the origin is unknown (e.g. a restored route).
      if (view === NOTIFICATIONS_BACK_SENTINEL) {
        const target = notificationsReturnViewRef.current ?? 'dashboard';
        notificationsReturnViewRef.current = null;
        handleNativeViewNavigation(target);
        return;
      }

      // Sentinel from buildNativeHeaderState's takeoffUploaderOpen branch: the
      // native chevron closes the full-page Add Drawings uploader (back to the
      // sheet gallery). TakeoffView listens for 'takeoff:upload-close' — the
      // same event the browser header's inTakeoffUploader branch dispatches.
      if (view === TAKEOFF_CLOSE_UPLOADER_SENTINEL) {
        window.dispatchEvent(new CustomEvent('takeoff:upload-close'));
        return;
      }

      // Uploader-open + deeper than root: step up one folder instead of closing.
      if (view === TAKEOFF_UPLOADER_UP_SENTINEL) {
        window.dispatchEvent(new CustomEvent('takeoff:upload-up'));
        return;
      }

      // Uploader-open + on a post-processing step (page picker or review
      // grid): step back through the flow instead of closing the uploader or
      // stepping up a folder. TakeoffUploader's listener picks the target —
      // from the review grid that's the picker, or the file picker when the
      // batch skipped page selection.
      if (view === TAKEOFF_UPLOADER_BACK_TO_SOURCE_SENTINEL) {
        window.dispatchEvent(new CustomEvent('takeoff:upload-back-to-source'));
        return;
      }

      // Sentinel from buildNativeHeaderState's takeoffSheetDetailsOpen branch:
      // the native chevron closes the full-page Sheet details editor (back to
      // the sheet gallery) instead of leaving the project and silently
      // discarding unsaved edits. TakeoffView listens for
      // 'takeoff:sheet-details-close'.
      if (view === TAKEOFF_CLOSE_SHEET_DETAILS_SENTINEL) {
        window.dispatchEvent(new CustomEvent('takeoff:sheet-details-close'));
        return;
      }

      // Sentinel from buildNativeHeaderState's templatePreviewOpen branch: the
      // macOS chevron closes the export-template preview (back to the template
      // library) instead of leaving Settings. TakeoffSheetPreview listens for
      // 'takeoff-template:close'.
      if (view === TEMPLATE_PREVIEW_CLOSE_SENTINEL) {
        window.dispatchEvent(new CustomEvent('takeoff-template:close'));
        return;
      }

      // Same sentinel pattern as Takeoff above: the macOS toolbar chevron on
      // an open workspace detail must close the detail (back to the
      // Directory list) rather than leave Workspaces entirely. WorkspaceList
      // listens for 'workspaces:close-detail' and clears selectedClient.
      if (view === WORKSPACES_CLOSE_DETAIL_SENTINEL) {
        window.dispatchEvent(new CustomEvent('workspaces:close-detail'));
        return;
      }

      // Same sentinel pattern for a drilled-in employer profile: the chevron
      // closes the profile back to the directory rather than leaving Employers.
      if (view === EMPLOYERS_CLOSE_DETAIL_SENTINEL) {
        window.dispatchEvent(new CustomEvent(EMPLOYERS_CLEAR_SELECTION_EVENT));
        return;
      }

      // Same sentinel pattern for an open rate item form: the chevron closes
      // the form back to the library list. RateItemPage listens for
      // 'resource-library-item:back', the same event the web header's
      // 'rate-item-back' pill dispatches, so both surfaces share one close path.
      if (view === RESOURCE_LIBRARY_ITEM_BACK_SENTINEL) {
        window.dispatchEvent(new CustomEvent('resource-library-item:back'));
        return;
      }

      // Same sentinel pattern: the back chevron inside an open documents
      // folder steps up one level. DocumentManagementTab listens.
      if (view === DOCUMENTS_FOLDER_UP_SENTINEL) {
        window.dispatchEvent(new CustomEvent('documents:folder-up'));
        return;
      }
      if (view === NOTES_CLOSE_DETAIL_SENTINEL) {
        window.dispatchEvent(new CustomEvent('project-notes:back-to-list'));
        return;
      }
      if (view === REPORTS_CLOSE_REPORT_SENTINEL) {
        window.dispatchEvent(new CustomEvent('reports:back-to-cards'));
        return;
      }
      if (view === PLANNER_CLOSE_EDITOR_SENTINEL) {
        window.dispatchEvent(new CustomEvent('planner:close-editor'));
        return;
      }

      // Back inside an open interim application closes it (back to the
      // Applications list). InterimApplication listens.
      if (view === INTERIM_CLOSE_APPLICATION_SENTINEL) {
        window.dispatchEvent(new CustomEvent('interim:close-application'));
        return;
      }

      // A drilled-in machine (standalone /plant or the register embedded in
      // a project's H&S tab) closes back to the table. navigation.ts resolves
      // this ahead of HS_BACK_SENTINEL, so the embedded case gets two taps:
      // this one first, then HS_BACK_SENTINEL walks up to the H&S hub.
      // PlantRegisterView already listens for PLANT_CLEAR_SELECTION_EVENT
      // regardless of which screen mounted it.
      if (view === PLANT_CLOSE_DETAIL_SENTINEL) {
        window.dispatchEvent(new CustomEvent(PLANT_CLEAR_SELECTION_EVENT));
        return;
      }

      // Back inside the H&S drill walks the RAMS stack down one level
      // (editor → record → list → hub). RAMSList/ProjectHSTab listen — the
      // same event the web header's chevron fires.
      if (view === HS_BACK_SENTINEL) {
        window.dispatchEvent(new CustomEvent('hs:back'));
        return;
      }

      // Sidebar section rows of an open interim application - route the
      // section id to InterimApplication rather than the app router.
      if (typeof view === 'string' && view.startsWith(INTERIM_SECTION_PREFIX)) {
        window.dispatchEvent(
          new CustomEvent('interim:navigate-section', {
            detail: { sectionId: view.slice(INTERIM_SECTION_PREFIX.length) },
          })
        );
        return;
      }

      // RAMS editor section rows live in the actual native sidebar. They swap
      // the editor's active page rather than changing the app route.
      if (typeof view === 'string' && view.startsWith(HS_EDITOR_SECTION_PREFIX)) {
        window.dispatchEvent(new CustomEvent('hs:navigate-editor-section', {
          detail: { sectionId: view.slice(HS_EDITOR_SECTION_PREFIX.length) },
        }));
        return;
      }

      if (typeof view === 'string' && view.startsWith(HS_DOCUMENT_SECTION_PREFIX)) {
        window.dispatchEvent(new CustomEvent('hs:navigate-document-section', {
          detail: { sectionId: view.slice(HS_DOCUMENT_SECTION_PREFIX.length) },
        }));
        return;
      }

      if (resetSelection && view === 'projects') {
        handleNativeViewNavigation('projects');
        return;
      }

      if (typeof view === 'string' && view.startsWith(PROJECT_TAB_PREFIX)) {
        const tab = view.slice(PROJECT_TAB_PREFIX.length);
        if (tab) {
          if (
            currentViewRef.current === 'project-detail' &&
            projectActiveTabRef.current === tab &&
            selectedProjectIdRef.current
          ) {
            return;
          }

          setProjectActiveTab(tab);
          if (currentViewRef.current !== 'project-detail') {
            // Keep the currently selected project context when native sidebar/tab
            // commands arrive slightly ahead of the web route state update.
            if (selectedProjectIdRef.current) {
              navigateImmediatelyForNative('project-detail');
            } else {
              handleNativeViewNavigation('projects');
            }
            return;
          }
        }
        return;
      }

      if (typeof view === 'string' && view.startsWith(SYSTEM_SETTINGS_TAB_PREFIX)) {
        const tab = view.slice(SYSTEM_SETTINGS_TAB_PREFIX.length);
        if (tab) {
          setSystemSettingsActiveTab(tab);
          if (currentViewRef.current !== 'system-settings') {
            handleNativeViewNavigation('system-settings');
          }
        }
        return;
      }

      if (typeof view === 'string' && view.startsWith(PREFERENCES_TAB_PREFIX)) {
        const tab = view.slice(PREFERENCES_TAB_PREFIX.length);
        if (tab) {
          setPreferencesSection(tab === PREFERENCES_LIST_ID ? null : tab);
          if (currentViewRef.current !== 'preferences') {
            handleNativeViewNavigation('preferences');
          }
        }
        return;
      }

      if (typeof view === 'string' && view.startsWith(PROFILE_TAB_PREFIX)) {
        const tab = view.slice(PROFILE_TAB_PREFIX.length);
        if (tab) {
          setProfileActiveTab(tab);
          const targetView = tab.startsWith(WORKSPACE_PROFILE_TAB_PREFIX) ? 'profile' : 'account';
          if (currentViewRef.current !== targetView) {
            handleNativeViewNavigation(targetView);
          }
        }
        return;
      }

      // Email folder rows (macOS sidebar only — see buildNativeNavItems).
      // Unlike the hub prefixes below, email has no separate section
      // component to notify via window event: emailActiveTab is consumed
      // directly by EmailView's activeLabel prop (App.tsx owns the state),
      // so setting it here is the entire wiring.
      if (typeof view === 'string' && view.startsWith(EMAIL_TAB_PREFIX)) {
        const tab = view.slice(EMAIL_TAB_PREFIX.length);
        if (tab) {
          setEmailActiveTab(tab);
          if (currentViewRef.current !== 'email') {
            handleNativeViewNavigation('email');
          }
        }
        return;
      }

      // Shared mailbox rows use their own native prefix but feed the same
      // EmailView active-label state as the personal Gmail folders.
      if (typeof view === 'string' && view.startsWith(EMAIL_SHARED_TAB_PREFIX)) {
        const groupId = view.slice(EMAIL_SHARED_TAB_PREFIX.length);
        if (groupId) {
          setEmailActiveTab(`group:${groupId}`);
          if (currentViewRef.current !== 'email') {
            handleNativeViewNavigation('email');
          }
        }
        return;
      }

      // Workspace detail section rows (macOS sidebar only, when a workspace is
      // open — see buildNativeNavItems' workspaceDetail branch). Unlike
      // WORKSPACES_TAB_PREFIX below there's no local state to set here:
      // WorkspaceList owns detailActiveTab and listens for
      // 'workspaces:detail-set-tab' directly, so dispatching is the entire
      // wiring (mirrors the Email folder rows' pattern above).
      if (typeof view === 'string' && view.startsWith(WORKSPACE_DETAIL_TAB_PREFIX)) {
        const tab = view.slice(WORKSPACE_DETAIL_TAB_PREFIX.length);
        if (tab) {
          window.dispatchEvent(new CustomEvent('workspaces:detail-set-tab', { detail: { tab } }));
          if (currentViewRef.current !== 'workspaces') {
            handleNativeViewNavigation('workspaces');
          }
        }
        return;
      }

      // Employer add/edit form section rows. Same shape as the profile rows
      // below, and checked first so the shorter-lived, deeper context wins.
      // EmployerForm owns which page is mounted and listens for this directly —
      // no router leg, because the form's page is not in the URL.
      if (typeof view === 'string' && view.startsWith(EMPLOYER_FORM_TAB_PREFIX)) {
        const sectionId = view.slice(EMPLOYER_FORM_TAB_PREFIX.length);
        if (sectionId) {
          window.dispatchEvent(new CustomEvent(EMPLOYER_FORM_SET_SECTION_EVENT, { detail: { sectionId } }));
          if (currentViewRef.current !== 'employers') {
            handleNativeViewNavigation('employers');
          }
        }
        return;
      }

      // Employer profile section rows. Like the workspace detail rows above
      // there is no local state to set: EmployerProfilePanel owns the scroll
      // position and listens for the section event directly.
      if (typeof view === 'string' && view.startsWith(EMPLOYER_DETAIL_TAB_PREFIX)) {
        const sectionId = view.slice(EMPLOYER_DETAIL_TAB_PREFIX.length);
        if (sectionId) {
          window.dispatchEvent(new CustomEvent(EMPLOYER_DETAIL_SET_SECTION_EVENT, { detail: { sectionId } }));
          if (currentViewRef.current !== 'employers') {
            handleNativeViewNavigation('employers');
          }
        }
        return;
      }

      if (typeof view === 'string' && view.startsWith(WORKSPACES_TAB_PREFIX)) {
        const tab = view.slice(WORKSPACES_TAB_PREFIX.length) as 'clients' | 'requests' | 'add-client';
        if (tab) {
          setWorkspacesActiveTab(tab);
          if (currentViewRef.current !== 'workspaces') {
            handleNativeViewNavigation('workspaces');
          }
        }
        return;
      }

      if (typeof view === 'string' && view.startsWith(PAYROLL_TAB_PREFIX)) {
        const tab = view.slice(PAYROLL_TAB_PREFIX.length);
        if (tab) {
          setPayrollActiveTab(tab);
          window.dispatchEvent(new CustomEvent('payroll:navigate-section', { detail: { sectionId: tab } }));
          if (currentViewRef.current !== 'payroll') {
            handleNativeViewNavigation('payroll');
          }
        }
        return;
      }

      if (typeof view === 'string' && view.startsWith(ACCOUNTING_TAB_PREFIX)) {
        const tab = view.slice(ACCOUNTING_TAB_PREFIX.length);
        if (tab) {
          setAccountingActiveTab(tab);
          window.dispatchEvent(new CustomEvent('accounting:navigate-section', { detail: { sectionId: tab } }));
          if (currentViewRef.current !== 'accounting') {
            handleNativeViewNavigation('accounting');
          }
        }
        return;
      }

      if (typeof view === 'string' && view.startsWith(SALES_TAB_PREFIX)) {
        const tab = view.slice(SALES_TAB_PREFIX.length);
        if (tab) {
          setSalesActiveTab(tab);
          window.dispatchEvent(new CustomEvent('sales:navigate-section', { detail: { sectionId: tab } }));
          if (currentViewRef.current !== 'sales') {
            handleNativeViewNavigation('sales');
          }
        }
        return;
      }

      if (typeof view === 'string' && view.startsWith(PURCHASES_TAB_PREFIX)) {
        const tab = view.slice(PURCHASES_TAB_PREFIX.length);
        if (tab) {
          setPurchasesActiveTab(tab);
          window.dispatchEvent(new CustomEvent('purchases:navigate-section', { detail: { sectionId: tab } }));
          if (currentViewRef.current !== 'purchases') {
            handleNativeViewNavigation('purchases');
          }
        }
        return;
      }

      if (typeof view === 'string' && view.startsWith(TAX_TAB_PREFIX)) {
        const tab = view.slice(TAX_TAB_PREFIX.length);
        if (tab) {
          setTaxActiveTab(tab);
          window.dispatchEvent(new CustomEvent('tax:navigate-section', { detail: { sectionId: tab } }));
          if (currentViewRef.current !== 'tax') {
            handleNativeViewNavigation('tax');
          }
        }
        return;
      }

      const mappedView = NATIVE_TAB_TO_VIEW[view] ?? view;
      if (mappedView && isAppView(mappedView)) {
        handleNativeViewNavigation(mappedView);
      }
    });

    const unsubscribeOpenChat = NativeBridge.on('openChat', ({ chatId, messageId }) => {
      handleOpenChatThread(chatId, messageId ?? null);
    });

    const unsubscribeHandlePush = NativeBridge.on('handlePush', (payload) => {
      const calendarIntent = resolveNotificationNavigationIntent(payload.actionUrl, {
        eventId: payload.eventId ?? payload.metadata?.eventId,
        occurrenceStart: payload.occurrenceStart ?? payload.metadata?.occurrenceStart,
        taskId: payload.taskId ?? payload.metadata?.taskId,
      });
      if (calendarIntent.kind === 'calendar-event') {
        void queueCalendarEventTarget(calendarIntent).catch(() => { toast.error('Could not open this calendar event.'); });
        window.dispatchEvent(new CustomEvent('calendar:open-project', { detail: { projectId: null } }));
      } else if ((payload.type === 'message' || payload.type === 'chat_message') && payload.chatId) {
        handleOpenChatThread(payload.chatId, payload.messageId ?? null);
      } else if (calendarIntent.kind === 'task') {
        handleShowCalendarWithTask(calendarIntent.taskId);
      } else if (payload.type === 'project' && payload.projectId) {
        handleSelectProjectRef.current(payload.projectId, payload.projectName || 'Project');
      } else if (
        (payload.type === 'email_received' || payload.actionUrl === '/email') &&
        queueEmailNotificationOpen({
          notificationId: payload.notificationId,
          messageId: payload.messageId,
          threadId: payload.threadId,
        })
      ) {
        setEmailActiveTab('INBOX');
        handleNativeViewNavigation('email');
      }
    });

    // Tapped web-push notification while a tab was already open: the service
    // worker forwards the notification's actionUrl instead of reloading.
    const unsubscribeNavigateUrl = NativeBridge.on('navigateUrl', ({ actionUrl }: { actionUrl: string }) => {
      const intent = resolveNotificationNavigationIntent(actionUrl);
      if (intent.kind === 'calendar-event') {
        void queueCalendarEventTarget(intent).catch(() => { toast.error('Could not open this calendar event.'); });
        window.dispatchEvent(new CustomEvent('calendar:open-project', { detail: { projectId: null } }));
      } else if (intent.kind === 'boq-comment') {
        window.location.assign(intent.url);
      } else if (intent.kind === 'chat') {
        handleOpenChatThread(intent.chatId, intent.messageId ?? null);
      } else if (intent.kind === 'task') {
        handleShowCalendarWithTask(intent.taskId);
      } else if (intent.kind === 'project') {
        handleSelectProjectRef.current(intent.projectId, intent.projectName || 'Project');
      } else if (intent.kind === 'view') {
        handleNativeViewNavigation(intent.view);
      }
    });

    const unsubscribeNotificationAction = NativeBridge.on('notificationAction', async (payload) => {
      const chatId = typeof payload?.chatId === 'string' ? payload.chatId : null;
      const messageId = typeof payload?.messageId === 'string' ? payload.messageId : null;
      const action = typeof payload?.action === 'string' ? payload.action : 'open';
      const replyText = typeof payload?.replyText === 'string' ? payload.replyText.trim() : '';

      if (!chatId) return;

      if (action === 'markRead' && auth.user?.id) {
        try {
          await MessagingSvc.markAsRead(chatId, auth.user.id);
        } catch (error) {
          console.error('Failed to mark chat as read from native notification action:', error);
        }
      }

      if (action === 'reply' && auth.user?.id && replyText) {
        try {
          await MessagingSvc.sendMessage(chatId, auth.user.id, replyText);
        } catch (error) {
          console.error('Failed to send chat reply from native notification action:', error);
        }
      }

      handleOpenChatThread(chatId, messageId);
    });

    const unsubscribeRefresh = NativeBridge.on('requestRefresh', () => {
      DataService.refreshFromBackend(true).catch((error: unknown) =>
        console.error('Failed to refresh from native request:', error)
      );
    });

    const unsubscribePanel = NativeBridge.on('panel', ({ panel, chatId }: { panel: string; chatId?: string }) => {
      if (nativeOwnsSidePanel) {
        if (panel === 'messages') {
          setPanelChatId(typeof chatId === 'string' && chatId.length > 0 ? chatId : null);
          setPanelThreadMessageId(null);
        } else if (panel === 'notifications') {
          setPanelChatId(null);
          setPanelThreadMessageId(null);
        }
        return;
      }

      if (panel === 'notifications') {
        if (notificationOpenRef.current) {
          setNotificationOpen(false);
          return;
        }

        setMessagePanelOpen(false);
        setPanelChatId(null);
        setPanelThreadMessageId(null);
        setNotificationOpen(true);
        return;
      }

      if (panel === 'messages') {
        if ((!chatId || chatId.length === 0) && messagePanelOpenRef.current) {
          setMessagePanelOpen(false);
          setPanelChatId(null);
          setPanelThreadMessageId(null);
          return;
        }

        setNotificationOpen(false);
        if (typeof chatId === 'string' && chatId.length > 0) {
          setMessagePanelOpen(true);
          setPanelChatId(chatId);
          setPanelThreadMessageId(null);
          return;
        }

        setMessagePanelOpen(true);
        setPanelChatId(null);
        setPanelThreadMessageId(null);
      }
    });

    const unsubscribePrimaryAction = NativeBridge.on('primaryAction', ({ action }: { action: string }) => {
      // View-owned action ids are handled by the page that published them
      // (its own primaryAction subscription); the controller must never grow
      // a branch for these or the action fires twice.
      if (action === 'export-portfolio-overview') return;
      if (action === 'addProject') {
        navigateToView('add-project');
      } else if (action === 'compose') {
        navigateToView('messages');
      } else if (action === 'addUser') {
        // Inside an open workspace the Users tab owns its own add form
        // (WorkspaceDetail's showAddUserForm), so route to that rather than
        // the standalone user-form view, which would navigate away from the
        // workspace entirely.
        if (
          (currentViewRef.current === 'workspaces' && workspaceDetailRef.current?.activeTab === 'users')
          || currentViewRef.current === 'profile'
          || currentViewRef.current === 'account'
        ) {
          window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_CREATE_EVENT));
        } else {
          navigateToView('user-form');
        }
      } else if (action === 'sendWorkspaceInvite') {
        window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_INVITE_SUBMIT_EVENT));
      } else if (action === 'addContact') {
        // WHY: contacts:open-add is the same event the web header + pill fires;
        // CompaniesView listens for it and opens the slide-over form.
        window.dispatchEvent(new CustomEvent('contacts:open-add'));
      } else if (action === 'addEnquiry') {
        // WHY: pipeline views expose different create events depending on which
        // pipeline is active; fire the matching one for the current view.
        if (currentViewRef.current === 'pipeline') {
          window.dispatchEvent(new CustomEvent('pipeline:create-deal'));
        } else if (currentViewRef.current === 'workspace-pipeline') {
          window.dispatchEvent(new CustomEvent('workspace-pipeline:create-deal'));
        }
      } else if (action === 'uploadDocument') {
        if (
          (currentViewRef.current === 'project-detail' && projectActiveTabRef.current === 'documents')
          || workspaceDocumentsOpenRef.current
        ) {
          window.dispatchEvent(new CustomEvent('quantis:documents:upload'));
        }
      } else if (action === 'add' && currentViewRef.current === 'project-detail') {
        if (projectActiveTabRef.current === 'pricing') {
          window.dispatchEvent(new CustomEvent('boq:add-item'));
        } else if (projectActiveTabRef.current === 'documents') {
          window.dispatchEvent(new CustomEvent('quantis:documents:upload'));
        } else if (projectActiveTabRef.current === 'planner') {
          window.dispatchEvent(new CustomEvent('planner:create-task'));
        } else if (projectActiveTabRef.current === 'tasks') {
          // Keep native and web creation on one path. ProjectTasksList owns
          // the shared task sheet and preselects the open project.
          window.dispatchEvent(new CustomEvent('project-tasks:create-new'));
        } else if (projectActiveTabRef.current === 'interim' || projectActiveTabRef.current === 'applications') {
          // InterimApplicationsList listens for interim:create-new and
          // early-returns unless the user can manage applications.
          window.dispatchEvent(new CustomEvent('interim:create-new'));
        } else if (projectActiveTabRef.current === 'hs') {
          const hsSection = hsHeaderStateRef.current?.section ?? 'rams';
          if (hsSection === 'toolbox') {
            window.dispatchEvent(new CustomEvent('hs:toolbox-create'));
          } else if (hsSection === 'plant') {
            // A check sheet has no blank form: its rows come from a template,
            // so the native + opens the picker rather than an empty record.
            window.dispatchEvent(new CustomEvent('plant:from-template'));
          } else if (hsSection === 'register') {
            // Same event the standalone /plant screen's + pill fires —
            // PlantRegisterView listens for this regardless of which screen
            // mounted it.
            window.dispatchEvent(new CustomEvent(PLANT_CREATE_EVENT));
          } else if (hsSection === 'near-miss') {
            window.dispatchEvent(new CustomEvent('hs:near-miss-create'));
          } else {
            // Blank RAMS. The web header's + also offers From template via a
            // pull-down; native has a single primary slot, and the RAMS empty
            // state carries its own From template button, so blank is the +.
            window.dispatchEvent(new CustomEvent('hs:create'));
          }
        }
      } else if (action === 'hsSubmit') {
        // Draft record's native primary action — same event the web Send for
        // review pill fires; RAMSViewer listens and opens the recipient-picker
        // dialog rather than transitioning directly.
        window.dispatchEvent(new CustomEvent('hs:submit-review'));
      } else if (action === 'savePlannerActivity') {
        window.dispatchEvent(new CustomEvent('planner:save-activity'));
      } else if (action === 'hsIssue') {
        // Approved record's native primary action — same event the web Issue
        // pill fires; RAMSViewer listens and runs the server transition.
        // Approval itself happens on the public review page, not natively.
        window.dispatchEvent(new CustomEvent('hs:transition', { detail: { to: 'issued' } }));
      } else if (action === 'add' && currentViewRef.current === 'employers') {
        // navigation.ts has published primaryAction 'add' for this view all
        // along with no branch here, so the native + was inert. Same event the
        // web header's + pill fires; EmployerManagement listens.
        window.dispatchEvent(new CustomEvent(EMPLOYERS_CREATE_EVENT));
      } else if (action === 'add' && currentViewRef.current === 'plant') {
        // Same shape as Employers above — navigation.ts publishes primaryAction
        // 'add' for /plant, PlantRegisterView listens for this event.
        window.dispatchEvent(new CustomEvent(PLANT_CREATE_EVENT));
      } else if (action === 'add' && currentViewRef.current === 'calendar') {
        // WHY: native primary-action for tasks now fires on the calendar view;
        // the calendar's Tasks tab listens for tasks:create-new.
        window.dispatchEvent(new CustomEvent('tasks:create-new'));
      } else if (action === 'clearNotifications') {
        // Native nav-bar "Clear" on the Notifications view; the mounted panel
        // listens for this and clears all notifications.
        window.dispatchEvent(new CustomEvent('notifications:clear-all'));
      } else if (action === 'saveWorkspace') {
        // WorkspaceOverview listens for workspace:save (same contract the web
        // header pill uses) — this is the only save affordance on the macOS
        // shell, where the web header pills are suppressed.
        window.dispatchEvent(new CustomEvent('workspace:save'));
      } else if (action === 'saveEmployer') {
        // One native id, two saves: the add/edit form and a drilled-in profile.
        // navigation.ts only publishes it for the profile once that profile is
        // dirty, so unsaved-changes state is what tells the two apart.
        window.dispatchEvent(new CustomEvent(
          employerDetailStateRef.current?.hasChanges
            ? EMPLOYER_DETAIL_SAVE_EVENT
            : EMPLOYER_FORM_SAVE_EVENT,
        ));
      } else if (action === 'addWorkspace') {
        // AuthenticatedWorkspace owns this event (same one the web header's
        // + pill fires) and switches the directory to the add-client tab.
        window.dispatchEvent(new CustomEvent('workspaces:create-new'));
      } else if (action === 'savePlant') {
        // One id for both the standalone /plant screen and the register
        // embedded in a project's H&S tab, unlike saveEmployer's two-event
        // split — PlantRegisterView's own handleSave already branches on
        // `creating` internally, so a single event covers Save and Create.
        window.dispatchEvent(new CustomEvent(PLANT_DETAIL_SAVE_EVENT));
      } else if (action === 'saveProspect') {
        window.dispatchEvent(new CustomEvent(PROSPECT_DETAIL_SAVE_EVENT));
      }
    });

    // Cancel counterpart to 'saveWorkspace' above — rides the generic
    // secondaryAction bus (same mechanism UserProfile.tsx uses directly for
    // its own Save/Cancel toolbar pair).
    const unsubscribeSecondaryAction = NativeBridge.on('secondaryAction', ({ action }: { action: string }) => {
      if (action === 'workspace-cancel') {
        window.dispatchEvent(new CustomEvent('workspace:cancel'));
      }
      if (action === 'workspace-invite-cancel') {
        window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_INVITE_CANCEL_EVENT));
      }
      if (action === 'employer-cancel') {
        window.dispatchEvent(new CustomEvent(EMPLOYER_FORM_CANCEL_EVENT));
      }
      if (action === 'employer-detail-cancel') {
        window.dispatchEvent(new CustomEvent(EMPLOYER_DETAIL_CANCEL_EVENT));
      }
      if (action === 'prospect-detail-cancel') {
        window.dispatchEvent(new CustomEvent(PROSPECT_DETAIL_CANCEL_EVENT));
      }
    });

    const unsubscribeAccountAction = NativeBridge.on('accountAction', ({ action, status }: { action: string; status?: string }) => {
      if (action === 'profile') {
        navigateToView('account');
        return;
      }

      if (action === 'logout') {
        void auth.logout();
        setSelectedProjectId(null);
        setSelectedProjectName(null);
        setSelectedClientId(null);
        setSelectedClientName(null);
        setSelectedTaskId(null);
        setDocumentRouteSlug(null);
        setEventsRouteSlug(null);
        setTaskRouteSlug(null);
        setShareToken(null);
        return;
      }

      // Native account menu presence picker — map contract id → internal value
      // and forward to AppSidebar via window event (avoids prop-threading).
      if (action === 'set-status' && status) {
        const internalStatus = NATIVE_STATUS_TO_PRESENCE[status as NativePresenceStatusId];
        if (internalStatus) {
          window.dispatchEvent(
            new CustomEvent('quantis:presence:set-status', { detail: { status: internalStatus } }),
          );
        }
        return;
      }

      // The native account menu follows the same active-membership gate as the
      // browser sidebar. It opens the self-service settings route, never the
      // super-admin cross-workspace directory.
      if (action === 'workspace-settings') {
        if (canManageWorkspaceSettings) {
          setProfileActiveTab(`${WORKSPACE_PROFILE_TAB_PREFIX}overview`);
          navigateToView('profile');
        } else {
          navigateToView('dashboard');
        }
      }
    });

    return () => {
      unsubscribeNavigate();
      unsubscribeOpenChat();
      unsubscribeHandlePush();
      unsubscribeNavigateUrl();
      unsubscribeNotificationAction();
      unsubscribeRefresh();
      unsubscribePanel();
      unsubscribePrimaryAction();
      unsubscribeSecondaryAction();
      unsubscribeAccountAction();
    };
  }, [
    auth,
    auth.user?.id,
    canManageWorkspaceSettings,
    handleOpenChatThread,
    handleShowCalendarWithTask,
    handleNativeViewNavigation,
    setMessagePanelOpen,
    setNotificationOpen,
    setPanelChatId,
    setSelectedClientId,
    setSelectedClientName,
    setSelectedProjectId,
    setSelectedProjectName,
    setSelectedTaskId,
    setShareToken,
    setTaskRouteSlug,
    setDocumentRouteSlug,
    setProjectActiveTab,
    setSystemSettingsActiveTab,
    setPreferencesSection,
    setProfileActiveTab,
    setWorkspacesActiveTab,
    setEmailActiveTab,
    nativeOwnsSidePanel,
    toggleMessagesPanel,
    toggleNotificationsPanel,
    navigateImmediatelyForNative,
    navigateToView,
  ]);

  useEffect(() => {
    if (pageTitle && pageTitle !== 'Quantis') {
      lastStableNativeTitleRef.current = pageTitle;
    }
  }, [pageTitle]);

  useEffect(() => {
    if (!NativeBridge.isRunningNatively()) return;
    if (nativeBridge.hasNativeSidebar || nativeBridge.platform === 'macos') return;

    NativeBridge.send('panelState', {
      messagesOpen: messagePanelOpen,
      notificationsOpen: notificationOpen,
    });
  }, [messagePanelOpen, nativeBridge.hasNativeSidebar, nativeBridge.platform, notificationOpen]);

  useEffect(() => {
    const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const resolvedNativeTitle =
      pageTitle && pageTitle !== 'Quantis'
        ? pageTitle
        : lastStableNativeTitleRef.current;
    const activeSharedMailbox = currentView === 'email' && emailActiveTab.startsWith('group:')
      ? emailSharedMailboxes.find(({ id }) => id === emailActiveTab.slice('group:'.length)) ?? null
      : null;
    const nativeHeaderTitle =
      currentView === 'payroll' && payrollProfileMenu
        ? payrollProfileMenu.name
      : currentView === 'project-detail'
        && projectActiveTab === 'site_photos'
        && sitePhotoFullViewOpen
        ? 'Site Photo'
      : currentView === 'project-detail' && projectReportIdFromTab(projectActiveTab) && reportHeaderState
        ? reportHeaderState.title
      : currentView === 'project-detail'
        && projectActiveTab === 'email'
        && projectEmailHeaderState.detailOpen
        && projectEmailHeaderState.subject
        ? projectEmailHeaderState.subject
      : activeSharedMailbox
          ? activeSharedMailbox.label
        : resolvedNativeTitle;
    const emailNativeSubtitle = currentView === 'email' && !emailThreadState
      ? activeSharedMailbox
        ? 'Shared Mailbox'
        : `All Mail · ${unreadEmailCount} Unread`
      : null;
    const projectEmailNativeSubtitle = currentView === 'project-detail'
      && projectActiveTab === 'email'
      && projectEmailHeaderState.detailOpen
      ? projectEmailHeaderState.subtitle
      : null;
    const pathname = window.location.pathname;
    const search = window.location.search;
    const pathDerivedProjectTab = (() => {
      const prettyTakeoffRoute = parsePrettyProjectTakeoffPath(pathname);
      if (prettyTakeoffRoute) {
        return 'takeoff';
      }

      const prettyDocumentRoute = parsePrettyProjectDocumentPath(pathname);
      if (prettyDocumentRoute) {
        return 'documents';
      }

      const prettyProjectTabRoute = parsePrettyProjectTabPath(pathname);
      if (prettyProjectTabRoute?.tab) {
        return prettyProjectTabRoute.tab;
      }

      const projectMatch = pathname.match(/^\/projects\/([^/]+)\/?$/);
      if (projectMatch) {
        const params = new URLSearchParams(search);
        return params.get('tab')?.trim() || 'overview';
      }

      return null;
    })();

    const resolvedProjectTabForNative =
      currentView === 'project-detail'
        ? (projectActiveTab || pathDerivedProjectTab || 'overview')
        : projectActiveTab;

    const itemIdentifier = getNativeRouteItemIdentifier({
      currentView,
      pipelineMainItemId: nativePipelineNavigation.primaryItemId,
      accountingMainItemId: nativeAccountingNavigation.primaryItemId,
      interimApplicationOpen: !!interimAppState,
      interimActiveSection: interimAppState?.activeSection ?? null,
      hsEditorActiveSection:
        currentView === 'project-detail' && resolvedProjectTabForNative === 'hs'
          ? hsEditorState?.activeSection ?? null
          : null,
      hsDocumentActiveSection:
        currentView === 'project-detail' && resolvedProjectTabForNative === 'hs'
          ? hsDocumentState?.activeSection ?? null
          : null,
      projectActiveTab: resolvedProjectTabForNative,
      systemSettingsActiveTab,
      preferencesSection,
      profileActiveTab,
      workspacesActiveTab,
      workspaceDetail,
      payrollActiveTab,
      accountingActiveTab,
      salesActiveTab,
      purchasesActiveTab,
      taxActiveTab,
      emailActiveTab,
      hasNativeSidebar: nativeBridge.hasNativeSidebar,
      hasNativeTabBar: nativeBridge.hasNativeTabBar,
    });

    NativeBridge.reportRouteState(
      path,
      resolvedNativeTitle,
      itemIdentifier,
      window.location.href
    );

    NativeBridge.sendNavigationDiagnostics({
      currentView,
      path,
      title: resolvedNativeTitle,
      itemIdentifier: itemIdentifier ?? null,
      url: window.location.href,
      nativeTab: VIEW_TO_NATIVE_TAB[currentView] ?? null,
      willEmitNavigateTo:
        !!(VIEW_TO_NATIVE_TAB[currentView] &&
          !NativeBridge.isRunningNatively() &&
          !nativeBridge.hasNativeSidebar &&
          !nativeBridge.hasNativeTabBar &&
          currentView !== 'project-detail'),
      isRunningNatively: NativeBridge.isRunningNatively(),
      hasNativeSidebar: nativeBridge.hasNativeSidebar,
      hasNativeTabBar: nativeBridge.hasNativeTabBar,
      stateView: window.history.state?.view ?? null,
      stateProjectId: window.history.state?.projectId ?? null,
      stateProjectActiveTab: window.history.state?.projectActiveTab ?? null,
    });

    const nativeTab = VIEW_TO_NATIVE_TAB[currentView];
    // Avoid feeding navigation commands back into native shells. Native hosts
    // already derive active selection from routeState/itemIdentifier, and any
    // extra navigateTo() command can trigger a WKWebView top-level reload that
    // snaps users back to home/dashboard.
    //
    // Keep navigateTo() for pure web-only shells that still embed this code, but
    // never emit it when running in iOS/macOS native containers.
    if (
      nativeTab &&
      !NativeBridge.isRunningNatively() &&
      !nativeBridge.hasNativeSidebar &&
      !nativeBridge.hasNativeTabBar &&
      currentView !== 'project-detail'
    ) {
      NativeBridge.navigateTo(nativeTab);
    }

    // Notifications owns its own native header on iOS (title + Mark-all-read +
    // Clear, driven by its filter/unread state) — NotificationContent supplies
    // that replacement entirely, so this parent effect bails out completely
    // there. On macOS the parent effect still runs (it owns the base title/
    // primaryAction/canGoBack via buildNativeHeaderState), but navButtons are
    // deferred to NotificationContent via the viewOwnsNavButtons gate below —
    // otherwise the toolbar keeps the previous view's stale title.
    if (currentView === 'notifications' && nativeBridge.platform === 'ios') return;

    // Members panel owns the native toolbar while open: it sets this flag on
    // mount and clears it on unmount. Guard prevents this parent effect from
    // clobbering the "Members" title with the section title on unrelated dep
    // changes (e.g. messagesUnreadCount). MembersPanel re-sends its own header
    // state from its own useEffect, which always runs after this parent effect.
    if (currentView === 'messages' && (window as any).__quantisMembersHeaderActive) return;

    // Sidebar shells (macOS + iPad): inject global Messages + Notifications as
    // native toolbar navButtons so the web floating header is not needed there.
    // Gated on hasNativeSidebar rather than platform === 'macos' — the iPad
    // idiom swapped its floating tab bar (which carried an Alerts tab) for the
    // NavigationSplitView sidebar, and the sidebar's navItems don't include
    // Alerts, so without this iPad loses its only route to Notifications.
    // hasNativeSidebar is false on iPhone (it keeps the floating tab bar with
    // its own Alerts tab), so this can't double up there.
    // Views that publish their own navButtons (ChatWindow → 'messages',
    // CalendarPage → 'calendar', NotificationContent → 'notifications') call
    // sendHeaderState from a child effect that runs BEFORE this parent effect
    // on mount, so passing the globals here would overwrite their view-
    // specific buttons until the next child resend. Pass undefined for those
    // views instead — buildNativeHeaderState then omits the navButtons field,
    // leaving the child's buttons intact.
    // NotificationContent is the SOLE source of the macOS notifications
    // header (Select/Edit mode navButtons, Mark-all-read) — see its own
    // sendHeaderState effect. Including 'notifications' here prevents this
    // parent effect from ever sending navButtons for that view, so there is
    // only one sender and no race between the two.
    const viewOwnsNavButtons =
      currentView === 'messages' || currentView === 'calendar' || currentView === 'notifications';
    // Admin Save/Cancel pills — prepended when the Admin tab has dirty edits.
    const adminNavButtons: NativeHeaderState['navButtons'] =
      nativeBridge.platform === 'macos' &&
      currentView === 'project-detail' &&
      isProjectAdminTab(projectActiveTab) &&
      projectAdminHeaderState?.dirty === true
        ? [
            { id: 'admin-cancel', sfSymbol: 'xmark', label: 'Cancel' },
            { id: 'admin-save', sfSymbol: 'checkmark', label: 'Save' },
          ]
        : [];
    const nativeOwnsHsEditorControls = nativeBridge.hasNativeSidebar ||
      (nativeBridge.hasNativeTabBar && hsEditorState?.canAddItems === true);
    const hsEditorNavButtons: NativeHeaderState['navButtons'] =
      nativeOwnsHsEditorControls &&
      currentView === 'project-detail' &&
      projectActiveTab === 'hs' &&
      hsHeaderState?.view === 'editor' &&
      hsEditorState &&
      !hsEditorState.isSaving
        ? [
            { id: 'hs-editor:cancel', sfSymbol: 'xmark', label: 'Cancel' },
            { id: 'hs-editor:save', sfSymbol: 'checkmark', label: hsEditorState.saveLabel ?? 'Save' },
          ]
        : [];
    const hsEditorAddLabel = nativeOwnsHsEditorControls &&
      currentView === 'project-detail' && projectActiveTab === 'hs' &&
      hsEditorState?.canAddItems === true && !hsEditorState.isSaving
      ? getHSEditorAddLabel(hsEditorState.activeSection) : undefined;
    const hsEditorAddButtons: NativeHeaderState['navButtons'] = hsHeaderState?.view === 'editor' && hsEditorAddLabel
      ? [{ id: 'hs-editor-add-item', sfSymbol: 'plus', label: hsEditorAddLabel }] : [];
    const hsDocumentNavButtons: NativeHeaderState['navButtons'] =
      (nativeBridge.hasNativeSidebar || nativeBridge.hasNativeTabBar) &&
      currentView === 'project-detail' &&
      projectActiveTab === 'hs' &&
      hsHeaderState?.view === 'record' &&
      (hsHeaderState.section ?? 'rams') === 'rams'
        ? [{ id: 'hs-details', sfSymbol: 'info.circle', label: 'Details' }]
        : [];
    const hsOutlineState =
      currentView === 'project-detail' && projectActiveTab === 'hs' &&
      (hsHeaderState?.section ?? 'rams') === 'rams'
        ? hsHeaderState?.view === 'editor' && !hsEditorState?.isSaving
          ? hsEditorState
          : hsHeaderState?.view === 'record' ? hsDocumentState : null
        : null;
    const hsOutlineIndex = hsOutlineState?.sectionIds.indexOf(hsOutlineState.activeSection) ?? -1;
    const hsSectionNavButtons: NativeHeaderState['navButtons'] = hsOutlineState && hsOutlineIndex >= 0
      ? [
          { direction: 'previous', sectionId: hsOutlineState.sectionIds[hsOutlineIndex - 1], sfSymbol: 'arrow.left' },
          { direction: 'next', sectionId: hsOutlineState.sectionIds[hsOutlineIndex + 1], sfSymbol: 'arrow.right' },
        ].filter((item) => !!item.sectionId).map(({ direction, sectionId, sfSymbol }) => ({
          id: `${hsHeaderState?.view === 'editor' ? 'hs-editor-nav:' : 'hs-document-nav:'}${sectionId}`,
          sfSymbol,
          label: `${direction === 'previous' ? 'Previous' : 'Next'} section: ${HS_DOCUMENT_SECTION_ITEMS.find((item) => item.id === sectionId)?.label ?? sectionId}`,
        }))
      : [];
    // Takeoff upload/fit/run-AI/export + the Types inspector toggle — native
    // window-toolbar buttons (the web takeoff header never mounts on this shell)
    // like every other macOS action button. Gated on takeoffHeaderState
    // (null until TakeoffView's first broadcast, and on unmount) so nothing
    // renders before the tab is actually ready. `takeoff-fit` additionally
    // requires takeoffDrawingOpen (a sheet open in the canvas) — fit is
    // meaningless in the sheet gallery, mirroring buildTakeoffHeaderActions'
    // takeoffCanvasState gate. Loading states swap the icon for a spinner-ish
    // SF Symbol rather than disabling — NativeHeaderState.navButtons has no
    // disabled field, so an unavailable action is omitted outright instead of
    // shown dead (canRunAI/canExport already gate that).
    // Per-tab primary actions, published so the shells render them as real
    // toolbar buttons instead of leaving them to the in-page web chrome. Each
    // fires the same window event as its web pill, so one listener serves
    // both surfaces.
    const projectTasksNavButtons: NativeHeaderState['navButtons'] =
      (nativeBridge.hasNativeSidebar || nativeBridge.hasNativeTabBar) &&
      currentView === 'project-detail' &&
      (projectActiveTab === 'notes' || projectActiveTab === 'tasks') &&
      (workspaceNavigation.authoritative
        ? workspaceNavigation.navigation?.project.some(item => item.id === 'tasks') === true
        : projectPermissions?.canViewTasks === true)
        ? [{ id: 'project-notebook-tasks', sfSymbol: 'checkmark.square', label: 'Tasks' }]
        : [];
    const projectNotesNavButtons: NativeHeaderState['navButtons'] =
      nativeBridge.platform === 'macos' &&
      currentView === 'project-detail' &&
      (projectActiveTab === 'notes' || projectActiveTab === 'tasks') &&
      (!workspaceNavigation.authoritative || workspaceNavigation.navigation?.project.some(item => item.id === 'notes') === true)
        ? [
            // Only offered when something is actually unread — otherwise it
            // is a toolbar button whose only outcome is nothing happening.
            ...(notesUnreadCount > 0
              ? [{ id: 'project-notes-mark-read', sfSymbol: 'checkmark.circle', label: 'Mark All Read' }]
              : []),
            { id: 'project-notes-add', sfSymbol: 'plus', label: 'New Note' },
          ]
        : [];
    const projectEmailNavButtons: NativeHeaderState['navButtons'] =
      // Publish project-mail actions through whichever native toolbar the
      // shell owns. Sidebar shells keep their full desktop action set; tab-bar
      // shells switch between list actions and thread actions.
      (nativeBridge.hasNativeSidebar || nativeBridge.hasNativeTabBar) &&
      currentView === 'project-detail' &&
      projectActiveTab === 'email'
        ? nativeBridge.hasNativeSidebar
          ? [
              { id: 'project-email-refresh', sfSymbol: 'arrow.clockwise', label: 'Refresh' },
              ...(projectEmailHeaderState.hasThreads
                ? [
                    { id: 'project-email-reply', sfSymbol: 'arrowshape.turn.up.left', label: 'Reply' },
                    { id: 'project-email-reply-all', sfSymbol: 'arrowshape.turn.up.left.2', label: 'Reply All' },
                    { id: 'project-email-forward', sfSymbol: 'arrowshape.turn.up.right', label: 'Forward' },
                  ]
                : []),
              { id: 'project-email-compose', sfSymbol: 'square.and.pencil', label: 'New Communication' },
              ...(projectEmailHeaderState.hasThreads && !projectEmailHeaderState.isClient && !projectEmailHeaderState.loading
                ? [{ id: 'project-email-prepare-tender', sfSymbol: 'sparkles', label: 'Prepare Tender' }]
                : []),
            ]
          : projectEmailHeaderState.detailOpen
            ? (projectEmailHeaderState.hasThreads
              ? [
                  { id: 'project-email-reply', sfSymbol: 'arrowshape.turn.up.left', label: 'Reply' },
                  { id: 'project-email-reply-all', sfSymbol: 'arrowshape.turn.up.left.2', label: 'Reply All' },
                  { id: 'project-email-forward', sfSymbol: 'arrowshape.turn.up.right', label: 'Forward' },
                ]
              : [])
            : [
                { id: 'project-email-refresh', sfSymbol: 'arrow.clockwise', label: 'Refresh' },
                { id: 'project-email-compose', sfSymbol: 'square.and.pencil', label: 'New Communication' },
              ]
        : [];
    const isTakeoffTab =
      currentView === 'project-detail' && projectActiveTab === 'takeoff';
    // Mirrors buildTakeoffHeaderActions' canSelectAll on the web pill: offered
    // whenever picking it would still change something, including before
    // anything is selected, and hidden once every visible sheet already is.
    const takeoffGalleryVisible =
      !!takeoffHeaderState && !takeoffHeaderState.uploaderOpen && !takeoffDrawingOpen && !takeoffHeaderState.sheetDetailsOpen;
    const takeoffCanSelectAll =
      takeoffGalleryVisible
      && (takeoffHeaderState?.visibleSheetCount ?? 0) > 1
      && (takeoffHeaderState?.selectedSheetCount ?? 0) < (takeoffHeaderState?.visibleSheetCount ?? 0);
    // Mirrors buildTakeoffHeaderActions' canDownloadSheets: the bulk download
    // is offered whenever something in scope (the selection, else the whole
    // visible gallery) actually carries measurements to export.
    const takeoffDownloadCount = takeoffHeaderState?.downloadableSheetCount ?? 0;
    const takeoffCanDownload = takeoffGalleryVisible && takeoffDownloadCount > 0;
    const takeoffDownloadButton = {
      id: 'takeoff-selection-download',
      sfSymbol: takeoffHeaderState?.isDownloading ? 'hourglass' : 'square.and.arrow.down',
      label: (takeoffHeaderState?.selectedSheetCount ?? 0) > 0
        ? `Download ${takeoffDownloadCount} Drawing${takeoffDownloadCount === 1 ? '' : 's'}`
        : `Download All ${takeoffDownloadCount} Drawing${takeoffDownloadCount === 1 ? '' : 's'}`,
    };
    const takeoffNavButtons: NativeHeaderState['navButtons'] =
      nativeBridge.platform === 'macos' && isTakeoffTab && takeoffHeaderState
        ? [
            ...(takeoffUploaderDetails
              // Details step (title-block region drawing) — field toggles +
              // Import replace Browse/Clear/Continue. Native has no toggle
              // style, so the active field is prefixed with a bullet and a
              // set field swaps in a filled checkmark glyph.
              ? ([
                  ['drawingNumber', 'Drawing No'],
                  ['title', 'Title'],
                  ['revision', 'Revision'],
                ] as const).map(([key, label]) => ({
                  id: `takeoff-upload-field-${key}`,
                  sfSymbol: takeoffUploaderDetails!.fieldsSet[key] ? 'checkmark.circle.fill' : 'square.dashed',
                  label: takeoffUploaderDetails!.activeField === key ? `● ${label}` : label,
                }))
              : takeoffHeaderState.uploaderOpen
                // Uploader open — the native "+" would re-open a no-op, so the
                // toolbar slot becomes the "Browse my computer" file picker
                // (moved out of the uploader's bottom dropzone). Source step
                // only; past it the queued batch is owned by the run.
                ? (takeoffUploaderCanBrowse
                    ? [{ id: 'takeoff-upload-browse', sfSymbol: 'folder', label: 'Browse My Computer' }]
                    : [])
                // Suppressed while Sheet details is open — see the matching
                // web gate in buildTakeoffHeaderActions (P2 review finding
                // on PR #723: tapping Upload there discarded unsaved edits).
                : takeoffHeaderState.canUpload && !takeoffHeaderState.sheetDetailsOpen
                  ? [{ id: 'takeoff-upload', sfSymbol: 'plus', label: 'Upload Sheet' }]
                  : []),
            ...(takeoffGalleryVisible && takeoffHeaderState.gallery ? ([
              ['sort', 'Sort', 'arrow.up.arrow.down', GALLERY_SORTS],
              ['view', 'View', 'list.bullet', GALLERY_VIEWS],
              ['status', 'Filter', 'line.3.horizontal.decrease', GALLERY_FILTERS],
            ] as const).map(([field, label, sfSymbol, options]) => ({
              id: `takeoff-gallery-${field}`, label: options.find(([value]) => value === takeoffHeaderState.gallery?.[field])?.[1] ?? label, sfSymbol,
              menuItems: options.map(([value, text]) => ({ id: `takeoff-gallery:${field}:${value}`, label: text,
                ...(takeoffHeaderState.gallery?.[field] === value ? { sfSymbol: 'checkmark' } : {}),
              })),
            })) : []),
            // Review step — Cancel (full exit) + Import, matching the web
            // cluster. Cancel lives here because with the page picker retired
            // this is the only one-press way out of a parsed batch.
            ...(takeoffUploaderDetails
              ? [
                  { id: 'takeoff-upload-cancel', sfSymbol: 'xmark', label: 'Cancel' },
                  {
                    id: 'takeoff-upload-import',
                    sfSymbol: 'square.and.arrow.down',
                    label: `Import ${takeoffUploaderDetails.sheetCount} sheet${takeoffUploaderDetails.sheetCount === 1 ? '' : 's'}`,
                  },
                ]
              : []),
            // Selection actions — Clear + primary Continue, moved out of the
            // uploader's bottom footer. Only while something's selected.
            ...(!takeoffUploaderDetails && takeoffHeaderState.uploaderOpen && takeoffUploaderSelected > 0
              ? [
                  { id: 'takeoff-upload-clear', sfSymbol: 'xmark', label: 'Clear' },
                  {
                    id: 'takeoff-upload-continue',
                    sfSymbol: 'arrow.right.circle.fill',
                    label: `Continue with ${takeoffUploaderSelected} drawing${takeoffUploaderSelected === 1 ? '' : 's'}`,
                  },
                ]
              : []),
            // Gallery multi-select cluster — Edit Details/Delete/Select All/
            // Clear, badge carries the count (native toolbar buttons have no
            // text-label pill, unlike the web header's "N selected" chip).
            // Same gallery-visible gate as Sheet Details below; takes over its
            // slot while a selection is active (see gallerySelectionActive
            // in buildTakeoffHeaderActions for the matching web logic).
            ...(takeoffGalleryVisible && (takeoffHeaderState.selectedSheetCount > 0)
              ? [
                  { id: 'takeoff-selection-edit-details', sfSymbol: 'pencil', label: 'Edit Details', badge: takeoffHeaderState.selectedSheetCount },
                  ...(takeoffCanDownload ? [takeoffDownloadButton] : []),
                  { id: 'takeoff-selection-delete', sfSymbol: 'trash', label: 'Delete Selected' },
                  // Sits before Clear Selection so the pair reads left-to-right
                  // as "pick everything" then "pick nothing" (see the web pill's
                  // matching order in buildTakeoffHeaderActions).
                  ...(takeoffCanSelectAll ? [{ id: 'takeoff-selection-select-all', sfSymbol: 'checklist', label: 'Select All' }] : []),
                  { id: 'takeoff-selection-clear', sfSymbol: 'xmark.circle', label: 'Clear Selection' },
                ]
              // Nothing selected yet — Select All is still reachable so a big
              // gallery doesn't force hand-picking a first sheet to unlock it.
              : [
                  ...(takeoffCanDownload ? [takeoffDownloadButton] : []),
                  ...(takeoffCanSelectAll
                    ? [{ id: 'takeoff-selection-select-all', sfSymbol: 'checklist', label: 'Select All' }]
                    : []),
                ]),
            // Sheet details — after-the-fact editor for Drawing Title/Number/
            // Revision on already-imported sheets. Same gallery-only gate as
            // the web pill (see buildTakeoffHeaderActions). Suppressed while
            // a selection is active — see the cluster above.
            ...(!takeoffHeaderState.uploaderOpen && !takeoffDrawingOpen && !takeoffHeaderState.sheetDetailsOpen
                && takeoffHeaderState.hasSheets && takeoffHeaderState.selectedSheetCount === 0
              ? [{ id: 'takeoff-sheet-details', sfSymbol: 'pencil', label: 'Sheet Details' }]
              : []),
            ...(takeoffDrawingOpen
              ? [{ id: 'takeoff-fit', sfSymbol: 'house', label: 'Fit to View' }]
              : []),
            ...(takeoffHeaderState.canRunAI
              ? [{
                  id: 'takeoff-run-ai',
                  sfSymbol: takeoffHeaderState.isAILoading ? 'hourglass' : 'sparkles',
                  label: takeoffHeaderState.isAILoading ? 'Running AI Takeoff…' : 'Run AI Takeoff',
                }]
              : []),
            ...(takeoffHeaderState.canExport
              ? [{
                  id: 'takeoff-export',
                  sfSymbol: takeoffHeaderState.isExporting ? 'hourglass' : 'square.and.arrow.down',
                  label: takeoffHeaderState.isExporting ? 'Exporting…' : 'Export to Documents',
                }]
              : []),
            // Types inspector toggle — the native macOS inspector button. Shows
            // only with a drawing open (the web Types panel is mounted then).
            // Taps route to the same takeoff:toggle-panel-collapse event the web
            // trailing pill uses (see buildTakeoffHeaderActions). `sidebar.right`
            // is the system inspector glyph.
            ...(takeoffDrawingOpen
              ? [{ id: 'takeoff-inspector', sfSymbol: 'sidebar.right', label: 'Types Inspector' }]
              : []),
          ]
        : [];
    // Enquiries board Filter + Sort — native toolbar buttons that each carry
    // menuItems, so the macOS shell renders them as anchored SwiftUI Menus
    // (native popovers). Built from the board's published serviceFilter/sortKey
    // (live checkmarks); item taps round back via quantis:native:menuAction,
    // which ConsultancyPipelineView routes into filter/sort state.
    const pipelineNavButtons: NativeHeaderState['navButtons'] =
      nativeBridge.platform === 'macos' && currentView === 'pipeline' && pipelineBoardState
        ? [
            {
              id: 'pipeline:filter',
              sfSymbol: pipelineBoardState.serviceFilter
                ? 'line.3.horizontal.decrease.circle.fill'
                : 'line.3.horizontal.decrease.circle',
              label: 'Filter',
              menuItems: [
                {
                  id: 'pl-svc:',
                  label: 'All services',
                  ...(pipelineBoardState.serviceFilter === '' ? { sfSymbol: 'checkmark' } : {}),
                },
                ...pipelineBoardState.services.map(([key, label]) => ({
                  id: `pl-svc:${key}`,
                  label,
                  ...(pipelineBoardState.serviceFilter === key ? { sfSymbol: 'checkmark' } : {}),
                })),
              ],
            },
            {
              id: 'pipeline:sort',
              sfSymbol: 'arrow.up.arrow.down',
              label: 'Sort',
              menuItems: ([
                ['newest', 'Newest'], ['oldest', 'Oldest'],
                ['value', 'Highest value'], ['idle', 'Most idle'],
              ] as const).map(([key, label]) => ({
                id: `pl-sort:${key}`,
                label,
                ...(pipelineBoardState.sortKey === key ? { sfSymbol: 'checkmark' } : {}),
              })),
            },
          ]
        : [];
    // Workspaces directory Filter — mirrors the web header's filter into
    // the macOS toolbar as an anchored menu (live checkmark + badge, taps
    // round back via quantis:native:menuAction which WorkspaceList routes
    // into filterStatus). Directory only: it narrows the LIST, so it is
    // meaningless while a workspace detail fills the view, and the
    // Requests tab has its own in-content status pills. No list-pane
    // toggle here: the toolbar back chevron already closes an open detail
    // back to the full-width list.
    const workspacesNavButtons: NativeHeaderState['navButtons'] =
      nativeBridge.platform === 'macos' && currentView === 'workspaces'
        && workspacesActiveTab === 'clients' && !workspaceDetail
        ? [
            {
              id: 'ws:filter',
              sfSymbol: (workspacesListState?.activeFilterCount ?? 0) > 0
                ? 'line.3.horizontal.decrease.circle.fill'
                : 'line.3.horizontal.decrease.circle',
              label: 'Filter Workspaces',
              ...((workspacesListState?.activeFilterCount ?? 0) > 0
                ? { badge: workspacesListState!.activeFilterCount }
                : {}),
              menuItems: [
                ...([['production', 'Production'], ['non-production', 'Demo & Test'], ['all', 'All workspace types']] as const).map(([value, label]) => ({
                  id: `ws-classification:${value}`, label,
                  ...((workspacesListState?.classificationFilter ?? 'production') === value ? { sfSymbol: 'checkmark' } : {}),
                })),
                ...([
                ['all',                'All statuses'],
                ['active',             'Active'],
                ['invited',            'Invited'],
                ['pending_invitation', 'Pending'],
                ['inactive',           'Inactive'],
                ['suspended',          'Suspended'],
                ['payment_overdue',    'Payment Overdue'],
              ] as const).map(([value, label]) => ({
                id: `ws-filter:${value}`,
                label,
                ...((workspacesListState?.filterStatus ?? 'all') === value
                  ? { sfSymbol: 'checkmark' }
                  : {}),
              })),
              ],
            },
          ]
        : [];
    // Employers directory status filter — the one hoisted control with no
    // native home otherwise, mirrored into the sidebar shells' toolbar as an
    // anchored menu (live checkmarks; taps round back via
    // quantis:native:menuAction, which EmployerManagement routes into
    // statusFilter). Gated on hasNativeSidebar rather than platform 'macos' so
    // iPad gets it too — its shell renders anchored menu navButtons the same
    // way. Directory only: it narrows the list, so it is meaningless over a
    // profile or the add form.
    // iPad renders navButtons but NOT primaryAction/secondaryAction, so Add on the
    // directory and Save/Cancel on a dirty profile — all published through those
    // two slots for macOS and iPhone — simply do not exist there. Mirror them here.
    // Gated off macOS deliberately: macOS renders BOTH channels, so publishing to
    // both would give it two Saves.
    const isPadShell = nativeBridge.hasNativeSidebar && nativeBridge.platform !== 'macos';
    const workspaceInvitePadNavButtons: NativeHeaderState['navButtons'] =
      isPadShell
      && (currentView === 'profile' || currentView === 'account')
      && profileActiveTab === 'workspace/users/invite'
      && workspaceInviteState?.visible
      && workspaceInviteState.actionsInHeader
      && !workspaceInviteState.submitting
        ? [
            { id: 'workspace-invite:cancel', sfSymbol: 'xmark', label: 'Cancel' },
            ...(workspaceInviteState.canSubmit
              ? [{ id: 'workspace-invite:send', sfSymbol: 'paperplane.fill', label: 'Send Invitation' }]
              : []),
          ]
        : [];
    const employerPadNavButtons: NativeHeaderState['navButtons'] =
      isPadShell && currentView === 'employers'
        // Deepest context first. The add/edit form has no back chevron on this
        // shell either (employers' backTarget is 'account', which resolves to no
        // chevron off macSidebarDrillIn), so without a Cancel here the form is a
        // hard dead end — the sidebar shows only the form's own pages.
        // Cancel is unconditional, not gated on hasChanges: a pristine form still
        // has to be escapable, same reasoning as the secondaryAction publish.
        ? employerFormState && !employerFormState.isSubmitting
          ? [
              { id: 'emp:form-cancel', sfSymbol: 'xmark', label: 'Cancel' },
              ...(employerFormState.hasChanges
                ? [{ id: 'emp:form-save', sfSymbol: 'checkmark', label: 'Save' }]
                : []),
            ]
          : employersListState?.selectedName && employerDetailState?.hasChanges && !employerDetailState.isSubmitting
          ? [
              { id: 'emp:detail-cancel', sfSymbol: 'xmark', label: 'Cancel' },
              { id: 'emp:detail-save', sfSymbol: 'checkmark', label: 'Save' },
            ]
          : employersListState?.listVisible
            ? [{ id: 'emp:add', sfSymbol: 'plus', label: 'Add Employer' }]
            : []
        : [];
    // iPad mirror of the saveProspect / prospect-detail-cancel slots, for the
    // same reason as employerPadNavButtons above.
    const prospectPadNavButtons: NativeHeaderState['navButtons'] =
      isPadShell && currentView === 'outreach' && prospectDetailState?.hasChanges && !prospectDetailState.saving
        ? [
            { id: 'prospect:detail-cancel', sfSymbol: 'xmark', label: 'Cancel' },
            { id: 'prospect:detail-save', sfSymbol: 'checkmark', label: 'Save' },
          ]
        : [];
    const employersNavButtons: NativeHeaderState['navButtons'] =
      nativeBridge.hasNativeSidebar && currentView === 'employers' && !!employersListState?.listVisible
        ? [
            {
              id: 'emp:filter',
              sfSymbol: (employersListState.statusFilter ?? EMPLOYER_STATUS_FILTER_ALL) !== EMPLOYER_STATUS_FILTER_ALL
                ? 'line.3.horizontal.decrease.circle.fill'
                : 'line.3.horizontal.decrease.circle',
              label: 'Filter Employers',
              menuItems: EMPLOYER_STATUS_FILTER_OPTIONS.map((opt) => ({
                id: `${EMPLOYERS_NATIVE_FILTER_PREFIX}${opt.value}`,
                label: opt.label,
                ...((employersListState.statusFilter ?? EMPLOYER_STATUS_FILTER_ALL) === opt.value
                  ? { sfSymbol: 'checkmark' }
                  : {}),
              })),
            },
          ]
        : [];
    // Sample preview actions return through the native header-control channel.
    const templatePreviewNavButtons: NativeHeaderState['navButtons'] =
      nativeBridge.platform === 'macos' && templatePreviewState
        ? [
            { id: 'tpl:rerender', sfSymbol: 'arrow.clockwise', label: 'Re-render' },
            {
              id: 'tpl:fit',
              sfSymbol: templatePreviewState.zoomMode === 'fit'
                ? 'arrow.up.left.and.arrow.down.right'
                : 'arrow.down.right.and.arrow.up.left',
              label: templatePreviewState.zoomMode === 'fit' ? 'Fill width' : 'Fit page',
            },
            { id: 'tpl:download', sfSymbol: 'arrow.down.doc', label: 'Download PDF' },
          ]
        : [];
    // Resource Library page actions. Every one of these is a pill in the web
    // header row the native shell replaces wholesale, so the view arrives on
    // macOS with a title, a library picker and nothing else. Gated on macOS to
    // match the library viewSwitcher below: the picker these sit beside is
    // macOS-only, so publishing the actions on the iPad shell would strand them
    // next to no library. Suppressed while a rate item is open, where the form
    // owns the toolbar and the picker itself stands down.
    const rlItems =
      currentView === 'resource-library' && !resourceLibraryItemFormOpen
        ? resourceLibraryItemsState
        : null;
    const resourceLibraryNavButtons: NativeHeaderState['navButtons'] =
      nativeBridge.platform === 'macos' && rlItems
        ? [
            ...(rlItems.hasSections
              ? [{
                  id: 'rl:collapse',
                  sfSymbol: rlItems.allCollapsed
                    ? 'arrow.up.left.and.arrow.down.right'
                    : 'arrow.down.right.and.arrow.up.left',
                  label: rlItems.allCollapsed ? 'Expand All' : 'Collapse All',
                }]
              : []),
            ...(rlItems.canRateProfile
              ? [{ id: 'rl:rate-profile', sfSymbol: 'slider.horizontal.3', label: 'Rate Profile' }]
              : []),
          ]
        : [];
    // The native mailbox list header stays deliberately sparse: mailbox
    // navigation leads, while compose and search already have first-class
    // toolbar positions. Selection is a list interaction (not a permanent
    // toolbar mode) and refresh is automatic, so neither belongs beside them.
    // An open thread replaces this with its message-specific actions below.
    const emailLeadingNavButtons: NativeHeaderState['leadingNavButtons'] =
      currentView === 'email' && !emailThreadState
        ? [{
            id: 'email:mailbox',
            sfSymbol: 'chevron.left',
            label: 'Mailboxes',
            showsTitle: true,
            menuItems: [
              ...['INBOX', 'STARRED', 'UNREAD', 'SENT', 'DRAFT', 'TRASH'].map((id) => ({
              id: `${EMAIL_NATIVE_MAILBOX_PREFIX}${id}`,
              label:
                id === 'INBOX' && unreadEmailCount > 0
                  ? `Inbox (${unreadEmailCount})`
                  : id === 'DRAFT'
                    ? 'Drafts'
                    : id.charAt(0) + id.slice(1).toLowerCase(),
              ...(emailActiveTab === id ? { sfSymbol: 'checkmark' } : {}),
              })),
              ...emailSharedMailboxes.map(({ id, label }) => ({
                id: `${EMAIL_NATIVE_MAILBOX_PREFIX}group:${id}`,
                label,
                sfSymbol: emailActiveTab === `group:${id}` ? 'checkmark' : 'tray.2.fill',
              })),
            ],
          }]
        : [];
    const emailNavButtons: NativeHeaderState['navButtons'] =
      currentView === 'email'
        ? emailThreadState
          ? [
              { id: 'email:archive', sfSymbol: 'archivebox', label: 'Archive' },
              { id: 'email:trash', sfSymbol: 'trash', label: 'Delete' },
              { id: 'email:mark-unread', sfSymbol: 'envelope.badge', label: 'Mark as unread' },
              { id: 'email:toggle-star', sfSymbol: emailThreadState.isStarred ? 'star.fill' : 'star', label: emailThreadState.isStarred ? 'Unstar' : 'Star' },
              { id: 'email:reply', sfSymbol: 'arrowshape.turn.up.left', label: 'Reply' },
              { id: 'email:forward', sfSymbol: 'arrowshape.turn.up.right', label: 'Forward' },
              { id: 'email:print', sfSymbol: 'printer', label: 'Print' },
            ]
          : []
        : [];
    const globalNavButtons: NativeHeaderState['navButtons'] =
      currentView === 'add-workspace'
        ? [
            { id: 'workspace-setup:submit', label: workspaceCreateState.busy ? 'Creating…' : 'Create Workspace', sfSymbol: 'checkmark' },
          ]
        : currentView === 'email'
        ? emailNavButtons
        : currentView === 'project-detail' && projectActiveTab === 'email' && !viewOwnsNavButtons
        ? projectEmailNavButtons
        : nativeBridge.hasNativeTabBar && currentView === 'project-detail' && projectActiveTab === 'hs' &&
          (hsHeaderState?.view === 'record' || hsEditorState?.canAddItems === true)
        ? [
            ...(hsEditorNavButtons ?? []),
            ...(hsEditorAddButtons ?? []),
            ...(hsDocumentNavButtons ?? []),
            ...(hsSectionNavButtons ?? []),
          ]
        : nativeBridge.hasNativeTabBar && currentView === 'project-detail' &&
          (projectActiveTab === 'notes' || projectActiveTab === 'tasks')
        ? projectTasksNavButtons
        : nativeBridge.hasNativeSidebar && !viewOwnsNavButtons
        ? [
            ...(adminNavButtons ?? []),
            ...(hsEditorNavButtons ?? []),
            ...(hsEditorAddButtons ?? []),
            ...(hsDocumentNavButtons ?? []),
            ...(hsSectionNavButtons ?? []),
            ...(projectTasksNavButtons ?? []),
            ...(projectNotesNavButtons ?? []),
            ...(projectEmailNavButtons ?? []),
            ...(takeoffNavButtons ?? []),
            ...(pipelineNavButtons ?? []),
            ...(templatePreviewNavButtons ?? []),
            ...(workspacesNavButtons ?? []),
            ...(workspaceInvitePadNavButtons ?? []),
            ...(employersNavButtons ?? []),
            ...(employerPadNavButtons ?? []),
            ...(prospectPadNavButtons ?? []),
            ...(resourceLibraryNavButtons ?? []),
            {
              id: 'global:messages',
              sfSymbol: 'bubble.left',
              label: 'Messages',
              ...(messagesUnreadCount > 0 ? { badge: messagesUnreadCount } : {}),
            },
            {
              id: 'global:notifications',
              sfSymbol: 'bell',
              label: 'Notifications',
              ...(unreadNotificationCount > 0 ? { badge: unreadNotificationCount } : {}),
            },
          ]
        : undefined;

    NativeBridge.sendHeaderState(
      buildNativeHeaderState({
        currentView,
        title: nativeHeaderTitle,
        subtitle: currentView === 'project-detail' && projectActiveTab === 'site_photos' && sitePhotoFullViewOpen
          ? selectedProjectName ?? undefined
          : projectEmailNativeSubtitle || (currentView === 'email' ? emailNativeSubtitle : undefined),
        projectActiveTab,
        activeProjectId: selectedProjectId,
        activeProjectName: selectedProjectName,
        workspacesActiveTab,
        preferencesSection,
        profileActiveTab,
        profileDetailsDirty,
        profileSignatureState,
        workspaceDetail,
        workspaceSaveState,
        workspaceUsersListVisible,
        workspaceInviteState,
        employersListVisible: !!employersListState?.listVisible,
        employerDetailName: employersListState?.selectedName ?? null,
        employerDetailState,
        employerFormState,
        prospectDetailState,
        hasNativeSidebar: nativeBridge.hasNativeSidebar,
        hasNativeTabBar: nativeBridge.hasNativeTabBar,
        // Internal-team only (isAdmin = super_admin / internal / legacy admin /
        // staff; excludes workspace roles): drops the Projects New Project
        // (folder+) action for members/clients (mirrors onAddProject in
        // AuthenticatedWorkspace).
        canAddProject: auth.isAdmin(),
        canManageApplications:
          auth.user?.role === 'super_admin' || auth.user?.role === 'internal',
        canEditPlanner: canEditProjectPlanner(auth.user?.role),
        projectReadOnly: projectPermissions?.readOnly === true,
        plannerEditorState,
        navButtons: globalNavButtons,
        leadingNavButtons: currentView === 'email' ? emailLeadingNavButtons : undefined,
        // Site Photos has no in-page switcher, so the native toolbar owns it
        // on both Apple shells. BOQ and Library retain their macOS-only picker.
        viewSwitcher: nativeAccountingNavigation.viewSwitcher ?? (nativePipelineNavigation.viewSwitcher
          ? (prospectDetailState?.hasChanges ? null : nativePipelineNavigation.viewSwitcher)
          : currentView === 'project-detail' && projectActiveTab === 'site_photos'
          && !(new URLSearchParams(window.location.search).get('view') === 'full'
            && new URLSearchParams(window.location.search).has('photo'))
          ? {
              options: [
                { id: 'site-photos:view:gallery', label: 'Gallery' },
                { id: 'site-photos:view:map', label: 'Map' },
              ],
              selectedId: `site-photos:view:${sitePhotosView}`,
              style: 'segmented',
            }
          : nativeBridge.platform === 'macos'
            ? (currentView === 'resource-library'
                ? (resourceLibraryItemFormOpen ? null : resourceLibraryViewSwitcher)
                : boqViewSwitcher)
            : null),
        takeoffDrawingOpen: currentView === 'project-detail' && projectActiveTab === 'takeoff' && takeoffDrawingOpen,
        sitePhotoFullViewOpen: currentView === 'project-detail' && projectActiveTab === 'site_photos' && sitePhotoFullViewOpen,
        takeoffUploaderOpen:
          currentView === 'project-detail' && projectActiveTab === 'takeoff' && !!takeoffHeaderState?.uploaderOpen,
        takeoffUploaderDepth,
        takeoffUploaderStep,
        takeoffSheetDetailsOpen:
          currentView === 'project-detail' && projectActiveTab === 'takeoff' && !!takeoffHeaderState?.sheetDetailsOpen,
        documentsFolderOpen:
          currentView === 'project-detail' && projectActiveTab === 'documents' && documentsFolderDepth > 0,
        workspaceDocumentsFolderOpen: workspaceDocumentsOpen && documentsFolderDepth > 0,
        notesDetailOpen:
          currentView === 'project-detail' && (projectActiveTab === 'notes' || projectActiveTab === 'tasks') && !!notesDetailOpen,
        reportOpen: currentView === 'project-detail' && !!projectReportIdFromTab(projectActiveTab) && !!reportHeaderState,
        interimApplicationOpen: currentView === 'project-detail' && !!interimAppState,
        templatePreviewOpen: currentView === 'system-settings' && !!templatePreviewState?.canBack,
        resourceLibraryItemFormOpen: currentView === 'resource-library' && resourceLibraryItemFormOpen,
        emailThreadOpen:
          (currentView === 'email' && !!emailThreadState)
          || (currentView === 'project-detail' && projectActiveTab === 'email' && projectEmailHeaderState.detailOpen),
        hsView:
          currentView === 'project-detail' && projectActiveTab === 'hs'
            ? hsHeaderState?.view ?? null
            : null,
        // Suppressed outside 'rams': the native primary action (Send for
        // review / Issue) and the anchored lifecycle menu are RAMS-specific —
        // there is no native Toolbox Talks equivalent yet, so a 'draft'/
        // 'delivered' status must never reach buildNativeHeaderState's
        // RAMS-status switch (it would show a stray "Download RAMS pack"
        // menu item, or worse dispatch a RAMS-only event nothing handles).
        hsStatus:
          currentView === 'project-detail' && projectActiveTab === 'hs'
            && (hsHeaderState?.section ?? 'rams') === 'rams'
            ? hsHeaderState?.status ?? null
            : null,
        hsCanIssue: (hsHeaderState?.section ?? 'rams') === 'rams' && !!hsHeaderState?.canIssue,
        hsSection:
          currentView === 'project-detail' && projectActiveTab === 'hs'
            ? hsHeaderState?.section ?? 'rams'
            : null,
        // Covers both the standalone /plant screen and the register embedded
        // in a project's H&S tab: PlantRegisterView broadcasts the same
        // PLANT_STATE_EVENT regardless of which screen mounted it. A machine
        // is open when the table isn't visible, which is also true for the
        // unsaved "New machine" draft (selectedId is null then).
        plantDetailOpen:
          (currentView === 'plant'
            || (currentView === 'project-detail' && projectActiveTab === 'hs' && hsHeaderState?.section === 'register'))
          && !!plantListState && !plantListState.listVisible,
        plantDetailState,
      })
    );
  }, [
    profileSignatureState,
    workspacesActiveTab,
    workspaceDetail,
    workspaceSaveState,
    workspaceUsersListVisible,
    workspaceInviteState,
    workspacesListState,
    workspaceCreateState,
    // All three employers channels: the screen state drives the Add action, the
    // title, the chevron and the filter menu's checkmarks; the form state drives
    // its Save/Cancel; the detail state drives the profile's Edit and its own
    // Save/Cancel. Missing any is the native-side equivalent of a stale
    // mobileHeaderConfig dep — the toolbar simply stops updating.
    employersListState,
    employerFormState,
    employerDetailState,
    prospectDetailState,
    plannerEditorState,
    payrollActiveTab,
    accountingActiveTab,
    salesActiveTab,
    purchasesActiveTab,
    taxActiveTab,
    emailActiveTab,
    emailSharedMailboxes,
    emailThreadState,
    unreadEmailCount,
    currentView,
    documentRouteSlug,
    pipelineBoardState,
    templatePreviewState,
    nativeBridge.hasNativeSidebar,
    nativeBridge.hasNativeTabBar,
    nativeBridge.platform,
    pageTitle,
    projectActiveTab,
    projectPermissions,
    workspaceNavigation.authoritative,
    workspaceNavigation.navigation,
    projectEmailHeaderState,
    sitePhotosView,
    sitePhotoFullViewOpen,
    payrollProfileMenu,
    selectedProjectId,
    selectedProjectName,
    systemSettingsActiveTab,
    preferencesSection,
    profileActiveTab,
    profileDetailsDirty,
    auth.user?.role,
    messagesUnreadCount,
    unreadNotificationCount,
    boqViewSwitcher,
    resourceLibraryViewSwitcher,
    nativePipelineNavigation,
    nativeAccountingNavigation,
    resourceLibraryItemFormOpen,
    // Drives the page-action buttons' visibility, labels and checkmarks. Missing
    // it would freeze that cluster at whatever the first broadcast said.
    resourceLibraryItemsState,
    projectAdminHeaderState,
    takeoffDrawingOpen,
    takeoffUploaderDepth,
    takeoffUploaderStep,
    takeoffUploaderSelected,
    takeoffUploaderCanBrowse,
    takeoffUploaderDetails,
    documentsFolderDepth,
    workspaceDocumentsOpen,
    workspaceDocumentsOpen,
    notesDetailOpen,
    reportHeaderState,
    notesUnreadCount,
    interimAppState,
    hsHeaderState,
    hsEditorState,
    hsDocumentState,
    takeoffHeaderState,
    // Drives plantDetailOpen/plantDetailState — the register's back chevron,
    // + suppression and Save primary action all key off these. Missing them
    // would freeze that cluster at whatever the first broadcast said, same
    // trap as the employers channels above.
    plantListState,
    plantDetailState,
  ]);

  // Sidebar shells (macOS + iPad): handle taps on the Messages / Notifications
  // navButtons that are published as part of the headerState effect above.
  // Navigate to the respective view (full-page, not a side panel). Gated on
  // hasNativeSidebar to match the emitting gate above — the admin-*/takeoff-*/
  // pipeline:*/ws:* ids below only ever arrive on macOS (those navButtons stay
  // platform === 'macos'-gated at their source), so widening this listener to
  // iPad is inert for them; it only makes the iPad-reachable global:* ids live.
  // Also routes BOQ viewSwitcher selections back to the BOQ hook via boq:select-bill;
  // non-global ids that don't match Messages/Notifications are forwarded because the
  // BOQ hook ignores ids it doesn't recognise, and other views handle their own
  // headerControl events from within their own child effects.
  // Notifications' own navButton ids (notifications:select, :selectAll,
  // :markSelectedRead, :deleteSelected, :done, :markAllRead) are NOT handled
  // here — NotificationContent subscribes to NativeBridge.onHeaderControl
  // directly (mirroring its iOS block), since it's the sole sender of that
  // view's navButtons. They fall through to the BOQ forward below, which the
  // BOQ hook silently ignores as an unrecognised id.
  // The takeoff-* ids (upload/sheet-details/selection edit-details+delete+clear/
  // fit/run-AI/export + inspector) dispatch the SAME
  // `takeoff:*` window events buildTakeoffHeaderActions emits for the web/iOS
  // header — TakeoffView's existing listeners are unchanged. `takeoff-upload`
  // passes trigger: null (no DOM button to anchor to on native); TakeoffUploader
  // already tolerates that — its anchorEl prop is unused, the picker always
  // docks to the bottom of the viewport regardless of trigger.
  useEffect(() => {
    if (!NativeBridge.isRunningNatively()) return;
    // The tab-bar shell needs this too, not just the sidebar shells: it is the
    // only place `sections:toggle` is handled, and headerState publishes that
    // button off hasNativeTabBar — the exact flag this gate excluded. The two
    // are mutually exclusive on iOS, so the button was being drawn precisely
    // where its handler was switched off.
    if (!NativeBridge.hasNativeSidebar() && !NativeBridge.hasNativeTabBar()) return;

    return NativeBridge.onHeaderControl(({ id }) => {
      if (id.startsWith(ACCOUNTING_AREA_PREFIX)) {
        const target = nativeAccountingNavigationTarget(id, nativeAccountingNavigation);
        if (target) navigateToView(target);
        return;
      }
      if (id.startsWith(PIPELINE_SECTION_PREFIX)) {
        if (prospectDetailState?.hasChanges) return;
        const target = nativePipelineNavigationTarget(id, nativePipelineNavigation);
        if (!target) return;
        if (target.view === 'outreach') {
          if (currentViewRef.current === 'outreach') {
            window.dispatchEvent(new CustomEvent(OUTREACH_SET_SECTION_EVENT, { detail: { sectionId: target.sectionId } }));
          } else {
            window.dispatchEvent(new CustomEvent(OUTREACH_OPEN_SECTION_EVENT, { detail: { sectionId: target.sectionId } }));
          }
        } else {
          navigateToView(target.view);
        }
        return;
      }
      if (dispatchSignatureNativeAction(id)) return;
      if (id === 'workspace-setup:submit') {
        window.dispatchEvent(new CustomEvent('workspaces:create-submit'));
        return;
      }
      if (id.startsWith('workspace-setup:')) {
        window.dispatchEvent(new CustomEvent('workspaces:create-step', { detail: { step: Number(id.split(':')[1]) } }));
        return;
      }
      // Resource Library page actions, claimed by their id namespace ahead of
      // everything below: the library viewSwitcher match is by library id, and
      // the BOQ forward at the end is a catch-all that would swallow the press
      // in silence.
      if (dispatchResourceLibraryNativeAction(id)) return;

      // Email list tools live in the native global header on iOS/macOS. They
      // reuse the same window events as the web header so EmailView remains the
      // single owner of search, refresh and bulk-read behaviour.
      if (id === 'email:focus-search') {
        window.dispatchEvent(new CustomEvent('email:focus-search'));
      } else if (id === 'email:select') {
        window.dispatchEvent(new CustomEvent('email:toggle-select'));
      } else if (id === 'email:mark-all-read') {
        window.dispatchEvent(new CustomEvent('email:mark-all-read'));
      } else if (id === 'email:refresh') {
        window.dispatchEvent(new CustomEvent('email:refresh'));
      } else if (id === 'email:mailbox') {
        // The mailbox action is normally a native Menu. Keep a no-op claim for
        // the plain-button fallback so it cannot fall through to BOQ routing.
      } else if (id === 'project-email-refresh') {
        window.dispatchEvent(new CustomEvent('project-email:refresh'));
      } else if (id === 'project-email-compose') {
        window.dispatchEvent(new CustomEvent('project-email:compose'));
      } else if (id === 'project-email-prepare-tender') {
        window.dispatchEvent(new CustomEvent('project-email:prepare-tender'));
      } else if (id === 'project-email-reply') {
        window.dispatchEvent(new CustomEvent('project-email:reply'));
      } else if (id === 'project-email-reply-all') {
        window.dispatchEvent(new CustomEvent('project-email:reply-all'));
      } else if (id === 'project-email-forward') {
        window.dispatchEvent(new CustomEvent('project-email:forward'));
      } else if (id === 'email:archive' || id === 'email:trash' || id === 'email:reply'
                 || id === 'email:reply-all' || id === 'email:forward' || id === 'email:print'
                 || id === 'email:toggle-star' || id === 'email:mark-unread') {
        window.dispatchEvent(new CustomEvent(id));
      } else if (id === 'sections:toggle') {
        // The iPhone shell's leading hamburger (headerState.showSectionsToggle).
        // AppSidebar owns the drawer state behind useSidebar(), which is scoped
        // to SidebarProvider well below this handler, so ask over the bridge.
        window.dispatchEvent(new CustomEvent('sidebar:open-mobile'));
      } else if (id === 'global:messages') {
        navigateToView('messages');
      } else if (id === 'global:notifications') {
        if (currentViewRef.current !== 'notifications') {
          notificationsReturnViewRef.current = currentViewRef.current as AppView;
        }
        navigateToView('notifications');
      } else if (id === 'messages:open-notifications') {
        // The iPad Messages bar's bell. The native Messages overlay never
        // navigates the web (the tab's onSelect preserves web state), so
        // currentView still points at whatever was underneath — record the
        // TRUE origin explicitly or back lands there instead of Messages.
        notificationsReturnViewRef.current = 'messages';
        navigateToView('notifications');
      } else if (id === 'emp:add') {
        // iPad-only mirrors of the primaryAction/secondaryAction slots it does not
        // render — see employerPadNavButtons. Same window events the web pills fire.
        window.dispatchEvent(new CustomEvent(EMPLOYERS_CREATE_EVENT));
      } else if (id === 'workspace-invite:send') {
        window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_INVITE_SUBMIT_EVENT));
      } else if (id === 'workspace-invite:cancel') {
        window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_INVITE_CANCEL_EVENT));
      } else if (id === 'emp:form-save') {
        window.dispatchEvent(new CustomEvent(EMPLOYER_FORM_SAVE_EVENT));
      } else if (id === 'emp:form-cancel') {
        window.dispatchEvent(new CustomEvent(EMPLOYER_FORM_CANCEL_EVENT));
      } else if (id === 'emp:detail-save') {
        window.dispatchEvent(new CustomEvent(EMPLOYER_DETAIL_SAVE_EVENT));
      } else if (id === 'emp:detail-cancel') {
        window.dispatchEvent(new CustomEvent(EMPLOYER_DETAIL_CANCEL_EVENT));
      } else if (id === 'prospect:detail-save') {
        window.dispatchEvent(new CustomEvent(PROSPECT_DETAIL_SAVE_EVENT));
      } else if (id === 'prospect:detail-cancel') {
        window.dispatchEvent(new CustomEvent(PROSPECT_DETAIL_CANCEL_EVENT));
      } else if (id === 'admin-save') {
        // ProjectAdminTab listens for this and triggers handleSaveAll.
        window.dispatchEvent(new CustomEvent('project-admin:save'));
      } else if (id === 'admin-cancel') {
        // ProjectAdminTab listens for this and triggers discardAllChanges.
        window.dispatchEvent(new CustomEvent('project-admin:cancel'));
      } else if (id === 'hs-editor:save') {
        window.dispatchEvent(new CustomEvent('hs:editor-save'));
      } else if (id === 'hs-editor-add-item') {
        window.dispatchEvent(new CustomEvent('hs:editor-add-item'));
      } else if (id === 'hs-editor:cancel') {
        window.dispatchEvent(new CustomEvent('hs:editor-cancel'));
      } else if (id === 'hs-details') {
        window.dispatchEvent(new CustomEvent('hs:toggle-details'));
      } else if (id.startsWith('hs-document-nav:')) {
        window.dispatchEvent(new CustomEvent('hs:navigate-document-section', {
          detail: { sectionId: id.slice('hs-document-nav:'.length) },
        }));
      } else if (id.startsWith('hs-editor-nav:')) {
        window.dispatchEvent(new CustomEvent('hs:navigate-editor-section', {
          detail: { sectionId: id.slice('hs-editor-nav:'.length) },
        }));
      } else if (id === 'project-notebook-tasks') {
        if (currentViewRef.current !== 'project-detail') return;
        if (projectActiveTabRef.current === 'tasks') window.dispatchEvent(new Event('project-notebook:toggle-tasks'));
        else setProjectActiveTab('tasks');
      } else if (id === 'project-tasks-add') {
        // ProjectTasksList opens the shared task sheet with this project
        // preselected — same event the web header pill dispatches.
        window.dispatchEvent(new CustomEvent('project-tasks:create-new'));
      } else if (id === 'project-notes-add') {
        window.dispatchEvent(new CustomEvent('project-notes:create-new'));
      } else if (id === 'project-notes-mark-read') {
        window.dispatchEvent(new CustomEvent('project-notes:mark-all-read'));
      } else if (id === 'takeoff-upload') {
        window.dispatchEvent(new CustomEvent('takeoff:upload', { detail: { trigger: null } }));
      } else if (id === 'takeoff-upload-browse') {
        // Uploader-open state: fire the hidden file input in the web uploader.
        // NOTE: a native toolbar press may not carry the WKWebView user
        // activation a file dialog needs — verified in the running app.
        window.dispatchEvent(new CustomEvent('takeoff:upload-browse'));
      } else if (id === 'takeoff-upload-continue') {
        window.dispatchEvent(new CustomEvent('takeoff:upload-continue'));
      } else if (id === 'takeoff-upload-clear') {
        window.dispatchEvent(new CustomEvent('takeoff:upload-clear'));
      } else if (id === 'takeoff-upload-cancel') {
        // Closes the uploader outright. The toolbar chevron is the step back
        // (TAKEOFF_UPLOADER_BACK_TO_SOURCE_SENTINEL); this is the full exit.
        window.dispatchEvent(new CustomEvent('takeoff:upload-close'));
      } else if (id.startsWith('takeoff-upload-field-')) {
        const field = id.slice('takeoff-upload-field-'.length);
        window.dispatchEvent(new CustomEvent('takeoff:upload-field', { detail: { field } }));
      } else if (id === 'takeoff-upload-import') {
        window.dispatchEvent(new CustomEvent('takeoff:upload-import'));
      } else if (id === 'takeoff-sheet-details') {
        window.dispatchEvent(new CustomEvent('takeoff:sheet-details'));
      } else if (id === 'takeoff-selection-edit-details') {
        window.dispatchEvent(new CustomEvent('takeoff:sheet-details', {
          detail: { sheetIds: takeoffHeaderState?.selectedSheetIds ?? [] },
        }));
      } else if (id === 'takeoff-selection-download') {
        window.dispatchEvent(new CustomEvent('takeoff:selection-download'));
      } else if (id === 'takeoff-selection-delete') {
        window.dispatchEvent(new CustomEvent('takeoff:selection-delete'));
      } else if (id === 'takeoff-selection-select-all') {
        window.dispatchEvent(new CustomEvent('takeoff:selection-select-all'));
      } else if (id.startsWith('takeoff-gallery:')) {
        const [, field, value] = id.split(':');
        window.dispatchEvent(new CustomEvent('takeoff:gallery-change', { detail: { field, value } }));
      } else if (id === 'site-photos:view:gallery' || id === 'site-photos:view:map') {
        window.dispatchEvent(new CustomEvent('site-photos:set-view', { detail: { view: id.slice('site-photos:view:'.length) } }));
      } else if (id === 'takeoff-selection-clear') {
        window.dispatchEvent(new CustomEvent('takeoff:selection-clear'));
      } else if (id === 'takeoff-fit') {
        window.dispatchEvent(new CustomEvent('takeoff:fit-view'));
      } else if (id === 'takeoff-run-ai') {
        window.dispatchEvent(new CustomEvent('takeoff:run-ai'));
      } else if (id === 'takeoff-export') {
        window.dispatchEvent(new CustomEvent('takeoff:export'));
      } else if (id === 'takeoff-inspector') {
        window.dispatchEvent(new CustomEvent('takeoff:toggle-panel-collapse'));
      } else if (dispatchHsNativeMenuAction(id)) {
        // Handled by the shared RAMS menu router above.
      } else if (currentViewRef.current === 'resource-library'
                 && resourceLibraryViewSwitcherRef.current?.options.some((o) => o.id === id)) {
        // A Resource Library viewSwitcher press. Claimed before the BOQ forward
        // below because that forward is a catch-all: the BOQ hook would swallow
        // the id silently and the library would never change.
        window.dispatchEvent(new CustomEvent('resource-library:select-library', { detail: { id } }));
      } else {
        // Forward to the BOQ bill selector; the BOQ hook discards ids that
        // don't match any bill or summary-tab. Other views (Calendar etc.) manage
        // their own onHeaderControl listeners from within their own child effects.
        window.dispatchEvent(new CustomEvent('boq:select-bill', { detail: { billId: id } }));
      }
    });
  // takeoffHeaderState: takeoff-selection-edit-details reads its
  // selectedSheetIds at click time, so the listener must re-subscribe
  // whenever selection changes rather than closing over a stale snapshot.
  }, [navigateToView, takeoffHeaderState, nativeAccountingNavigation, nativePipelineNavigation, prospectDetailState?.hasChanges, setProjectActiveTab]);

  // macOS shell: track the BOQ bill/summary picker so it can be published as a
  // native viewSwitcher in the toolbar. Only active on macOS; the web floating
  // header handles this on iOS and browser via boq:state-change directly.
  useEffect(() => {
    // Registered UNCONDITIONALLY (no platform gate): gating on getPlatform() at
    // mount is racy — if this []-deps effect runs before the document-start
    // bootstrap sets the platform attribute it would bail permanently and the
    // switcher would never populate (same lesson as AuthContext's native-session
    // listener). Maintaining boqViewSwitcher on web/iOS is harmless; it only
    // reaches the native toolbar via the macOS-gated send below.
    type BoqHeaderDetail = {
      bills?: { id: string; label: string }[];
      activeBillId?: string | null;
      summaryTabs?: { id: string; label: string }[];
    } | null;

    const applyDetail = (detail: BoqHeaderDetail) => {
      if (!detail) {
        setBoqViewSwitcher(null);
        return;
      }

      const options = [
        ...(detail.bills ?? []),
        ...(detail.summaryTabs ?? []),
      ];
      if (options.length === 0) {
        setBoqViewSwitcher(null);
        return;
      }

      // If activeBillId is null, 'all', or doesn't match any option, fall back
      // to the first option so the native picker always has a valid selection.
      const rawId = detail.activeBillId ?? null;
      const matched = rawId && options.some((o) => o.id === rawId);
      const selectedId = matched ? (rawId as string) : options[0].id;

      // 'menu' => a compact trailing pull-down in the macOS toolbar. Set
      // explicitly (not inferred from option count) so it doesn't briefly
      // render as a centred segmented control before the bills finish loading.
      // Functional update lets us shallow-compare against previous state without
      // adding boqViewSwitcher to this effect's deps (which would cause the
      // listener to re-register on every dispatch, creating a subscription gap).
      setBoqViewSwitcher(prev => {
        if (
          prev !== null &&
          prev.selectedId === selectedId &&
          prev.style === 'menu' &&
          prev.options.length === options.length &&
          prev.options.every((o, idx) => o.id === options[idx].id && o.label === options[idx].label)
        ) {
          return prev;
        }
        return { options, selectedId, style: 'menu' };
      });
    };

    const onBoqStateChange = (event: Event) => {
      applyDetail((event as CustomEvent<BoqHeaderDetail>).detail);
    };

    window.addEventListener('boq:state-change', onBoqStateChange);
    // The BOQ hook dispatches boq:state-change once on mount; on a cold load
    // landing directly on the pricing tab, that one-shot can fire before this
    // listener attaches (and the static empty state never re-dispatches). The
    // hook also stashes its latest detail on this global, so read it on attach
    // to pick up an event we missed.
    applyDetail((window as unknown as { __QUANTIS_BOQ_HEADER_STATE__?: BoqHeaderDetail }).__QUANTIS_BOQ_HEADER_STATE__ ?? null);
    return () => window.removeEventListener('boq:state-change', onBoqStateChange);
  }, []);

  // macOS shell: track the Resource Library picker so it can be published as a
  // native viewSwitcher, the same way the BOQ bill picker is above.
  useEffect(() => {
    // Registered UNCONDITIONALLY for the same reason as the BOQ listener: a
    // getPlatform() gate on a []-deps effect races the document-start bootstrap
    // and, having lost, would bail permanently. Keeping the state on web/iOS is
    // harmless, since only the macOS-gated send below puts it on a toolbar.
    type LibraryRow = { id?: string | null; name?: string | null };
    type LibrariesChangeDetail = {
      libraries?: LibraryRow[];
      selectedLibraryId?: string | null;
    } | null;

    const onLibrariesChange = (event: Event) => {
      const detail = (event as CustomEvent<LibrariesChangeDetail>).detail;
      if (!detail) {
        setResourceLibraryViewSwitcher(null);
        return;
      }

      const options = (detail.libraries ?? []).flatMap((library) => {
        // resolveLibraryName: client-owned rows store a NULL name and take it
        // from the workspaces embed, so reading `name` raw would drop them.
        const resolved = resolveLibraryName(library);
        const name = resolved.name?.trim();
        if (!resolved.id || !name) return [];
        return [{ id: resolved.id, label: formatLibraryName(name) }];
      });

      if (options.length === 0) {
        setResourceLibraryViewSwitcher(null);
        return;
      }

      // A selection the list doesn't contain (still loading, or just deleted)
      // would leave the native menu with no checked row, so fall back to the
      // first option the way the BOQ switcher does.
      const rawId = detail.selectedLibraryId ?? null;
      const matched = rawId && options.some((o) => o.id === rawId);
      const selectedId = matched ? (rawId as string) : options[0].id;

      // Functional update so the shallow compare can skip no-op renders without
      // this state joining the deps (see the BOQ listener's note above).
      setResourceLibraryViewSwitcher((prev) => {
        if (
          prev !== null &&
          prev.selectedId === selectedId &&
          prev.options.length === options.length &&
          prev.options.every((o, idx) => o.id === options[idx].id && o.label === options[idx].label)
        ) {
          return prev;
        }
        return { options, selectedId, style: 'menu' };
      });
    };

    window.addEventListener('resource-library:libraries-change', onLibrariesChange);
    return () => window.removeEventListener('resource-library:libraries-change', onLibrariesChange);
  }, []);

  // macOS shell: track the rate-item list's toolbar state so its page actions
  // can be published as navButtons. Registered unconditionally for the same
  // reason as the listener above: a getPlatform() gate on a []-deps effect races
  // the document-start bootstrap and, having lost, would bail permanently.
  useEffect(() => {
    const onItemsStateChange = (event: Event) => {
      const detail = (event as CustomEvent<{ kind?: string } | null>).detail;
      // The merchant catalogue shares this event and has its own controls, so
      // its payload clears the buttons exactly as the unmount null does.
      setResourceLibraryItemsState(
        detail?.kind === 'items' ? (detail as ResourceLibraryItemsHeaderState) : null,
      );
    };
    window.addEventListener('resource-library:state-change', onItemsStateChange);
    return () => window.removeEventListener('resource-library:state-change', onItemsStateChange);
  }, []);

  // Resource Library actions arrive on the shared menu channel. Keep the
  // listener unconditional for the same reason as the other native bridges.
  useEffect(() => {
    const onMenuAction = (event: Event) => {
      const id = (event as CustomEvent<{ id?: string }>).detail?.id ?? '';
      if (dispatchSignatureNativeAction(id)) return;
      if (dispatchHsNativeMenuAction(id)) return;
      if (dispatchEmailNativeMenuAction(id)) return;
      dispatchResourceLibraryNativeAction(id);
    };
    window.addEventListener('quantis:native:menuAction', onMenuAction);
    return () => window.removeEventListener('quantis:native:menuAction', onMenuAction);
  }, []);

  // The web hides its LibraryPicker while a rate item is open, because changing
  // library mid-edit throws away unsaved work. The native toolbar has to follow,
  // and RateItemPage announces itself on this event. Registered unconditionally
  // for the same reason as the listener above.
  useEffect(() => {
    const onItemFormChange = (e: Event) => {
      setResourceLibraryItemFormOpen((e as CustomEvent).detail != null);
    };
    window.addEventListener('resource-library-item:state-change', onItemFormChange);
    return () => window.removeEventListener('resource-library-item:state-change', onItemFormChange);
  }, []);

  useEffect(() => {
    const usesNativeIOSWalletButton =
      NativeBridge.isRunningNatively() &&
      NativeBridge.getPlatform() === 'ios';

    if (!usesNativeIOSWalletButton) {
      NativeBridge.sendWalletButtonState({ visible: false, kind: null, issueUrl: null });
      return;
    }

    let kind: 'member' | 'staff' | null = null;
    if (currentView === 'billing') {
      kind = 'member';
    } else if (currentView === 'profile') {
      kind = (
        auth.user?.role === 'workspace_owner' || auth.user?.role === 'workspace_admin' ||
        auth.user?.role === 'workspace_user'  ||
        auth.user?.role === 'member'          || auth.user?.role === 'client'
      ) ? 'member' : 'staff';
    }

    NativeBridge.sendWalletButtonState({
      visible: kind !== null,
      kind,
      issueUrl: kind ? `${API_BASE}/wallet/issue?kind=${encodeURIComponent(kind)}` : null,
    });
  }, [auth.user?.role, currentView]);

  useEffect(() => {
    const updateBadges = () => {
      try {
        // Bell/Alerts counts every unread in-app notification row, including
        // Gmail. The Email badge is a separate mailbox-unread counter.
        NativeBridge.updateBadge('notifications', unreadNotificationCount);
        // Email lives in the native "More" overflow — surface its unread count
        // there too (previously only messages + notifications reached native).
        NativeBridge.updateBadge('email', unreadEmailCount);
        // Per-folder badges for the macOS sidebar's email-tab:<LABEL> rows (see
        // buildNativeNavItems). updateBadge is keyed by nav item id, so this is
        // all the wiring the native row needs — no separate nav-item badge field.
        // Scoped to the folder rows that exist: unreadByLabel carries every
        // Gmail label (CATEGORY_*, IMPORTANT, custom), which would otherwise
        // spam the bridge with ids no native row matches on every poll.
        for (const label of EMAIL_FOLDER_LABELS) {
          NativeBridge.updateBadge(`${EMAIL_TAB_PREFIX}${label}`, emailUnreadByLabel[label] ?? 0);
        }
      } catch (error) {
        console.error('Error updating native badges:', error);
      }
    };

    if (auth.user?.id) {
      updateBadges();
      const unsubscribe = DataService.subscribe(() => {
        updateBadges();
      });
      return unsubscribe;
    }
  }, [auth.user?.id, unreadNotificationCount, unreadEmailCount, emailUnreadByLabel]);

  // Messages badge is driven off the live MessagingContext count (updated on
  // realtime message arrival + app-load fetch) so the native sidebar badge is
  // instant, like Email — no extra getChats round-trip on every DataService tick.
  useEffect(() => {
    if (!auth.user?.id) return;
    NativeBridge.updateBadge('messages', messagesUnreadCount);
  }, [auth.user?.id, messagesUnreadCount]);

  return null;
}

AuthenticatedController.displayName = 'AuthenticatedController';
